import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { Helmet } from 'react-helmet-async'
import { FiArrowLeft, FiFlag, FiShoppingCart, FiMenu, FiX } from 'react-icons/fi'
import { FaInstagram, FaFacebook, FaTiktok, FaTwitter } from 'react-icons/fa'

// Professional WhatsApp SVG Icon
const WhatsAppIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 32 32"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M16 0C7.163 0 0 7.163 0 16c0 2.766.662 5.391 1.961 7.738L0 32l8.594-2.219C11.266 31.208 13.578 32 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.5c-2.062 0-4.084-.507-5.912-1.464l-.424-.254-4.391 1.137 1.159-4.237-.275-.436c-1.107-1.755-1.693-3.786-1.693-5.846 0-7.339 5.973-13.312 13.312-13.312 3.567 0 6.922 1.391 9.447 3.917 2.524 2.524 3.917 5.879 3.917 9.447 0 7.339-5.974 13.312-13.312 13.312zm7.251-9.945c-.396-.2-2.343-1.157-2.707-1.288-.364-.132-.631-.198-.896.199-.264.396-1.026 1.292-1.259 1.558-.233.265-.466.298-.862.099-.397-.199-1.676-.62-3.198-1.975-1.183-1.055-1.982-2.359-2.215-2.756-.233-.396-.024-.611.175-.809.18-.176.396-.466.595-.699.198-.233.264-.397.396-.661.132-.265.066-.497-.033-.697-.099-.199-.896-2.158-1.227-2.955-.324-.783-.654-.676-.896-.689-.232-.012-.496-.015-.759-.015-.264 0-.696.099-.96.497-.265.397-1.01 1.157-1.01 2.116 0 .959 1.034 2.459 1.175 2.623.14.165 1.988 3.048 4.818 4.275 1.349.574 2.407.737 3.261.947.682.176 1.302.151 1.793.092.546-.068 1.677-.685 1.913-1.348.237-.662.237-1.229.166-1.348-.07-.118-.268-.187-.664-.387z"/>
  </svg>
)
import ReportContentModal from '../../components/public/ReportContentModal'
import ImageGalleryModal from '../../components/public/ImageGalleryModal'
import AudioPlayer from '../../components/public/AudioPlayer'
import PublicProductsSection from '../../components/public/PublicProductsSection'
import PublicDonationSection from '../../components/public/PublicDonationSection'
import { isExternalVideoUrl } from '../../utils/videoUrl'

const WHATSAPP_BUTTON_OVERRIDE_CSS = `
button.permanent-whatsapp-button,
button.permanent-whatsapp-button:hover,
button.permanent-whatsapp-button:active,
button.permanent-whatsapp-button:focus,
button.permanent-whatsapp-button:visited,
button.permanent-whatsapp-button:disabled {
  background: #10B981 !important;
  background-color: #10B981 !important;
  background-image: none !important;
  color: #FFFFFF !important;
  border: 1px solid transparent !important;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3) !important;
  outline: none !important;
  text-decoration: none !important;
}

button.permanent-whatsapp-button:hover {
  background: #059669 !important;
  background-color: #059669 !important;
  background-image: none !important;
}
button.permanent-whatsapp-button:active {
  background: #047857 !important;
  background-color: #047857 !important;
  background-image: none !important;
}
button.permanent-whatsapp-button:focus {
  background: #047857 !important;
  background-color: #047857 !important;
  background-image: none !important;
}
button.permanent-whatsapp-button:visited {
  background: #10B981 !important;
  background-color: #10B981 !important;
  background-image: none !important;
}
button.permanent-whatsapp-button:disabled {
  opacity: 0.65 !important;
  cursor: not-allowed !important;
}
button.permanent-whatsapp-button .whatsapp-icon {
  color: #FFFFFF !important;
}
`

function normalizeSectionType(sectionType) {
  return (sectionType || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-')
}

const getWebsiteUrlFromGalleryItem = (galleryItem) => {
  if (!galleryItem) return ''
  const candidate = galleryItem.link || galleryItem.website || galleryItem.websiteUrl
  if (typeof candidate === 'string') return candidate.trim()
  return (candidate?.url || galleryItem.linkUrl || '').toString().trim()
}

const getVideoMimeType = (url) => {
  if (!url) return undefined
  const extension = url.split('?')[0].split('.').pop().toLowerCase()
  const mimeMap = {
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    ogv: 'video/ogg',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    mkv: 'video/x-matroska',
    flv: 'video/x-flv',
    wmv: 'video/x-ms-wmv',
    m4v: 'video/x-m4v',
    mpeg: 'video/mpeg',
    mpg: 'video/mpeg',
    '3gp': 'video/3gpp',
    '3g2': 'video/3gpp2'
  }
  return mimeMap[extension]
}

const getVideoUrl = (video) => {
  if (!video) return ''
  if (typeof video === 'string') return video
  return video.url || video.videoUrl || ''
}

const getVideoPoster = (video) => {
  if (!video) return ''
  if (typeof video === 'string') return ''
  return video.thumbnail || video.poster || video.previewImage || video.image?.url || ''
}

const getExternalVideoPreviewImage = (video) => {
  const customPoster = getVideoPoster(video)
  if (customPoster) return customPoster

  return 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=1200&q=80'
}

const videoPosterCache = new Map()

const generateVideoPosterFromUrl = async (videoUrl) => {
  if (!videoUrl || typeof videoUrl !== 'string') return ''
  if (typeof document === 'undefined') return ''

  if (videoPosterCache.has(videoUrl)) {
    return videoPosterCache.get(videoUrl)
  }

  try {
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.preload = 'auto'
    video.muted = true
    video.playsInline = true
    video.setAttribute('playsinline', 'true')

    const poster = await new Promise((resolve, reject) => {
      let settled = false
      const cleanup = () => {
        video.onloadedmetadata = null
        video.onseeked = null
        video.onplaying = null
        video.onerror = null
        clearTimeout(timeoutId)
      }
      const timeoutId = setTimeout(() => {
        if (settled) return
        settled = true
        cleanup()
        reject(new Error('Frame extraction timed out'))
      }, 10000)

      const finish = (dataUrl) => {
        if (settled) return
        settled = true
        cleanup()
        resolve(dataUrl)
      }

      const extractFrame = () => {
        try {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            finish('')
            return
          }

          const width = Math.max(160, Math.min(video.videoWidth || 320, 800))
          const naturalHeight = video.videoHeight || 180
          const height = Math.max(120, Math.round((width / (video.videoWidth || width || 1)) * naturalHeight))
          canvas.width = width
          canvas.height = height

          ctx.drawImage(video, 0, 0, width, height)
          finish(canvas.toDataURL('image/jpeg', 0.82))
        } catch (err) {
          finish('')
        }
      }

      video.onloadedmetadata = () => {
        try {
          const duration = Number.isFinite(video.duration) ? video.duration : 0
          const targetTime = duration > 0 ? Math.min(Math.max(duration * 0.2, 0.5), Math.max(duration - 0.25, 0.5)) : 0.25
          video.currentTime = targetTime
        } catch (err) {
          finish('')
        }
      }

      video.onseeked = () => {
        extractFrame()
      }

      video.onplaying = () => {
        extractFrame()
      }

      video.onerror = () => {
        if (settled) return
        settled = true
        cleanup()
        reject(new Error('Unable to read video for frame extraction'))
      }

      video.src = videoUrl
      video.load()
    })

    if (!poster) {
      videoPosterCache.set(videoUrl, '')
      return ''
    }

    videoPosterCache.set(videoUrl, poster)
    return poster
  } catch (error) {
    console.warn('[PublicWebsiteViewer] Unable to generate video poster from URL:', videoUrl, error)
    videoPosterCache.set(videoUrl, '')
    return ''
  }
}

export const getGalleryThumbnailForItem = (galleryItem) => {
  if (!galleryItem) return null

  const videoEntry = galleryItem.video || galleryItem.videoUrl || galleryItem
  const videoUrl = getVideoUrl(videoEntry)

  if (!videoUrl) {
    const imageUrl = galleryItem.image?.url || galleryItem.url || ''
    return imageUrl ? { type: 'image', src: imageUrl, poster: imageUrl } : null
  }

  return {
    type: 'video',
    src: videoUrl,
    poster: getVideoPoster(videoEntry)
  }
}

function VideoThumbnail({ video, alt = 'Video thumbnail', className = 'w-full h-full object-cover pointer-events-none', missingFallbackClassName = 'w-10 h-10 rounded bg-gray-200 flex items-center justify-center text-xs text-gray-500', size = 'small' }) {
  const [poster, setPoster] = useState(() => getVideoPoster(video))

  useEffect(() => {
    let cancelled = false
    const directPoster = getVideoPoster(video)
    if (directPoster) {
      setPoster(directPoster)
      return () => {
        cancelled = true
      }
    }

    const videoUrl = getVideoUrl(video)
    if (!videoUrl) {
      setPoster('')
      return () => {
        cancelled = true
      }
    }

    const resolvePoster = async () => {
      const generatedPoster = await generateVideoPosterFromUrl(videoUrl)
      if (!cancelled) {
        setPoster(generatedPoster)
      }
    }

    resolvePoster()

    return () => {
      cancelled = true
    }
  }, [video])

  if (!poster) {
    return <div className={missingFallbackClassName}>Video</div>
  }

  return <img src={poster} alt={alt} className={className} draggable={false} onMouseDown={(e) => e.preventDefault()} />
}

import PublicTicketsSection from '../../components/public/PublicTicketsSection'
import ShoppingCart from '../../components/public/ShoppingCart'
import Checkout from '../../components/public/Checkout'
import ContactUsForm from '../../components/public/ContactUsForm'
import ReviewsSection from '../../components/public/ReviewsSection'
import PublicFixturesSection from '../../components/public/PublicFixturesSection'
import PublicPointsTable from '../../components/public/PublicPointsTable'
import PublicResumeUpload from '../../components/public/PublicResumeUpload'
import HeroCarousel from '../../components/public/HeroCarousel'
import SpecialMediaImageCarousel, { SpecialMediaVideoPreview } from '../../components/public/SpecialMediaImageCarousel'
import VideoUrlFrame from '../../components/public/VideoUrlFrame'
import WebsiteUrlCard from '../../components/public/WebsiteUrlCard'
import CryptoSection from '../../components/public/CryptoSection'
import CustomerContactChats from '../../components/public/CustomerContactChats'
import CustomerNotificationCenter from '../../components/public/CustomerNotificationCenter'
import PaginationControls from '../../components/public/PaginationControls'
import { CartProvider, useCart } from '../../context/CartContext'
import { formatPrice } from '../../utils/currency'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'
import { buildPublicSiteSeo } from '../../utils/seo'

// Inner component that uses cart context
function PublicWebsiteViewerContent({ prefetchedWebsite, prefetchedWebsiteError }) {
  const params = useParams()
  const { slug, verificationId: paramsVerificationId } = params
  const navigate = useNavigate()
  const { cartItems, itemCount } = useCart()
  const location = useLocation()
  const effectiveSlug = slug || prefetchedWebsite?.slug

  const [verificationId, setVerificationId] = useState(paramsVerificationId || '')
  const [verificationResult, setVerificationResult] = useState(null)
  const [verificationLoading, setVerificationLoading] = useState(false)
  const [website, setWebsite] = useState(null)
  const [sections, setSections] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(prefetchedWebsiteError || null)
  const [ticketDetail, setTicketDetail] = useState(null)
  const [ticketDetailError, setTicketDetailError] = useState(null)
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [currentView, setCurrentView] = useState('website')
  const [customerActivity, setCustomerActivity] = useState([])
  const [customerActivityLoading, setCustomerActivityLoading] = useState(false)
  const [customerActivityError, setCustomerActivityError] = useState('')
  const [customerActivityPage, setCustomerActivityPage] = useState(1)
  const [customerActivityTotal, setCustomerActivityTotal] = useState(0)
  const [accountSection, setAccountSection] = useState('profile')
  const CUSTOMER_ACTIVITY_LIMIT = 10

  // Gallery modal state for viewing all images/videos of an item
  const [showGalleryModal, setShowGalleryModal] = useState(false)
  const [galleryItems, setGalleryItems] = useState([])
  const [galleryStartIndex, setGalleryStartIndex] = useState(0)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [buyingTicket, setBuyingTicket] = useState(false)
  const [heroAspectRatios, setHeroAspectRatios] = useState({})
  const [logoLoadError, setLogoLoadError] = useState(false)
  const [logoRetryCount, setLogoRetryCount] = useState(0)
  const [showBuyerForm, setShowBuyerForm] = useState(false)
  const [buyerInfo, setBuyerInfo] = useState({ name: '', email: '', phone: '' })
  const [selectedItem, setSelectedItem] = useState(null)
  const [selectedItemGalleryIndex, setSelectedItemGalleryIndex] = useState(0)
  const [itemDetailsOpen, setItemDetailsOpen] = useState(false)
  const [selectedItemPreviewVideoPoster, setSelectedItemPreviewVideoPoster] = useState('')
  const [specialMediaVideo, setSpecialMediaVideo] = useState(null)
  const [specialMediaVideoLoading, setSpecialMediaVideoLoading] = useState(false)
  const [specialMediaVideoError, setSpecialMediaVideoError] = useState('')
  const [isFooterHovered, setIsFooterHovered] = useState(false)
  const [isHeaderHovered, setIsHeaderHovered] = useState(false)
  const [isBodyHovered, setIsBodyHovered] = useState(false)
  const [hoveredSectionId, setHoveredSectionId] = useState(null)
  const [hoveredCtaId, setHoveredCtaId] = useState(null)
  const [customerAuthOpen, setCustomerAuthOpen] = useState(false)
  const [customerAuthMode, setCustomerAuthMode] = useState('login')
  const [customerVerificationNotice, setCustomerVerificationNotice] = useState(null)
  const [selectedConversationIdFromNotification, setSelectedConversationIdFromNotification] = useState(null)
  const [customerAuthForm, setCustomerAuthForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  })
  const [customerAuthLoading, setCustomerAuthLoading] = useState(false)
  const [customerAuthMessage, setCustomerAuthMessage] = useState('')
  const [customerAuthError, setCustomerAuthError] = useState('')
  const [customerAuthToken, setCustomerAuthToken] = useState('')
  const [customerAuthUser, setCustomerAuthUser] = useState(null)
  const [customerProfileForm, setCustomerProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    shippingAddress: '',
    shippingCity: '',
    shippingState: '',
    shippingZip: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: ''
  })
  const [profileSaving, setProfileSaving] = useState(false)
  const [resendingVerification, setResendingVerification] = useState(false)

  const customerAuthEnabled = useMemo(() => {
    return Boolean(website?.customerAuth?.enabled || website?.customerAuthConfig?.enabled)
  }, [website?.customerAuth, website?.customerAuthConfig])

  const hasCartFeature = useMemo(() => {
    // The Cart icon should only be displayed if there's an enabled Product section
    // The backend public API already filters out disabled sections, so if a Product
    // section exists in the returned array, it's enabled and published
    const normalizeType = (section) => (section?.sectionType || section?.type || '').toString().trim().toLowerCase()

    const hasProductSection = (section) => {
      const type = normalizeType(section)
      // Only check for 'products' section type as it's the main cart driver
      return type === 'products'
    }

    // Check the sections array from the public website API (filtered for enabled sections)
    if (Array.isArray(sections) && sections.some(hasProductSection)) {
      console.log('[PublicWebsiteViewer] Product section found - Cart feature enabled')
      return true
    }
    
    // Also check website.sections as fallback
    if (Array.isArray(website?.sections) && website.sections.some(hasProductSection)) {
      console.log('[PublicWebsiteViewer] Product section found in website.sections - Cart feature enabled')
      return true
    }
    
    console.log('[PublicWebsiteViewer] No Product section found - Cart feature disabled')
    return false
  }, [sections, website?.sections])

  const closeGalleryModal = useCallback(() => setShowGalleryModal(false), [])

  const openSpecialMediaVideo = useCallback((media) => {
    if (!media?.url) return
    setSpecialMediaVideoError('')
    setSpecialMediaVideoLoading(true)
    setSpecialMediaVideo(media)
  }, [])

  const closeSpecialMediaVideo = useCallback(() => {
    setSpecialMediaVideo(null)
    setSpecialMediaVideoLoading(false)
    setSpecialMediaVideoError('')
  }, [])

  const resetCustomerAuthForm = () => {
    setCustomerAuthForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: ''
    })
  }

  const openCustomerAuthModal = (mode) => {
    setCustomerAuthMode(mode)
    setCustomerAuthError('')
    setCustomerAuthMessage('')
    resetCustomerAuthForm()
    setCustomerAuthOpen(true)
  }

  const closeCustomerAuthModal = () => {
    setCustomerAuthOpen(false)
    setCustomerAuthError('')
    setCustomerAuthMessage('')
  }

  const handleCustomerAuthFormChange = (e) => {
    const { name, value } = e.target
    setCustomerAuthForm((prev) => ({ ...prev, [name]: value }))
  }

  const getCustomerAuthStorageKey = () => {
    if (!website?._id) return null
    return `customerAuthToken:${website._id}`
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const verificationStatus = params.get('customerVerificationStatus')
    const verificationMessage = params.get('customerVerificationMessage')

    if (verificationStatus || verificationMessage) {
      setCustomerVerificationNotice({
        type: verificationStatus === 'success' ? 'success' : 'error',
        message: verificationMessage || 'Verification request completed.'
      })
    }
  }, [location.search])

  useEffect(() => {
    if (!website?._id) return
    const tokenKey = getCustomerAuthStorageKey()
    const storedToken = tokenKey ? localStorage.getItem(tokenKey) : null
    if (!storedToken) return

    const verifyCustomerAuth = async () => {
      try {
        const response = await axios.get('/customer-auth/me', {
          headers: {
            Authorization: `Bearer ${storedToken}`
          }
        })

        setCustomerAuthToken(storedToken)
        setCustomerAuthUser(response.data.customer)
        setCustomerProfileForm({
          firstName: response.data.customer?.firstName || '',
          lastName: response.data.customer?.lastName || '',
          email: response.data.customer?.email || '',
          phone: response.data.customer?.phone || '',
          shippingAddress: response.data.customer?.shippingAddress || '',
          shippingCity: response.data.customer?.shippingCity || '',
          shippingState: response.data.customer?.shippingState || '',
          shippingZip: response.data.customer?.shippingZip || '',
          billingAddress: response.data.customer?.billingAddress || '',
          billingCity: response.data.customer?.billingCity || '',
          billingState: response.data.customer?.billingState || '',
          billingZip: response.data.customer?.billingZip || ''
        })
      } catch (error) {
        console.warn('[PublicWebsiteViewer] Stored customer auth token invalid, clearing it', error?.response?.data || error?.message)
        localStorage.removeItem(tokenKey)
        setCustomerAuthToken('')
        setCustomerAuthUser(null)
      }
    }

    verifyCustomerAuth()
  }, [website?._id])

  const handleCustomerProfileChange = (e) => {
    const { name, value } = e.target
    setCustomerProfileForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleCustomerProfileSave = async (e) => {
    e.preventDefault()
    if (!customerAuthToken) return
    setProfileSaving(true)
    try {
      const response = await axios.put('/customer-profile/me', customerProfileForm, {
        headers: { Authorization: `Bearer ${customerAuthToken}` }
      })
      setCustomerAuthUser(response.data.customer)
      setCustomerProfileForm({
        firstName: response.data.customer?.firstName || '',
        lastName: response.data.customer?.lastName || '',
        email: response.data.customer?.email || '',
        phone: response.data.customer?.phone || '',
        shippingAddress: response.data.customer?.shippingAddress || '',
        shippingCity: response.data.customer?.shippingCity || '',
        shippingState: response.data.customer?.shippingState || '',
        shippingZip: response.data.customer?.shippingZip || '',
        billingAddress: response.data.customer?.billingAddress || '',
        billingCity: response.data.customer?.billingCity || '',
        billingState: response.data.customer?.billingState || '',
        billingZip: response.data.customer?.billingZip || ''
      })
      toast.success('Profile updated successfully')
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to save profile')
    } finally {
      setProfileSaving(false)
    }
  }

  const handleCustomerAuthSubmit = async (event) => {
    event.preventDefault()
    if (!website?._id) {
      setCustomerAuthError('Website information is missing.')
      return
    }

    setCustomerAuthLoading(true)
    setCustomerAuthError('')
    setCustomerAuthMessage('')

    try {
      let endpoint = '/customer-auth/login'
      let payload = {
        websiteId: website._id,
        email: customerAuthForm.email?.trim(),
        password: customerAuthForm.password
      }

      if (customerAuthMode === 'register') {
        endpoint = '/customer-auth/register'
        payload = {
          websiteId: website._id,
          firstName: customerAuthForm.firstName.trim(),
          lastName: customerAuthForm.lastName.trim(),
          email: customerAuthForm.email.trim(),
          phone: customerAuthForm.phone.trim(),
          password: customerAuthForm.password,
          confirmPassword: customerAuthForm.confirmPassword
        }
      }

      if (customerAuthMode === 'forgot') {
        endpoint = '/customer-auth/forgot-password'
        payload = {
          websiteId: website._id,
          email: customerAuthForm.email.trim()
        }
      }

      const response = await axios.post(endpoint, payload)

      if (customerAuthMode === 'login') {
        const token = response.data.token
        if (token) {
          const tokenKey = getCustomerAuthStorageKey()
          if (tokenKey) {
            localStorage.setItem(tokenKey, token)
          }
          setCustomerAuthToken(token)
          setCustomerAuthUser(response.data.customer)
          setCustomerProfileForm({
            firstName: response.data.customer?.firstName || '',
            lastName: response.data.customer?.lastName || '',
            email: response.data.customer?.email || '',
            phone: response.data.customer?.phone || '',
            shippingAddress: response.data.customer?.shippingAddress || '',
            shippingCity: response.data.customer?.shippingCity || '',
            shippingState: response.data.customer?.shippingState || '',
            shippingZip: response.data.customer?.shippingZip || '',
            billingAddress: response.data.customer?.billingAddress || '',
            billingCity: response.data.customer?.billingCity || '',
            billingState: response.data.customer?.billingState || '',
            billingZip: response.data.customer?.billingZip || ''
          })
          setCustomerAuthMessage('You are now logged in.')
          setCustomerAuthOpen(false)
        } else {
          setCustomerAuthMessage(response.data.message || 'Login completed.')
        }
      } else {
        setCustomerAuthMessage(response.data.message || 'Request completed successfully.')
        if (customerAuthMode === 'register') {
          if (!response.data.requireEmailVerification) {
            setCustomerAuthMode('login')
          }
        }
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Authentication failed.'
      setCustomerAuthError(message)
    } finally {
      setCustomerAuthLoading(false)
    }
  }

  const handleResendVerification = async () => {
    if (!customerAuthToken || !website?._id) return

    try {
      setResendingVerification(true)
      setCustomerAuthError('')
      setCustomerAuthMessage('')
      const response = await axios.post('/customer-auth/resend-verification', {
        websiteId: website._id,
        email: customerAuthUser?.email
      }, {
        headers: {
          Authorization: `Bearer ${customerAuthToken}`
        }
      })
      setCustomerAuthMessage(response.data.message || 'Verification email resent successfully.')
    } catch (error) {
      const message = error.response?.data?.message || 'Verification email could not be sent. Please try again.'
      setCustomerAuthError(message)
    } finally {
      setResendingVerification(false)
    }
  }

  const handleCustomerAuthLogout = async () => {
    if (!customerAuthToken) {
      setCustomerAuthUser(null)
      return
    }

    try {
      await axios.post('/customer-auth/logout', null, {
        headers: {
          Authorization: `Bearer ${customerAuthToken}`
        }
      })
    } catch (error) {
      console.warn('[PublicWebsiteViewer] Logout request failed', error?.response?.data || error?.message)
    }

    const tokenKey = getCustomerAuthStorageKey()
    if (tokenKey) {
      localStorage.removeItem(tokenKey)
    }
    setCustomerAuthToken('')
    setCustomerAuthUser(null)
    setCustomerActivity([])
    setCustomerActivityPage(1)
    setCustomerActivityTotal(0)
    setCustomerActivityError('')
    setCustomerAuthMessage('You have been logged out.')
  }

  const handleOpenConversationFromNotification = (conversationId) => {
    setAccountSection('messages')
    setSelectedConversationIdFromNotification(conversationId)
  }

  const fetchCustomerActivity = useCallback(async (page = 1) => {
    if (!customerAuthToken || !website?._id) return

    setCustomerActivityLoading(true)
    setCustomerActivityError('')

    try {
      const response = await axios.get('/customer-auth/activity', {
        headers: {
          Authorization: `Bearer ${customerAuthToken}`
        },
        params: {
          page,
          limit: CUSTOMER_ACTIVITY_LIMIT
        }
      })

      setCustomerActivity(response.data.activities || [])
      setCustomerActivityPage(page)
      setCustomerActivityTotal(response.data.pagination?.total || 0)
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Unable to load activity history'
      setCustomerActivityError(message)
      if (error.response?.status === 401) {
        const tokenKey = getCustomerAuthStorageKey()
        if (tokenKey) localStorage.removeItem(tokenKey)
        setCustomerAuthToken('')
        setCustomerAuthUser(null)
      }
    } finally {
      setCustomerActivityLoading(false)
    }
  }, [customerAuthToken, website?._id])

  useEffect(() => {
    if (!customerAuthUser) return
    if (currentView !== 'account') return

    fetchCustomerActivity(customerActivityPage)
  }, [customerAuthUser, currentView, customerActivityPage, fetchCustomerActivity])

  const buyTicket = async () => {
    if (!ticketDetail || !website || !buyerInfo.email) return
    
    setBuyingTicket(true)
    try {
      // Determine payment gateway
      const paymentGateway = website?.paymentSettings?.paymentGateway || 'paystack'
      const endpoint = `/ticket-payments/${paymentGateway}/checkout`
      
      const response = await axios.post(endpoint, {
        websiteId: website._id,
        ticketSlug: ticketDetail.slug,
        quantity: 1,
        buyerEmail: buyerInfo.email,
        buyerName: buyerInfo.name,
        buyerPhone: buyerInfo.phone
      })
      
      const { paymentUrl } = response.data
      if (paymentUrl) {
        window.location.href = paymentUrl
      } else {
        toast.error('Payment URL not received')
      }
    } catch (err) {
      console.error('Ticket purchase error:', err)
      toast.error(err.response?.data?.message || 'Failed to initiate ticket purchase')
    } finally {
      setBuyingTicket(false)
      setShowBuyerForm(false)
    }
  }

  const formatWhatsAppNumber = (raw) => {
    if (!raw) return ''
    const cleaned = raw.toString().replace(/[^0-9]/g, '')
    return cleaned
  }

  const formatSocialUrl = (url) => {
    if (!url || typeof url !== 'string') return ''
    const trimmed = url.trim()
    if (!trimmed) return ''
    if (/^(https?:)?\/\//i.test(trimmed)) return trimmed.startsWith('http') ? trimmed : `https:${trimmed}`
    return `https://${trimmed}`
  }

  function updatePublishedWebsiteFavicon(website) {
  const favicon48 = website?.favicon?.favicon48 || website?.favicon?.favicon48Url || website?.favicon?.favicon96 || website?.favicon?.favicon128 || website?.favicon?.favicon192 || null
  const favicon96 = website?.favicon?.favicon96 || website?.favicon?.favicon96Url || favicon48 || null
  const favicon128 = website?.favicon?.favicon128 || website?.favicon?.favicon128Url || favicon96 || null
  const favicon192 = website?.favicon?.favicon192 || website?.favicon?.favicon192Url || favicon128 || null

  if (!favicon48 && !favicon96 && !favicon128 && !favicon192) {
    console.warn('[PUBLIC-FAVICON] No generated favicon found in public website data')
    return
  }

  console.log('[PUBLIC-FAVICON] Updating published website favicon', {
    version: website?.favicon?.version,
    favicon48: favicon48 || null,
    favicon96: favicon96 || null,
    favicon128: favicon128 || null,
    favicon192: favicon192 || null,
    logoUrl: website?.logo?.url || null
  })

  document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach((element) => element.remove())

  const faviconLinks = [
    { size: '48x48', href: favicon48 },
    { size: '96x96', href: favicon96 },
    { size: '128x128', href: favicon128 },
    { size: '192x192', href: favicon192 }
  ]

  faviconLinks.forEach(({ size, href }) => {
    if (!href) return
    const link = document.createElement('link')
    link.rel = 'icon'
    link.type = 'image/png'
    link.sizes = size
    link.href = href
    document.head.appendChild(link)
  })

  if (favicon48) {
    const shortcut = document.createElement('link')
    shortcut.rel = 'shortcut icon'
    shortcut.type = 'image/png'
    shortcut.href = favicon48
    document.head.appendChild(shortcut)
  }

  if (favicon192) {
    const apple = document.createElement('link')
    apple.rel = 'apple-touch-icon'
    apple.sizes = '192x192'
    apple.href = favicon192
    document.head.appendChild(apple)
  }

  console.log('[PUBLIC-FAVICON] Published website favicon successfully updated', {
    version: website?.favicon?.version,
    activeFavicon: favicon48 || favicon96 || favicon128 || favicon192
  })
}

const getItemWhatsAppMessage = (item) => {
    if (!item) return ''

    let messageParts = [
      `Hi! I'm interested in this item:`,
      `Title: ${item.title || item.name || 'N/A'}`,
      `Description: ${item.description || 'N/A'}`
    ]

    // Include price if available
    if (item.price !== undefined && item.price !== null && item.price !== '') {
      const formattedPrice = formatPrice(item.price, item.currency)
      messageParts.push(`Price: ${formattedPrice}`)
    }

    if (item.link) {
      messageParts.push(`Link: ${item.link}`)
    }

    // Handle gallery items with better URL management
    if (item.gallery && item.gallery.length > 0) {
      const externalUrls = []
      
      item.gallery.forEach((g, i) => {
        const mediaUrl = g.image?.url || g.url || g.video?.url || g.videoUrl
        // Separate base64 from external URLs
        if (mediaUrl && !mediaUrl.startsWith('data:')) {
          externalUrls.push(`${i + 1}. ${mediaUrl}`)
        }
      })
      
      // Add gallery header
      messageParts.push(`Gallery (${item.gallery.length} items):`)
      
      // Include external URLs if available
      if (externalUrls.length > 0) {
        externalUrls.forEach(url => messageParts.push(url))
      } else {
        // If only base64 images, mention they're available on website
        messageParts.push(`Images/videos are available for preview on the website`)
      }
    }

    // Include any other shallow key values to supplement details
    const otherKeys = Object.keys(item).filter(key => !['title', 'name', 'description', 'price', 'currency', 'link', 'gallery', 'image', 'video', 'whatsappContact', 'whatsappNumber', 'id'].includes(key))
    otherKeys.forEach((key) => {
      const value = item[key]
      if (value && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
        messageParts.push(`${key}: ${value}`)
      }
    })

    messageParts.push('')
    messageParts.push('Please provide more information and availability.')

    return messageParts.join('\n')
  }

  const handleItemWhatsAppContact = (item) => {
    const rawNumber = item.whatsappContact || item.whatsappNumber
    const number = formatWhatsAppNumber(rawNumber)
    if (!number) return

    const message = getItemWhatsAppMessage(item)
    const whatsappUrl = `https://wa.me/${number}?text=${encodeURIComponent(message)}`
    
    // Get button styling info for logging
    const screenWidth = window.innerWidth
    const buttonStyle = {
      backgroundColor: '#10B981',
      color: '#FFFFFF',
      hasImportant: true
    }
    
    window.open(whatsappUrl, '_blank')
    
    console.log('[PublicWebsiteViewer] Section item WhatsApp button clicked:', {
      itemTitle: item.name || item.title,
      itemId: item._id,
      phoneNumber: number,
      timestamp: new Date().toISOString(),
      buttonColor: 'permanent green (#10B981)',
      styling: 'inline with !important flags for override',
      deviceType: screenWidth < 768 ? 'mobile' : screenWidth < 1024 ? 'tablet' : 'desktop',
      screenWidth: screenWidth
    })
    
    // Log WhatsApp interaction to backend with comprehensive styling verification
    try {
      fetch('/websites-public/log/whatsapp-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          websiteId: website?._id,
          websiteSlug: website?.publicUrl,
          sectionType: 'section-items',
          itemName: item.name || item.title,
          itemId: item._id,
          phoneNumber: number,
          timestamp: new Date().toISOString(),
          buttonClass: 'permanent-whatsapp-button',
          buttonStyling: buttonStyle,
          buttonState: 'default',
          deviceType: screenWidth < 768 ? 'mobile' : screenWidth < 1024 ? 'tablet' : 'desktop',
          screenWidth: screenWidth
        })
      }).catch(err => console.warn('[PublicWebsiteViewer] Failed to log WhatsApp contact:', err.message))
    } catch (err) {
      console.warn('[PublicWebsiteViewer] Error logging WhatsApp contact:', err.message)
    }
  }

  const socialMediaLinks = [
    {
      name: 'whatsapp',
      href: formatSocialUrl(website?.socialLinks?.whatsapp),
      icon: WhatsAppIcon
    },
    {
      name: 'instagram',
      href: formatSocialUrl(website?.socialLinks?.instagram),
      icon: FaInstagram
    },
    {
      name: 'facebook',
      href: formatSocialUrl(website?.socialLinks?.facebook),
      icon: FaFacebook
    },
    {
      name: 'tiktok',
      href: formatSocialUrl(website?.socialLinks?.tiktok),
      icon: FaTiktok
    },
    {
      name: 'twitter',
      href: formatSocialUrl(website?.socialLinks?.twitter),
      icon: FaTwitter
    }
  ].filter(link => link.href)

  // Fetch public website data by slug or use prefetchedWebsite if provided
  useEffect(() => {
    let mounted = true

    const fetchWebsite = async () => {
      try {
        setError(null)

        if (prefetchedWebsite) {
          if (!mounted) return
          setWebsite(prefetchedWebsite)
          setSections((prefetchedWebsite && prefetchedWebsite.sections) || [])
          setLoading(false)
          console.log('[PublicWebsiteViewer] Using prefetched website data:', {
            slug: prefetchedWebsite?.slug,
            websiteId: prefetchedWebsite?._id,
            sections: (prefetchedWebsite?.sections || []).length,
            paymentGateway: prefetchedWebsite?.paymentSettings?.paymentGateway
          })
          console.log('[PublicWebsiteViewer] ⭐ STYLING CONFIGURATION (PREFETCHED):', {
            useDefaultStyling: prefetchedWebsite?.useDefaultStyling,
            useDefaultStylingType: typeof prefetchedWebsite?.useDefaultStyling,
            willUseDefaults: prefetchedWebsite?.useDefaultStyling !== false
          })
          console.log('[PublicWebsiteViewer] Prefetched website logo data:', prefetchedWebsite?.logo)

          if (prefetchedWebsite?.slug) {
            try {
              const response = await axios.get(`/websites-public/${prefetchedWebsite.slug}`)
              if (!mounted) return
              const w = response.data.website
              if (w) {
                setWebsite(w)
                setSections((w && w.sections) || [])
                console.log('[PublicWebsiteViewer] Refreshed prefetched website from backend:', { slug: w?.slug, websiteId: w?._id, sections: (w?.sections || []).length })
              }
            } catch (refreshError) {
              console.warn('[PublicWebsiteViewer] Failed to refresh prefetched website:', refreshError.response?.data || refreshError.message)
              if (mounted && refreshError.response?.status === 404) {
                const message = refreshError.response?.data?.message || 'Website not found or not published'
                setError(message)
                setWebsite(null)
                setSections([])
              }
            }
          }

          return
        }

        if (!effectiveSlug) return

        setLoading(true)
        const response = await axios.get(`/websites-public/${effectiveSlug}`)
        const w = response.data.website
        if (!mounted) return
        setWebsite(w)
        setSections((w && w.sections) || [])
        console.log('[PublicWebsiteViewer] Website loaded:', { slug: w?.slug, websiteId: w?._id, sections: (w?.sections || []).length, paymentGateway: w?.paymentSettings?.paymentGateway })
        console.log('[PublicWebsiteViewer] ⭐ STYLING CONFIGURATION (FETCHED):', {
          useDefaultStyling: w?.useDefaultStyling,
          useDefaultStylingType: typeof w?.useDefaultStyling,
          willUseDefaults: w?.useDefaultStyling !== false,
          hasCustomHeader: !!w?.headerStyling,
          hasCustomFooter: !!w?.footerStyling,
          hasCustomBody: !!w?.bodyStyling,
          hasCustomForm: !!w?.sectionFormStyling
        })
        console.log('[PublicWebsiteViewer] Website logo data:', w?.logo)
      } catch (err) {
        console.error('[PublicWebsiteViewer] Failed to load website:', err)
        const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load website'
        if (mounted) {
          setError(msg)
          setWebsite(null)
          setSections([])
        }
      } finally {
        if (mounted && !prefetchedWebsite) setLoading(false)
      }
    }

    fetchWebsite()

    return () => { mounted = false }
  }, [effectiveSlug, prefetchedWebsite])

  // Refetch website data when page becomes visible (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && website && effectiveSlug) {
        console.log('[PublicWebsiteViewer] Page became visible, refetching website data')
        // Refetch website data
        const refetchWebsite = async () => {
          try {
            const response = await axios.get(`/websites-public/${effectiveSlug}`)
            const w = response.data.website
            setWebsite(w)
            setSections((w && w.sections) || [])
            console.log('[PublicWebsiteViewer] Website refetched:', { slug: w?.slug, websiteId: w?._id, sections: (w?.sections || []).length })
          } catch (err) {
            console.error('[PublicWebsiteViewer] Failed to refetch website:', err)
            if (err.response?.status === 404) {
              const message = err.response?.data?.message || 'Website not found or not published'
              setError(message)
              setWebsite(null)
              setSections([])
            }
          }
        }
        refetchWebsite()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [website, effectiveSlug])

  // Determine verificationId when the route or path changes (supports /store/:slug/verify/:id and custom domain /verify/:id)
  useEffect(() => {
    if (paramsVerificationId) {
      setVerificationId(paramsVerificationId)
    } else if (location.pathname.startsWith('/verify/')) {
      setVerificationId(location.pathname.replace('/verify/', ''))
    } else {
      setVerificationId('')
      setVerificationResult(null)
    }
  }, [paramsVerificationId, location.pathname])

  // Fetch QR verification result when verificationId is present
  useEffect(() => {
    if (!verificationId) {
      setVerificationResult(null)
      return
    }

    const verifyCard = async () => {
      setVerificationLoading(true)
      try {
        const response = await axios.get(`/cards/verify/${verificationId}`)
        if (response.data.success) {
          setVerificationResult(response.data.verification)
        } else {
          setVerificationResult({ error: response.data.message || 'Verification failed' })
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Verification failed'
        setVerificationResult({ error: message })
      } finally {
        setVerificationLoading(false)
      }
    }

    verifyCard()
  }, [verificationId])

  // Set meta title and description only. The public website favicon is sourced from the generated website-specific favicon asset, not the logo.
  useEffect(() => {
    const currentWebsite = website || prefetchedWebsite
    const currentSeoMeta = buildPublicSiteSeo({
      website: currentWebsite,
      host: typeof window !== 'undefined' ? window.location.hostname : '',
      pathname: location.pathname,
      search: location.search,
      protocol: typeof window !== 'undefined' ? window.location.protocol.replace(':', '') : 'https'
    })

    console.log('[PublicWebsiteViewer] Public website identity', {
      websiteId: currentWebsite?._id,
      slug: currentWebsite?.slug,
      host: typeof window !== 'undefined' ? window.location.hostname : '',
      title: currentSeoMeta.title,
      description: currentSeoMeta.description,
      canonicalUrl: currentSeoMeta.canonicalUrl,
      favicon: currentWebsite?.favicon?.favicon128Url || currentWebsite?.favicon?.url || null
    })

    if (currentWebsite) {
      try {
        document.title = currentSeoMeta.title || currentWebsite.name || 'Website'
        const metaDesc = document.querySelector('meta[name="description"]')
        if (metaDesc) {
          metaDesc.content = currentSeoMeta.description || currentWebsite.seo?.metaDescription || currentWebsite.description || ''
        }
      } catch (error) {
        console.error('[PublicWebsiteViewer] Error setting meta data:', error)
      }
    }
  }, [prefetchedWebsite, website, location.pathname, location.search])

  useEffect(() => {
    const currentWebsite = website || prefetchedWebsite
    if (!currentWebsite) return

    console.log('[PUBLISHED-FAVICON] Applying favicon', {
      websiteId: currentWebsite?._id,
      faviconVersion: currentWebsite?.favicon?.version,
      favicon48: currentWebsite?.favicon?.favicon48 || currentWebsite?.favicon?.favicon48Url || null,
      favicon96: currentWebsite?.favicon?.favicon96 || currentWebsite?.favicon?.favicon96Url || null,
      favicon128: currentWebsite?.favicon?.favicon128 || currentWebsite?.favicon?.favicon128Url || null,
      favicon192: currentWebsite?.favicon?.favicon192 || currentWebsite?.favicon?.favicon192Url || null,
      logoUrl: currentWebsite?.logo?.url || null
    })

    updatePublishedWebsiteFavicon(currentWebsite)

    console.log('[PUBLISHED-FAVICON] Active favicon links:', Array.from(document.querySelectorAll('link[rel*="icon"]')).map((link) => ({
      rel: link.rel,
      type: link.type,
      sizes: link.sizes?.value,
      href: link.href
    })))
  }, [prefetchedWebsite, website])

  // Log styling configuration when website changes
  useEffect(() => {
    const currentWebsite = website || prefetchedWebsite
    if (currentWebsite) {
      const useDefault = currentWebsite?.useDefaultStyling !== false
      console.log('⭐ [STYLING-CONFIG] Website styling configuration updated:', {
        websiteId: currentWebsite?._id,
        slug: currentWebsite?.slug,
        useDefaultStyling: currentWebsite?.useDefaultStyling,
        willUseDefaults: useDefault,
        message: useDefault ? '✅ DEFAULT design will be applied' : '🎨 CUSTOM design will be applied',
        timestamp: new Date().toISOString()
      })
    }
  }, [prefetchedWebsite, website])

  // Ticket detail by query param
  useEffect(() => {
    const query = new URLSearchParams(location.search)
    const ticketSlug = query.get('ticket')

    if (!ticketSlug || !website) {
      setTicketDetail(null)
      setTicketDetailError(null)
      return
    }

    const fetchTicketDetail = async () => {
      try {
        let response;
        if (slug) {
          // Regular domain - use slug-based route
          response = await axios.get(`/event-tickets/store/${slug}/${ticketSlug}`)
        } else if (website?._id) {
          // Custom domain - use website ID-based route
          response = await axios.get(`/event-tickets/website-public/${website._id}/${ticketSlug}`)
        }
        setTicketDetail(response.data.ticket)
        setTicketDetailError(null)
      } catch (err) {
        console.error('[PublicWebsiteViewer] fetchTicketDetail', err)
        setTicketDetail(null)
        setTicketDetailError(err.response?.data?.message || 'Unable to load ticket details')
      }
    }

    fetchTicketDetail()
  }, [location.search, website])

  const handleOpenGallery = useCallback((item, startIndex = 0) => {
    if (!item) return
    let imgs = []
    if (item.gallery && item.gallery.length > 0) {
      imgs = item.gallery.filter((g) => (
        g.image?.url || g.url || g.video?.url || g.videoUrl || getWebsiteUrlFromGalleryItem(g)
      ))
    } else if (item.image) {
      const imageUrl = typeof item.image === 'string' ? item.image : item.image?.url
      if (imageUrl) imgs.push({ image: { url: imageUrl } })
    } else if (item.video) {
      const videoUrl = typeof item.video === 'string' ? item.video : item.video?.url
      if (videoUrl) imgs.push({ video: { url: videoUrl } })
    } else if (item.link) {
      const linkUrl = typeof item.link === 'string' ? item.link : item.link?.url
      if (linkUrl) imgs.push({ link: { url: linkUrl } })
    }
    if (imgs.length === 0) return
    setGalleryItems(imgs)
    setGalleryStartIndex(startIndex || 0)
    setShowGalleryModal(true)
  }, [])

  const handleHeroImageLoad = (sectionId, naturalWidth, naturalHeight) => {
    if (!sectionId || !naturalWidth || !naturalHeight) return

    setHeroAspectRatios((prev) => ({
      ...prev,
      [sectionId]: naturalHeight / naturalWidth,
    }))
  }

  const handleViewItem = useCallback((item, startIndex = 0) => {
    if (!item) {
      console.warn('[PublicWebsiteViewer] handleViewItem called with empty item')
      return
    }
    const normalizedStartIndex = Number.isFinite(startIndex) ? Math.max(0, Math.floor(startIndex)) : 0
    console.log('[PublicWebsiteViewer] Opening item details modal for:', { title: item.title, id: item.id, startIndex: normalizedStartIndex })
    setSelectedItem(item)
    setSelectedItemGalleryIndex(normalizedStartIndex)
    setItemDetailsOpen(true)
  }, [])

  // Prepare preview media for the selected item modal
  const selectedItemPreviewMedia = useMemo(() => {
    if (!selectedItem) return null

    // Prefer gallery media when a gallery exists, so clicking thumbnails updates the preview immediately.
    if (Array.isArray(selectedItem.gallery) && selectedItem.gallery.length > 0) {
      const idx = Math.min(Math.max(0, selectedItemGalleryIndex || 0), selectedItem.gallery.length - 1)
      console.log('[selectedItemPreviewMedia] Gallery preview:', {
        itemId: selectedItem.id,
        selectedIndex: selectedItemGalleryIndex,
        normalizedIndex: idx,
        galleryLength: selectedItem.gallery.length,
        selectedMediaStructure: selectedItem.gallery[idx]
      })

      const selectedMedia = selectedItem.gallery[idx]
      if (selectedMedia) {
        const galleryVideoEntry = selectedMedia.video || (selectedMedia.videoUrl ? {
          url: selectedMedia.videoUrl,
          videoUrl: selectedMedia.videoUrl,
          source: 'url'
        } : null)
        const galleryVideo = getVideoUrl(galleryVideoEntry)
        const galleryPoster = getVideoPoster(galleryVideoEntry)
        const galleryImage = selectedMedia.image?.url || selectedMedia.url
        const galleryLink = getWebsiteUrlFromGalleryItem(selectedMedia)

        console.log('[selectedItemPreviewMedia] Selected media analysis:', {
          hasImage: !!galleryImage,
          hasVideo: !!galleryVideo,
          galleryImage,
          galleryVideo
        })

        if (galleryVideo) {
          console.log('[selectedItemPreviewMedia] Returning gallery video:', galleryVideo)
          return {
            ...galleryVideoEntry,
            type: 'video',
            url: galleryVideo,
            videoUrl: galleryVideoEntry?.videoUrl || galleryVideo,
            poster: galleryPoster,
            thumbnail: galleryVideoEntry?.thumbnail || galleryPoster,
            embedUrl: galleryVideoEntry?.embedUrl || selectedMedia.embedUrl || ''
          }
        }

        if (galleryImage) {
          console.log('[selectedItemPreviewMedia] Returning gallery image:', galleryImage)
          return { type: 'image', url: galleryImage }
        }

        if (galleryLink) return { type: 'link', url: galleryLink }
      }

      console.log('[selectedItemPreviewMedia] Selected item gallery has no valid media, using fallback')
      const fallback = selectedItem.gallery[0]
      const fallbackVideoEntry = fallback?.video || (fallback?.videoUrl ? {
        url: fallback.videoUrl,
        videoUrl: fallback.videoUrl,
        source: 'url'
      } : null)
      const fallbackVideo = getVideoUrl(fallbackVideoEntry)
      const fallbackPoster = getVideoPoster(fallbackVideoEntry)
      if (fallbackVideo) {
        console.log('[selectedItemPreviewMedia] Fallback video:', fallbackVideo)
        return {
          ...fallbackVideoEntry,
          type: 'video',
          url: fallbackVideo,
          videoUrl: fallbackVideoEntry?.videoUrl || fallbackVideo,
          poster: fallbackPoster,
          thumbnail: fallbackVideoEntry?.thumbnail || fallbackPoster
        }
      }
      const fallbackImage = fallback?.image?.url || fallback?.url
      const fallbackLink = getWebsiteUrlFromGalleryItem(fallback)
      if (fallbackImage) {
        console.log('[selectedItemPreviewMedia] Fallback image:', fallbackImage)
        return { type: 'image', url: fallbackImage }
      }
      if (fallbackLink) return { type: 'link', url: fallbackLink }
    }

    // Direct image fallback
    if (selectedItem.image) {
      const imageUrl = typeof selectedItem.image === 'string' ? selectedItem.image : selectedItem.image?.url
      if (imageUrl) {
        console.log('[selectedItemPreviewMedia] Selected item has direct image:', { itemId: selectedItem.id, imageUrl })
        return { type: 'image', url: imageUrl }
      }
    }

    // Direct video fallback
    if (selectedItem.video) {
      const videoUrl = getVideoUrl(selectedItem.video)
      const videoPoster = getVideoPoster(selectedItem.video)
      if (videoUrl) {
        console.log('[selectedItemPreviewMedia] Selected item has direct video:', videoUrl)
        return {
          ...selectedItem.video,
          type: 'video',
          url: videoUrl,
          videoUrl: selectedItem.video?.videoUrl || videoUrl,
          poster: videoPoster,
          thumbnail: selectedItem.video?.thumbnail || videoPoster
        }
      }
    }

    console.log('[selectedItemPreviewMedia] No media found for item:', selectedItem.id)
    return null
  }, [selectedItem, selectedItemGalleryIndex])

  // Reset gallery index when a new item is selected so preview starts at first media
  useEffect(() => {
    if (selectedItem) {
      console.log('[PublicWebsiteViewer] Item selected, resetting gallery index to 0:', { 
        itemId: selectedItem.id, 
        itemTitle: selectedItem.title,
        galleryLength: selectedItem.gallery?.length || 0
      })
      setSelectedItemGalleryIndex(0)
    }
  }, [selectedItem])

  // Generate poster for the preview video when selectedItemPreviewMedia changes
  useEffect(() => {
    let cancelled = false

    if (!selectedItemPreviewMedia || selectedItemPreviewMedia.type !== 'video') {
      setSelectedItemPreviewVideoPoster('')
      return () => {
        cancelled = true
      }
    }

    const directPoster = selectedItemPreviewMedia.poster
    if (directPoster) {
      setSelectedItemPreviewVideoPoster(directPoster)
      return () => {
        cancelled = true
      }
    }

    const videoUrl = selectedItemPreviewMedia.url
    if (!videoUrl) {
      setSelectedItemPreviewVideoPoster('')
      return () => {
        cancelled = true
      }
    }

    const resolvePoster = async () => {
      const generatedPoster = await generateVideoPosterFromUrl(videoUrl)
      if (!cancelled) {
        setSelectedItemPreviewVideoPoster(generatedPoster)
      }
    }

    resolvePoster()

    return () => {
      cancelled = true
    }
  }, [selectedItemPreviewMedia])

  // Ref to the main preview video so we can force reload/play when source changes
  const selectedPreviewVideoRef = useRef(null)

  useEffect(() => {
    const videoEl = selectedPreviewVideoRef.current
    const url = selectedItemPreviewMedia?.type === 'video' ? selectedItemPreviewMedia.url : null
    if (!videoEl) return
    // Force reload of the new source
    try {
      videoEl.load()
      videoEl.currentTime = 0
      const p = videoEl.play()
      if (p && typeof p.then === 'function') p.catch(() => { /* autoplay blocked, user can click */ })
      console.log('[PublicWebsiteViewer] triggered load/play for preview video:', url)
    } catch (err) {
      console.warn('[PublicWebsiteViewer] failed to reload/play preview video', err)
    }
  }, [selectedItemPreviewMedia?.url])

  // Unified section item open behavior. Prefer gallery if media exists, fallback to details modal.
  const openSectionItem = useCallback((item, startIndex = 0) => {
    if (!item) return

    const hasGallery = Array.isArray(item.gallery) && item.gallery.length > 0
    const hasImage = !!(item.image || item.image?.url)
    const hasVideo = !!(item.video || item.video?.url)

    if (hasGallery || hasImage || hasVideo) {
      console.log('[PublicWebsiteViewer] openSectionItem (gallery preferred):', { itemId: item.id || item._id, title: item.title || item.name, startIndex, hasGallery, hasImage, hasVideo })
      handleOpenGallery(item, startIndex)
      return
    }

    console.log('[PublicWebsiteViewer] openSectionItem no media, model details instead:', { itemId: item.id || item._id, title: item.title || item.name })
    handleViewItem(item)
  }, [handleOpenGallery, handleViewItem])

  // Per-section item search filters (search by title or description)
  const [itemFilters, setItemFilters] = useState({})

  const handleItemFilterChange = (sectionId, value) => {
    setItemFilters((prev) => ({ ...prev, [sectionId]: value }))
    // Reset to page 1 when filter changes
    setItemPagination((prev) => ({ ...prev, [sectionId]: 1 }))
  }

  // Per-section pagination (page number for items when 10+ items exist)
  const [itemPagination, setItemPagination] = useState({})
  const [hoveredSectionItemCards, setHoveredSectionItemCards] = useState(new Set())
  const [hoveredSectionItemButtons, setHoveredSectionItemButtons] = useState(new Set())
  const [isHoveredWhatsAppModal, setIsHoveredWhatsAppModal] = useState(false)
  const ITEMS_PER_PAGE = 10

  const handleItemPageChange = (sectionId, page) => {
    setItemPagination((prev) => ({ ...prev, [sectionId]: page }))
  }

  const handleContinueShopping = () => {
    setCurrentView('website')
    
    // Find the products section and scroll to it
    setTimeout(() => {
      const sections = website?.sections || []
      const productsSectionIndex = sections.findIndex(section => 
        section.type === 'products' || section.content?.type === 'products'
      )
      
      if (productsSectionIndex !== -1) {
        const el = document.getElementById(`section-${productsSectionIndex}`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
    }, 100) // Small delay to ensure the view has switched
  }

  const renderCTA = (section) => {
    const ctaText = (section.content?.ctaText || section.ctaText || section.cta?.text || '').toString().trim()
    const ctaLink = (section.content?.ctaLink || section.ctaLink || section.cta?.link || '').toString().trim()
    
    if (!ctaText || !ctaLink) return null
    
    const ctaStyle = {}
    const hasCustomBackground = !!section.styling?.ctaButtonBackgroundColor
    const hasCustomTextColor = !!section.styling?.ctaButtonTextColor
    const isHovered = hoveredCtaId === section._id

    if (hasCustomBackground) {
      ctaStyle.backgroundColor = isHovered ? (section.styling.ctaButtonHoverBackgroundColor || section.styling.ctaButtonBackgroundColor) : section.styling.ctaButtonBackgroundColor
    }
    if (hasCustomTextColor) {
      ctaStyle.color = isHovered ? (section.styling.ctaButtonHoverTextColor || section.styling.ctaButtonTextColor) : section.styling.ctaButtonTextColor
    }

    // Build className conditionally. If a custom background color is provided,
    // do not apply default background/hover classes that would override it.
    const baseClasses = "px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-lg font-semibold transition-colors text-sm sm:text-base"
    const defaultBackgroundClasses = hasCustomBackground ? "" : " bg-primary-600 hover:bg-primary-700"
    const defaultTextClasses = hasCustomTextColor ? "" : " text-white"
    const ctaClassName = `${baseClasses}${defaultBackgroundClasses}${defaultTextClasses}`
    
    return (
      <div className="mt-8 text-center">
        <a
          href={ctaLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`${ctaClassName.trim()} transition-colors`}
          style={Object.keys(ctaStyle).length ? ctaStyle : undefined}
          onMouseEnter={() => setHoveredCtaId(section._id)}
          onMouseLeave={() => setHoveredCtaId(null)}
        >
          {ctaText}
        </a>
      </div>
    )
  }

  // Resolve uploaded logo URL to an absolute backend URL so the browser can fetch it.
  // ONLY use website-level uploaded logo from backend (no local preview data URLs)
  const effectiveWebsite = website || prefetchedWebsite
  const seoMeta = useMemo(() => buildPublicSiteSeo({
    website: effectiveWebsite,
    host: typeof window !== 'undefined' ? window.location.hostname : '',
    pathname: location.pathname,
    search: location.search,
    protocol: typeof window !== 'undefined' ? window.location.protocol.replace(':', '') : 'https'
  }), [effectiveWebsite, location.pathname, location.search])
  const effectiveSections = sections
  const headerPositionedInNextSection = effectiveWebsite?.headerStyling?.positioning === 'next-section'
  const headerOrder = Number.isInteger(effectiveWebsite?.headerOrder)
    ? Math.max(0, Math.min(effectiveWebsite.headerOrder, effectiveSections.length))
    : 0
  const movedHeader = headerOrder > 0
  const [headerSlotElement, setHeaderSlotElement] = useState(null)

  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const updateHeaderSlot = () => setHeaderSlotElement(document.getElementById('public-header-slot'))
    updateHeaderSlot()
    const frame = window.requestAnimationFrame(updateHeaderSlot)
    return () => window.cancelAnimationFrame(frame)
  }, [headerOrder, effectiveSections.length, currentView])

  // Default styling constants for professional appearance
  const DEFAULT_STYLING = {
    header: {
      backgroundColor: '#FFFFFF',
      textColor: '#000000',
      hoverBackgroundColor: '#F3F4F6',
      hoverTextColor: '#374151'
    },
    footer: {
      backgroundColor: '#111827',
      textColor: '#FFFFFF',
      hoverBackgroundColor: '#1F2937',
      hoverTextColor: '#F9FAFB'
    },
    body: {
      backgroundColor: '#FFFFFF',
      hoverBackgroundColor: '#F3F4F6'
    },
    form: {
      backgroundColor: '#FFFFFF',
      textColor: '#111827',
      hoverBackgroundColor: '#F9FAFB',
      hoverTextColor: '#374151',
      buttonBackgroundColor: '#3B82F6',
      buttonTextColor: '#FFFFFF',
      hoverButtonBackgroundColor: '#2563eb',
      hoverButtonTextColor: '#ffffff'
    },
    cta: {
      backgroundColor: '#3B82F6',
      textColor: '#FFFFFF',
      hoverBackgroundColor: '#2563eb',
      hoverTextColor: '#FFFFFF'
    }
  }

  // Helper to get styling - returns default if useDefaultStyling is true, otherwise returns custom
  const getHeaderStyling = () => {
    const useDefault = effectiveWebsite?.useDefaultStyling !== false
    if (useDefault) {
      console.log('🎨 [STYLING] Header: Using DEFAULT styling', {
        flag: effectiveWebsite?.useDefaultStyling,
        colors: DEFAULT_STYLING.header
      })
      return DEFAULT_STYLING.header
    }
    console.log('🎨 [STYLING] Header: Using CUSTOM styling', {
      flag: effectiveWebsite?.useDefaultStyling,
      colors: effectiveWebsite?.headerStyling
    })
    return effectiveWebsite?.headerStyling || DEFAULT_STYLING.header
  }

  const getFooterStyling = () => {
    const useDefault = effectiveWebsite?.useDefaultStyling !== false
    if (useDefault) {
      console.log('🎨 [STYLING] Footer: Using DEFAULT styling', {
        flag: effectiveWebsite?.useDefaultStyling,
        colors: DEFAULT_STYLING.footer
      })
      return DEFAULT_STYLING.footer
    }
    console.log('🎨 [STYLING] Footer: Using CUSTOM styling', {
      flag: effectiveWebsite?.useDefaultStyling,
      colors: effectiveWebsite?.footerStyling
    })
    return effectiveWebsite?.footerStyling || DEFAULT_STYLING.footer
  }

  const getBodyStyling = () => {
    const useDefault = effectiveWebsite?.useDefaultStyling !== false
    if (useDefault) {
      console.log('🎨 [STYLING] Body: Using DEFAULT styling', {
        flag: effectiveWebsite?.useDefaultStyling,
        colors: DEFAULT_STYLING.body
      })
      return DEFAULT_STYLING.body
    }
    console.log('🎨 [STYLING] Body: Using CUSTOM styling', {
      flag: effectiveWebsite?.useDefaultStyling,
      colors: effectiveWebsite?.bodyStyling
    })
    return effectiveWebsite?.bodyStyling || DEFAULT_STYLING.body
  }

  const getFormStyling = () => {
    const useDefault = effectiveWebsite?.useDefaultStyling !== false
    if (useDefault) {
      console.log('🎨 [STYLING] Form: Using DEFAULT styling', {
        flag: effectiveWebsite?.useDefaultStyling,
        colors: DEFAULT_STYLING.form
      })
      return DEFAULT_STYLING.form
    }
    console.log('🎨 [STYLING] Form: Using CUSTOM styling', {
      flag: effectiveWebsite?.useDefaultStyling,
      colors: effectiveWebsite?.sectionFormStyling
    })
    return effectiveWebsite?.sectionFormStyling || DEFAULT_STYLING.form
  }

  const getButtonStyling = () => {
    const useDefault = effectiveWebsite?.useDefaultStyling !== false
    if (useDefault) {
      console.log('🎨 [STYLING] Button: Using DEFAULT styling', {
        flag: effectiveWebsite?.useDefaultStyling,
        colors: DEFAULT_STYLING.form
      })
      return DEFAULT_STYLING.form
    }
    console.log('🎨 [STYLING] Button: Using CUSTOM styling', {
      flag: effectiveWebsite?.useDefaultStyling,
      colors: effectiveWebsite?.sectionFormStyling
    })
    return effectiveWebsite?.sectionFormStyling || DEFAULT_STYLING.form
  }

  const getCtaStyling = (section, isHovered) => {
    const useDefault = effectiveWebsite?.useDefaultStyling !== false
    if (useDefault) {
      console.log('🎨 [STYLING] CTA: Using DEFAULT styling', {
        flag: effectiveWebsite?.useDefaultStyling,
        isHovered: isHovered
      })
      return {
        backgroundColor: isHovered ? DEFAULT_STYLING.cta.hoverBackgroundColor : DEFAULT_STYLING.cta.backgroundColor,
        color: isHovered ? DEFAULT_STYLING.cta.hoverTextColor : DEFAULT_STYLING.cta.textColor
      }
    }

    const hasCustomBackground = !!section?.styling?.ctaButtonBackgroundColor
    const hasCustomTextColor = !!section?.styling?.ctaButtonTextColor

    console.log('🎨 [STYLING] CTA: Using CUSTOM styling', {
      flag: effectiveWebsite?.useDefaultStyling,
      hasCustomBackground: hasCustomBackground,
      hasCustomTextColor: hasCustomTextColor
    })

    let ctaStyle = {}
    if (hasCustomBackground) {
      ctaStyle.backgroundColor = isHovered ? (section.styling.ctaButtonHoverBackgroundColor || section.styling.ctaButtonBackgroundColor) : section.styling.ctaButtonBackgroundColor
    }
    if (hasCustomTextColor) {
      ctaStyle.color = isHovered ? (section.styling.ctaButtonHoverTextColor || section.styling.ctaButtonTextColor) : section.styling.ctaButtonTextColor
    }

    // Apply defaults if custom styling not fully set
    if (!hasCustomBackground) ctaStyle.backgroundColor = isHovered ? DEFAULT_STYLING.cta.hoverBackgroundColor : DEFAULT_STYLING.cta.backgroundColor
    if (!hasCustomTextColor) ctaStyle.color = isHovered ? DEFAULT_STYLING.cta.hoverTextColor : DEFAULT_STYLING.cta.textColor

    return ctaStyle
  }

  const normalizeLogoUrl = (logoUrl) => {
    if (!logoUrl || typeof logoUrl !== 'string') return null
    let normalized = logoUrl.trim().replace(/\s+/g, '')

    if (normalized.startsWith('//')) {
      normalized = `${window.location.protocol}${normalized}`
    }

    if (normalized.startsWith('data:') || normalized.startsWith('blob:')) {
      return normalized
    }

    if (!/^https?:\/\//i.test(normalized)) {
      const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
      const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : ''
      const origin = backendOrigin || fallbackOrigin
      const pathUrl = normalized.startsWith('/') ? normalized : `/${normalized}`
      return origin ? `${origin}${pathUrl}` : pathUrl
    }

    return normalized
  }

  let logoSrc = null
  if (effectiveWebsite?.logo?.url) {
    logoSrc = normalizeLogoUrl(effectiveWebsite.logo.url)
    console.log('[PublicWebsite] Website logo object (normalized):', { raw: effectiveWebsite.logo.url, normalized: logoSrc })
  }

  // Fallback: if website-level logo not present, check first section with a logo
  if (!logoSrc) {
    const sectionLogo = effectiveSections?.find(s => s.content?.logo?.url)?.content?.logo?.url
    if (sectionLogo) {
      logoSrc = normalizeLogoUrl(sectionLogo)
      console.log('[PublicWebsite] Section logo object (normalized fallback):', { raw: sectionLogo, normalized: logoSrc })
    }
  }

  if (logoSrc) {
    console.log('[PublicWebsite] Final logo URL:', logoSrc)
  } else {
    console.log('[PublicWebsite] No logo URL available, will use website name fallback')
  }

  // Reset logo error on URL update (important for mobile/hydration cases)
  useEffect(() => {
    setLogoLoadError(false)
    setLogoRetryCount(0)
  }, [logoSrc])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // For custom domains, show network error if prefetchedWebsiteError is set
  if (prefetchedWebsite === null && prefetchedWebsiteError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="mb-4">
            <div className="text-4xl font-bold text-red-500 mb-2">⚠️</div>
            <p className="text-xl font-semibold text-gray-900 mb-2">Network Error</p>
          </div>
          <p className="text-gray-600 mb-4">{prefetchedWebsiteError}</p>
          <p className="text-sm text-gray-500 mb-6">Please check your internet connection and try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (error && !prefetchedWebsite) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-red-600 font-semibold mb-2">Failed to load website</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen w-full overflow-x-hidden"
      style={{
        backgroundColor: isBodyHovered ? (getBodyStyling()?.hoverBackgroundColor || '#F3F4F6') : (getBodyStyling()?.backgroundColor || '#FFFFFF')
      }}
      onMouseEnter={() => setIsBodyHovered(true)}
      onMouseLeave={() => setIsBodyHovered(false)}
    >
      <Helmet>
        <title>{seoMeta.title}</title>
        <meta name="description" content={seoMeta.description} />
        {seoMeta.keywords && <meta name="keywords" content={seoMeta.keywords} />}
        <link rel="canonical" href={seoMeta.canonicalUrl} />
        <meta property="og:title" content={seoMeta.ogTitle} />
        <meta property="og:description" content={seoMeta.ogDescription} />
        <meta property="og:url" content={seoMeta.ogUrl} />
        {seoMeta.ogImage && <meta property="og:image" content={seoMeta.ogImage} />}
        <meta name="twitter:card" content={seoMeta.twitterCard} />
        <meta name="twitter:title" content={seoMeta.twitterTitle} />
        <meta name="twitter:description" content={seoMeta.twitterDescription} />
        {seoMeta.twitterImage && <meta name="twitter:image" content={seoMeta.twitterImage} />}
        <meta name="robots" content={seoMeta.robots} />
      </Helmet>
      <style>{WHATSAPP_BUTTON_OVERRIDE_CSS}</style>
      {customerVerificationNotice && (
        <div className={`mx-4 mt-4 rounded-lg border p-4 text-sm ${customerVerificationNotice.type === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          {customerVerificationNotice.message}
        </div>
      )}
      {customerAuthOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {customerAuthMode === 'login' ? (website?.customerAuth?.loginButtonText || 'Login') : customerAuthMode === 'register' ? (website?.customerAuth?.signupButtonText || 'Sign Up') : 'Forgot Password'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {customerAuthMode === 'login'
                    ? 'Sign in to access customer-only features.'
                    : customerAuthMode === 'register'
                      ? 'Create a customer account for this website.'
                      : 'Enter your email to receive a password reset link.'}
                </p>
              </div>
              <button onClick={closeCustomerAuthModal} className="text-gray-500 hover:text-gray-900">✕</button>
            </div>
            <form onSubmit={handleCustomerAuthSubmit} className="px-6 py-6 space-y-4">
              {customerAuthError && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {customerAuthError}
                </div>
              )}
              {(customerAuthMessage || customerVerificationNotice) && (
                <div className={`rounded-md border p-3 text-sm ${customerVerificationNotice?.type === 'error' || customerAuthError ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                  {customerVerificationNotice?.message || customerAuthMessage}
                </div>
              )}

              {customerAuthMode === 'register' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">First Name</span>
                    <input
                      name="firstName"
                      value={customerAuthForm.firstName}
                      onChange={handleCustomerAuthFormChange}
                      className="input-field w-full"
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Last Name</span>
                    <input
                      name="lastName"
                      value={customerAuthForm.lastName}
                      onChange={handleCustomerAuthFormChange}
                      className="input-field w-full"
                      required
                    />
                  </label>
                </div>
              )}

              {customerAuthMode === 'register' && (
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Phone</span>
                  <input
                    name="phone"
                    value={customerAuthForm.phone}
                    onChange={handleCustomerAuthFormChange}
                    className="input-field w-full"
                    placeholder="Optional"
                  />
                </label>
              )}

              <label className="block">
                <span className="text-sm font-medium text-gray-700">Email</span>
                <input
                  name="email"
                  type="email"
                  value={customerAuthForm.email}
                  onChange={handleCustomerAuthFormChange}
                  className="input-field w-full"
                  required
                />
              </label>

              {(customerAuthMode === 'login' || customerAuthMode === 'register') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Password</span>
                    <input
                      name="password"
                      type="password"
                      value={customerAuthForm.password}
                      onChange={handleCustomerAuthFormChange}
                      className="input-field w-full"
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Confirm Password</span>
                    <input
                      name="confirmPassword"
                      type="password"
                      value={customerAuthForm.confirmPassword}
                      onChange={handleCustomerAuthFormChange}
                      className="input-field w-full"
                      required={customerAuthMode === 'register'}
                      disabled={false}
                    />
                  </label>
                </div>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={customerAuthLoading}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-400"
                >
                  {customerAuthLoading ? 'Processing...' : customerAuthMode === 'login' ? 'Login' : customerAuthMode === 'register' ? 'Create Account' : 'Send Reset Link'}
                </button>
                {customerAuthMode === 'login' && website?.customerAuth?.enableForgotPassword && (
                  <button
                    type="button"
                    onClick={() => setCustomerAuthMode('forgot')}
                    className="w-full sm:w-auto rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Forgot password?
                  </button>
                )}
              </div>

              {customerAuthMode !== 'login' && (
                <div className="text-sm text-gray-600">
                  {customerAuthMode === 'register' ? (
                    <>
                      Already have an account?{' '}
                      <button type="button" onClick={() => setCustomerAuthMode('login')} className="text-primary-600 hover:text-primary-700 font-medium">Login</button>
                    </>
                  ) : (
                    <>
                      Remembered your password?{' '}
                      <button type="button" onClick={() => setCustomerAuthMode('login')} className="text-primary-600 hover:text-primary-700 font-medium">Back to login</button>
                    </>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      )}
      {verificationId && (
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className={`p-3 rounded-lg border ${verificationResult?.error ? 'border-red-300 bg-red-50' : 'border-emerald-300 bg-emerald-50'}`}>
            {verificationLoading ? (
              <p className="text-sm text-gray-700">Verifying card... please wait.</p>
            ) : verificationResult?.error ? (
              <p className="text-sm text-red-700">Verification failed: {verificationResult.error}</p>
            ) : verificationResult ? (
              <div>
                <p className="text-sm font-semibold text-gray-900">Card verification {verificationResult.isValid ? 'successful' : 'failed'}</p>
                <p className="text-sm text-gray-700">Card: {verificationResult.holderName || 'Unknown'}</p>
                <p className="text-sm text-gray-700">Status: <span className={verificationResult.isValid ? 'text-emerald-700' : 'text-red-700'}>{verificationResult.status}</span></p>
                <p className="text-sm text-gray-700">URL: <a href={verificationResult.verificationUrl} target="_blank" rel="noopener noreferrer" className="underline text-blue-600">View</a></p>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {currentView === 'cart' ? (
        <ShoppingCart
          website={website}
          sectionFormStyling={getFormStyling()}
          useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
          sectionType="cart"
          onContinueShopping={handleContinueShopping}
          onCheckout={() => setCurrentView('checkout')}
        />
      ) : currentView === 'checkout' ? (
        <Checkout
          website={website}
          sectionFormStyling={getFormStyling()}
          useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
          onBack={() => setCurrentView('cart')}
          customerProfile={customerAuthUser}
          customerAuthToken={customerAuthToken}
        />
      ) : (
        <>
          {/* Header mount point follows the persisted page order. */}
          {headerOrder === 0 && <div id="public-header-slot" className={`relative z-50 ${movedHeader ? 'pb-8 sm:pb-10 lg:pb-12' : ''}`} />}
          {website && headerSlotElement && createPortal((
            <header
              className={`${headerOrder > 0 ? 'sticky top-0' : headerPositionedInNextSection ? 'absolute top-0 left-0 right-0' : 'sticky top-0'} z-50 border-b border-gray-200 shadow-sm transition-colors`}
              style={{
                backgroundColor: isHeaderHovered ? (getHeaderStyling()?.hoverBackgroundColor || '#F3F4F6') : (getHeaderStyling()?.backgroundColor || '#ffffff'),
                color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit')
              }}
              onMouseEnter={() => setIsHeaderHovered(true)}
              onMouseLeave={() => setIsHeaderHovered(false)}
            >
              <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 ${movedHeader ? 'pb-5' : ''}`}>
                <div className="flex items-center justify-between">
                  {/* Logo or Website Name */}
                  <div
                    className="flex items-center gap-2 sm:gap-3 min-h-[32px] sm:min-h-[40px] flex-shrink-0 cursor-pointer"
                    onClick={() => {
                      const el = document.getElementById('section-0');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
                  >
                    {logoSrc && !logoLoadError ? (
                      <div className="relative flex-shrink-0">
                        <img
                          key={`${logoSrc}-${logoRetryCount}`}
                          src={logoSrc}
                          alt={website?.name || 'Logo'}
                          crossOrigin="anonymous"
                          loading="lazy"
                          className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto max-w-[64px] sm:max-w-[80px] md:max-w-[120px] lg:max-w-[160px] object-contain"
                          style={{
                            minHeight: '24px',
                            maxHeight: '56px',
                            width: 'auto',
                            height: 'auto',
                            display: 'block'
                          }}
                          onLoad={(e) => {
                            console.log('[PublicWebsite] Logo loaded successfully:', logoSrc, 'Size:', e.target.naturalWidth, 'x', e.target.naturalHeight)
                            setLogoLoadError(false)
                          }}
                          onError={(e) => {
                            console.error('[PublicWebsite] Logo failed to load on mobile/desktop:', logoSrc, e)

                            if (logoRetryCount < 2) {
                              setLogoRetryCount((prev) => prev + 1)
                              const retryUrl = `${logoSrc}${logoSrc.includes('?') ? '&' : '?'}_r=${Date.now()}`
                              e.target.src = normalizeLogoUrl(retryUrl)
                              console.log('[PublicWebsite] Retrying logo load with cache-busted URL:', e.target.src)
                              return
                            }

                            setLogoLoadError(true)
                            console.warn('[PublicWebsite] Logo permanently failed to load after retries, showing website name fallback')
                          }}
                        />
                      </div>
                    ) : (
                      <div className="text-sm sm:text-base md:text-lg lg:text-xl font-bold truncate max-w-[200px] sm:max-w-none" style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}>
                        {website?.name || ''}
                      </div>
                    )}
                  </div>

                  {/* Desktop Navigation */}
                  {sections && sections.length > 0 && (
                    <nav className={`${movedHeader ? 'flex' : 'hidden md:flex'} min-w-0 max-w-[60vw] items-center gap-2 overflow-x-auto overscroll-x-contain whitespace-nowrap [scrollbar-width:thin]`}>
                      {sections.filter(s => !s.disabled).map((s, i) => {
                        const title = s.titleAvailable !== false && s.title?.toString().trim()
                          ? s.title.toString().trim()
                          : s.content?.headingAvailable !== false && s.content?.heading?.toString().trim()
                            ? s.content.heading.toString().trim()
                            : ''
                        if (!title) return null
                        const sectionIndex = sections.findIndex((candidate) => (
                          (s._id && candidate._id === s._id) || candidate === s
                        ))
                        return (
                          <a
                            key={s._id || i}
                            href={`#section-${sectionIndex}`}
                            onClick={(e) => {
                              e.preventDefault()
                              const el = document.getElementById(`section-${sectionIndex}`)
                              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                            }}
                            className="text-sm px-3 py-2 rounded-md flex-shrink-0"
                            style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
                          >
                            {title}
                          </a>
                        )
                      })}
                    </nav>
                  )}

                  {/* Mobile Menu Button and Actions */}
                  <div className="flex items-center gap-4">
                    {!prefetchedWebsite && (
                      <button
                        onClick={() => setReportModalOpen(true)}
                        className="p-2 hover:bg-gray-100 rounded"
                        title="Report this website"
                        style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
                      >
                        <FiFlag className="w-6 h-6" />
                      </button>
                    )}
                    {customerAuthEnabled && !customerAuthUser && (
                      <>
                        <button
                          onClick={() => openCustomerAuthModal('login')}
                          className="hidden md:inline-flex px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
                          style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
                        >
                          {website?.customerAuth?.loginButtonText || 'Login'}
                        </button>
                        <button
                          onClick={() => openCustomerAuthModal('register')}
                          className="hidden md:inline-flex px-3 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
                        >
                          {website?.customerAuth?.signupButtonText || 'Sign Up'}
                        </button>
                      </>
                    )}
                    {customerAuthEnabled && customerAuthUser && (
                      <>
                        <button
                          onClick={() => {
                            setCustomerActivityPage(1)
                            setCurrentView('account')
                          }}
                          className="hidden md:inline-flex px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
                          style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
                        >
                          My Account
                        </button>
                        <button
                          onClick={handleCustomerAuthLogout}
                          className="hidden md:inline-flex px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
                          style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
                        >
                          Logout
                        </button>
                      </>
                    )}
                    {hasCartFeature && (
                      <button
                        onClick={() => setCurrentView(currentView === 'cart' ? 'website' : 'cart')}
                        className="relative p-2 hover:bg-gray-100 rounded"
                        style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
                      >
                        <FiShoppingCart className="w-6 h-6" />
                        {itemCount > 0 && (
                          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {itemCount}
                          </span>
                        )}
                      </button>
                    )}
                    {!movedHeader && sections && sections.length > 0 && (
                      <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="md:hidden p-2 hover:bg-gray-100 rounded"
                        style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
                      >
                        {mobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
                      </button>
                    )}
                  </div>
                </div>

                {/* Mobile Navigation Menu */}
                {!movedHeader && mobileMenuOpen && sections?.length > 0 && (
                  <div className={`${movedHeader ? 'mt-2' : 'md:hidden mt-4'} pb-4 border-t border-gray-200`}>
                    <nav className="flex gap-2 overflow-x-auto overscroll-x-contain whitespace-nowrap pb-2 [scrollbar-width:thin]">
                      {sections.filter(s => !s.disabled).map((s, i) => {
                        const title = s.titleAvailable !== false && s.title?.toString().trim()
                          ? s.title.toString().trim()
                          : s.content?.headingAvailable !== false && s.content?.heading?.toString().trim()
                            ? s.content.heading.toString().trim()
                            : ''
                        if (!title) return null
                        const sectionIndex = sections.findIndex((candidate) => (
                          (s._id && candidate._id === s._id) || candidate === s
                        ))
                        return (
                          <a
                            key={s._id || i}
                            href={`#section-${sectionIndex}`}
                            onClick={(e) => {
                              e.preventDefault()
                              setMobileMenuOpen(false)
                              const el = document.getElementById(`section-${sectionIndex}`)
                              if (el) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                window.history.replaceState(null, '', `#section-${sectionIndex}`)
                              }
                            }}
                            className="text-sm text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-100 flex-shrink-0"
                          >
                            {title}
                          </a>
                        )
                      })}
                    </nav>
                  </div>
                )}
                {mobileMenuOpen && customerAuthEnabled && !customerAuthUser && (
                  <div className="md:hidden mt-4 pb-4 border-t border-gray-200 space-y-2">
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false)
                        openCustomerAuthModal('login')
                      }}
                      className="w-full text-left px-3 py-2 rounded-md bg-white border border-gray-300 text-sm font-medium hover:bg-gray-50"
                    >
                      {website?.customerAuth?.loginButtonText || 'Login'}
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false)
                        openCustomerAuthModal('register')
                      }}
                      className="w-full text-left px-3 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                    >
                      {website?.customerAuth?.signupButtonText || 'Sign Up'}
                    </button>
                  </div>
                )}
                {mobileMenuOpen && customerAuthEnabled && customerAuthUser && (
                  <div className="md:hidden mt-4 pb-4 border-t border-gray-200 space-y-2">
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false)
                        setCurrentView('account')
                      }}
                      className="w-full text-left px-3 py-2 rounded-md bg-white border border-gray-300 text-sm font-medium hover:bg-gray-50"
                    >
                      My Account
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false)
                        handleCustomerAuthLogout()
                      }}
                      className="w-full text-left px-3 py-2 rounded-md bg-white border border-gray-300 text-sm font-medium hover:bg-gray-50"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </header>
          ), headerSlotElement)}

      <main className="whitespace-pre-wrap break-words">
        {ticketDetailError && (
          <div className="p-6 text-center text-red-600 bg-red-100 border border-red-200 rounded mb-4">
            {ticketDetailError}
          </div>
        )}
        {currentView === 'account' && customerAuthUser ? (
          <section className="max-w-6xl mx-auto p-6">
            <div className="bg-white border rounded-lg shadow-sm p-6 space-y-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0">
                  <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">My Account</h2>
                  <p className="text-sm text-gray-600 max-w-2xl">Welcome back, {customerAuthUser.firstName || customerAuthUser.email}.</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setCurrentView('website')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Back to website
                  </button>
                  <button
                    onClick={handleCustomerAuthLogout}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Profile</h3>
                  <div className="text-sm text-gray-700 space-y-2">
                    <div><span className="font-semibold">Name:</span> {customerAuthUser.firstName} {customerAuthUser.lastName}</div>
                    <div><span className="font-semibold">Email:</span> {customerAuthUser.email}</div>
                    {customerAuthUser.phone && <div><span className="font-semibold">Phone:</span> {customerAuthUser.phone}</div>}
                    <div><span className="font-semibold">Status:</span> {customerAuthUser.status || 'active'}</div>
                    <div><span className="font-semibold">Email verified:</span> {customerAuthUser.isEmailVerified ? 'Yes' : 'No'}</div>
                  </div>

                  {!customerAuthUser.isEmailVerified && (
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      <p className="font-semibold">Your account is awaiting email verification.</p>
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendingVerification}
                        className="mt-2 inline-flex items-center rounded bg-amber-600 px-3 py-2 text-white hover:bg-amber-700 disabled:bg-gray-400"
                      >
                        {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="md:col-span-2 rounded-lg border border-gray-200 p-4 bg-white">
                  <div className="flex flex-wrap gap-2 mb-6 items-center justify-between">
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: 'profile', label: 'Profile' },
                        { key: 'messages', label: 'Messages' },
                        { key: 'activity', label: 'Activity' }
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setAccountSection(tab.key)}
                          className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${accountSection === tab.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    {accountSection === 'messages' && website && customerAuthToken && customerAuthUser && (
                      <CustomerNotificationCenter 
                        website={website} 
                        customerAuthToken={customerAuthToken} 
                        customerAuthUser={customerAuthUser} 
                        onOpenConversation={handleOpenConversationFromNotification}
                      />
                    )}
                  </div>

                  {accountSection === 'profile' && (
                    <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Update profile</h3>
                          <p className="text-sm text-gray-500">Keep your account details up to date for future purchases and communications.</p>
                        </div>
                      </div>

                      <form onSubmit={handleCustomerProfileSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="text-sm text-gray-700">
                          <span className="mb-1 block font-medium">First name</span>
                          <input name="firstName" value={customerProfileForm.firstName} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <label className="text-sm text-gray-700">
                          <span className="mb-1 block font-medium">Last name</span>
                          <input name="lastName" value={customerProfileForm.lastName} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <label className="text-sm text-gray-700">
                          <span className="mb-1 block font-medium">Email</span>
                          <input name="email" type="email" value={customerProfileForm.email} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <label className="text-sm text-gray-700">
                          <span className="mb-1 block font-medium">Phone</span>
                          <input name="phone" value={customerProfileForm.phone} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <label className="text-sm text-gray-700 md:col-span-2">
                          <span className="mb-1 block font-medium">Shipping address</span>
                          <input name="shippingAddress" value={customerProfileForm.shippingAddress} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <label className="text-sm text-gray-700">
                          <span className="mb-1 block font-medium">Shipping city</span>
                          <input name="shippingCity" value={customerProfileForm.shippingCity} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <label className="text-sm text-gray-700">
                          <span className="mb-1 block font-medium">Shipping state</span>
                          <input name="shippingState" value={customerProfileForm.shippingState} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <label className="text-sm text-gray-700">
                          <span className="mb-1 block font-medium">Shipping ZIP</span>
                          <input name="shippingZip" value={customerProfileForm.shippingZip} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <label className="text-sm text-gray-700 md:col-span-2">
                          <span className="mb-1 block font-medium">Billing address</span>
                          <input name="billingAddress" value={customerProfileForm.billingAddress} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <label className="text-sm text-gray-700">
                          <span className="mb-1 block font-medium">Billing city</span>
                          <input name="billingCity" value={customerProfileForm.billingCity} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <label className="text-sm text-gray-700">
                          <span className="mb-1 block font-medium">Billing state</span>
                          <input name="billingState" value={customerProfileForm.billingState} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <label className="text-sm text-gray-700">
                          <span className="mb-1 block font-medium">Billing ZIP</span>
                          <input name="billingZip" value={customerProfileForm.billingZip} onChange={handleCustomerProfileChange} className="input-field w-full" />
                        </label>
                        <div className="md:col-span-2 flex justify-end">
                          <button type="submit" disabled={profileSaving} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-400">
                            {profileSaving ? 'Saving...' : 'Save profile'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {accountSection === 'messages' && (
                    <div className="mb-6">
                      <CustomerContactChats 
                        website={website} 
                        customerAuthToken={customerAuthToken} 
                        customerAuthUser={customerAuthUser}
                        selectedConversationId={selectedConversationIdFromNotification}
                        onSelectConversation={setSelectedConversationIdFromNotification}
                      />
                    </div>
                  )}

                  {accountSection === 'activity' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Recent activity</h3>
                          <p className="text-sm text-gray-500">Your latest account actions and history.</p>
                        </div>
                        <button
                          onClick={() => fetchCustomerActivity(1)}
                          className="text-sm text-primary-600 hover:text-primary-700"
                        >
                          Refresh
                        </button>
                      </div>

                      {customerActivityLoading ? (
                        <div className="py-12 text-center text-gray-600">Loading activity...</div>
                      ) : customerActivityError ? (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{customerActivityError}</div>
                      ) : customerActivity.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
                          No activity has been recorded for your account yet.
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {customerActivity.map((item) => (
                            <div key={item._id} className="rounded-lg border border-gray-200 p-4 bg-gray-50">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div>
                                  <p className="font-semibold text-gray-900">{item.activityType}</p>
                                  <p className="text-sm text-gray-600">{item.description}</p>
                                </div>
                                <div className="text-sm text-gray-500">
                                  {new Date(item.createdAt).toLocaleString()}
                                </div>
                              </div>
                              {item.status && <div className="mt-2 text-xs uppercase tracking-wide text-gray-500">Status: {item.status}</div>}
                              {item.metadata && Object.keys(item.metadata).length > 0 && (
                                <div className="mt-3 text-xs text-gray-500">
                                  {Object.entries(item.metadata).map(([key, value]) => (
                                    <span key={key} className="mr-3">{key}: {String(value)}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}

                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <span className="text-sm text-gray-600">Showing {customerActivity.length} of {customerActivityTotal} activities</span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => fetchCustomerActivity(Math.max(1, customerActivityPage - 1))}
                                disabled={customerActivityPage <= 1}
                                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
                              >
                                Previous
                              </button>
                              <button
                                onClick={() => fetchCustomerActivity(customerActivityPage + 1)}
                                disabled={(customerActivityPage * CUSTOMER_ACTIVITY_LIMIT) >= customerActivityTotal}
                                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        ) : ticketDetail ? (
          <section className="max-w-6xl mx-auto p-6">
            <div className="bg-white border rounded-lg shadow-sm p-6">
              <div className="flex flex-wrap gap-6">
                <div className="w-full md:w-1/3">
                  {ticketDetail.eventBanner?.url ? (
                    <img src={ticketDetail.eventBanner.url} alt={ticketDetail.eventName} className="w-full h-56 object-cover rounded" />
                  ) : (
                    <div className="w-full h-56 bg-gray-100 rounded flex items-center justify-center">No banner</div>
                  )}
                </div>

                <div className="w-full md:w-2/3">
                  <h2 className="text-3xl font-bold">{ticketDetail.eventName}</h2>
                  <p className="text-lg text-gray-500 mb-2">{ticketDetail.eventCategory} • {ticketDetail.ticketType}</p>
                  <p className="text-sm text-gray-600 mb-3">{ticketDetail.venueName} • {ticketDetail.city}, {ticketDetail.country}</p>
                  <p className="mb-3">{ticketDetail.eventDescription || ticketDetail.shortDescription}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-sm text-gray-700 mb-4">
                    <div><strong>Date:</strong> {(() => {
                      if (ticketDetail.eventDate) {
                        const [year, month, day] = ticketDetail.eventDate.split('-');
                        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                        return `${monthNames[parseInt(month)-1]} ${parseInt(day)}, ${year}`;
                      }
                      return new Date(ticketDetail.eventDate).toLocaleDateString();
                    })()}</div>
                    <div><strong>Time:</strong> {ticketDetail.startTime} - {ticketDetail.endTime}</div>
                    <div><strong>Doors Open:</strong> {ticketDetail.doorsOpenTime || 'N/A'}</div>
                    <div><strong>Price:</strong> {ticketDetail.currency} {ticketDetail.price}</div>
                    <div><strong>Remaining:</strong> {ticketDetail.totalQuantity - ticketDetail.soldQuantity}</div>
                    <div><strong>Status:</strong> {ticketDetail.salesOpen ? 'Open' : 'Closed'}</div>
                  </div>

                  {ticketDetail.features?.length > 0 && (
                    <div className="mb-4">
                      <h3 className="font-semibold">Features</h3>
                      <ul className="list-disc ml-5 text-sm text-gray-700">
                        {ticketDetail.features.map((feature, i) => <li key={i}>{feature}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setShowBuyerForm(true)} 
                      disabled={!ticketDetail?.salesOpen}
                      className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Buy Ticket
                    </button>
                    <button onClick={() => { setTicketDetail(null); setTicketDetailError(null); }} className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold">Back</button>
                  </div>
                </div>
              </div>

              {ticketDetail.ticketLink && (
                <div className="mt-4">
                  <a href={ticketDetail.ticketLink} className="text-blue-600">Event Link</a>
                </div>
              )}
            </div>
          </section>
        ) : (
          (() => {
            if (sections && sections.length > 0) {
              return sections.map((section, idx) => {
              if (section.disabled === true || section.status === 'disabled') return null

              const sectionType = normalizeSectionType(section.type || section.sectionType || '')
              const sectionTitle = section.titleAvailable !== false && section.title?.toString().trim()
                ? section.title.toString().trim()
                : ''
              const sectionHeading = section.content?.headingAvailable !== false && section.content?.heading?.toString().trim()
                ? section.content.heading.toString().trim()
                : ''
              const specialMedia = section.content?.specialMedia || []
              const left = section.content?.leftSidebar
              const middle = section.content?.middleBar
              const right = section.content?.rightSidebar
              const hasLeft = left && ((left.media && left.media.length > 0) || (left.contentText && left.contentText.trim()))
              const hasMiddle = middle && ((middle.media && middle.media.length > 0) || (middle.contentText && middle.contentText.trim()))
              const hasRight = right && ((right.media && right.media.length > 0) || (right.contentText && right.contentText.trim()))
              const hasSpecialMedia = specialMedia.length > 0
              const hasTopHeader = hasSpecialMedia || hasLeft || hasMiddle || hasRight
              const firstSectionHeaderOffset = headerPositionedInNextSection && idx === 0 ? 'clamp(72px, 10vw, 88px)' : undefined

              // Keep hero rendering unchanged
              if (sectionType === 'hero' || section.isHeroSection) {
                const heroImages = section.content?.heroImages || []
                const heroImage = section.content?.heroImage?.url || section.content?.backgroundImage?.url
                const ctaText = (section.content?.ctaText || section.ctaText || section.cta?.text || '').toString().trim()
                const ctaLink = (section.content?.ctaLink || section.ctaLink || section.cta?.link || '').toString().trim()
                const ctaBackgroundColor = section.styling?.ctaButtonBackgroundColor
                const ctaTextColor = section.styling?.ctaButtonTextColor
                const heroButtonLink = Array.isArray(section.content?.heroButtons)
                  ? section.content.heroButtons.find((btn) => btn?.link?.toString().trim())?.link?.toString().trim()
                  : ''
                const heroClickLink = ctaLink || heroButtonLink

                // Use carousel if multiple images exist, otherwise use single image
                const hasNonImageHeroMedia = heroImages.some((item) => item?.type && item.type !== 'image')
                if (heroImages.length > 1 || hasNonImageHeroMedia) {
                  return (
                    <HeroCarousel
                      key={section._id}
                      images={heroImages}
                      settings={section.content?.carouselSettings}
                      heading={sectionHeading}
                      headingPosition={section.content?.headingPosition || 'left'}
                      subheading={section.content?.subheading}
                      subheadingPosition={section.content?.subheadingPosition || 'left'}
                      subheadingTextStyle={section.content?.subheadingTextStyle || 'default'}
                      heroButtons={section.content?.heroButtons}
                      overlay={section.content?.heroOverlay}
                      ctaText={ctaText}
                      ctaLink={ctaLink}
                      ctaBackgroundColor={ctaBackgroundColor}
                      ctaTextColor={ctaTextColor}
                      displayMode={section.content?.heroDisplayMode || 'contain'}
                    />
                  )
                }
                
                // Fallback to single image or first carousel image
                const singleHeroMedia = heroImages.length === 1 ? heroImages[0] : null
                const displayImage = heroImages.length === 1 
                  ? singleHeroMedia?.type === 'video' ? '' : singleHeroMedia?.url
                  : heroImage
                
                const navigateToLink = (link) => {
                  if (!link) return
                  const trimmedLink = link.toString().trim()
                  if (!trimmedLink) return

                  if (trimmedLink.startsWith('#')) {
                    window.location.hash = trimmedLink
                    return
                  }

                  try {
                    const url = new URL(trimmedLink, window.location.href)
                    if (url.origin === window.location.origin) {
                      window.location.href = url.href
                    } else {
                      window.open(url.href, '_blank', 'noopener,noreferrer')
                    }
                  } catch (error) {
                    window.location.href = trimmedLink
                  }
                }

                return (
                  <section
                  key={section._id}
                  id={`section-${idx}`}
                  className={`relative w-screen left-1/2 -translate-x-1/2 max-w-screen ${headerOrder === idx && headerOrder > 0 ? 'overflow-visible' : 'overflow-hidden'} scroll-mt-20 ${heroClickLink ? 'cursor-pointer' : ''}`}
                  style={{ paddingTop: firstSectionHeaderOffset }}
                  onClick={(e) => {
                    if (e.target.closest('[role="dialog"]')) {
                      e.stopPropagation()
                      return
                    }
                    navigateToLink(heroClickLink)
                  }}
                  role={heroClickLink ? 'link' : undefined}
                  tabIndex={heroClickLink ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (heroClickLink && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault()
                      navigateToLink(heroClickLink)
                    }
                  }}
                >
                  {headerOrder > 0 && headerOrder === idx && <div id="public-header-slot" className="relative z-50 pb-8 sm:pb-10 lg:pb-12" />}
                  <div className="relative w-full overflow-hidden" style={{ zIndex: 0 }}>
                    {singleHeroMedia?.type === 'video' ? (
                      <VideoUrlFrame video={singleHeroMedia} className="min-h-[220px] w-full rounded-none" />
                    ) : displayImage && (() => {
                      const mode = (section.content && section.content.heroDisplayMode) ? section.content.heroDisplayMode : 'contain'
                      const isCoverMode = mode === 'cover'

                      if (isCoverMode) {
                        return (
                          <div className="absolute inset-0 w-full h-full overflow-hidden">
                            <img
                              src={displayImage}
                              alt="Hero background"
                              className="absolute inset-0 w-full h-full object-cover object-center"
                              onError={(e) => console.error('[PublicWebsiteViewer] Hero image failed to load:', displayImage.substring(0, 100) + '...')}
                            />
                          </div>
                        )
                      }

                      return (
                        <img
                          src={displayImage}
                          alt="Hero background"
                          className="w-full h-auto object-contain object-center"
                          style={{ maxWidth: '100%' }}
                          onLoad={(e) => handleHeroImageLoad(section._id, e.target.naturalWidth, e.target.naturalHeight)}
                          onError={(e) => console.error('[PublicWebsiteViewer] Hero image failed to load:', displayImage.substring(0, 100) + '...')}
                        />
                      )
                    })()}

                    {section.content?.heroOverlay?.enabled && (
                      <div
                        className="absolute inset-0 z-10"
                        style={{
                          backgroundColor: section.content.heroOverlay.color || '#000000',
                          opacity: section.content.heroOverlay.opacity || 0.3
                        }}
                      />
                    )}

                    <div className="absolute inset-0 z-20 flex items-center justify-center text-center text-white px-4 py-10 sm:px-6 sm:py-12 lg:px-8 pointer-events-none">
                      <div className="w-full max-w-5xl">
                        {sectionHeading && (
                          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 drop-shadow-lg leading-tight" style={{ textAlign: section.content?.headingPosition || 'left' }}>
                            {sectionHeading}
                          </h1>
                        )}
                        {section.content?.subheading && (
                          <p className={`text-base sm:text-lg md:text-xl mb-8 drop-shadow-md leading-relaxed max-w-3xl mx-auto ${section.content?.subheadingTextStyle === 'bold' ? 'font-bold' : section.content?.subheadingTextStyle === 'slightly-bold' ? 'font-medium' : ''}`} style={{ textAlign: section.content?.subheadingPosition || 'left' }}>
                            {section.content.subheading}
                          </p>
                        )}

                        <div className="flex gap-4 justify-center flex-wrap">
                          {section.content?.heroButtons && section.content.heroButtons.length > 0 && (
                            section.content.heroButtons.map((btn, btnIndex) => {
                              const hasCustomBackground = section.styling?.ctaButtonBackgroundColor
                              const hasCustomTextColor = section.styling?.ctaButtonTextColor
                              const isBtnHovered = hoveredCtaId === `${section._id}-btn-${btnIndex}`
                              
                              let buttonStyle = {}
                              if (hasCustomBackground) buttonStyle.backgroundColor = isBtnHovered ? (section.styling.ctaButtonHoverBackgroundColor || section.styling.ctaButtonBackgroundColor) : section.styling.ctaButtonBackgroundColor
                              if (hasCustomTextColor) buttonStyle.color = isBtnHovered ? (section.styling.ctaButtonHoverTextColor || section.styling.ctaButtonTextColor) : section.styling.ctaButtonTextColor
                              
                              const baseClasses = "px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
                              const defaultStyles = {
                                primary: {
                                  bg: "bg-white",
                                  text: "text-gray-900",
                                  hover: "hover:bg-gray-100"
                                },
                                secondary: {
                                  bg: "bg-gray-600",
                                  text: "text-white",
                                  hover: "hover:bg-gray-700"
                                },
                                outline: {
                                  bg: "border-2 border-white",
                                  text: "text-white",
                                  hover: "hover:bg-white hover:text-gray-900"
                                }
                              }
                              const buttonType = btn.style || 'primary'
                              const styleDefaults = defaultStyles[buttonType] || defaultStyles.primary

                              const buttonClassParts = [baseClasses]
                              if (!hasCustomBackground) buttonClassParts.push(styleDefaults.bg)
                              if (!hasCustomTextColor) buttonClassParts.push(styleDefaults.text)
                              if (!hasCustomBackground) buttonClassParts.push(styleDefaults.hover)

                              const buttonClassName = buttonClassParts.join(' ')
                              
                              return (
                                <a
                                  key={btn.id}
                                  href={btn.link || '#'}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`${buttonClassName.trim()} transition-colors`}
                                  style={Object.keys(buttonStyle).length ? buttonStyle : undefined}
                                  onMouseEnter={() => setHoveredCtaId(`${section._id}-btn-${btnIndex}`)}
                                  onMouseLeave={() => setHoveredCtaId(null)}
                                >
                                  {btn.text}
                                </a>
                              )
                            })
                          )}
                          {ctaText && ctaLink && (
                            (() => {
                              const hasCustomBackground = section.styling?.ctaButtonBackgroundColor
                              const hasCustomTextColor = section.styling?.ctaButtonTextColor
                              const isCtaHovered = hoveredCtaId === `${section._id}-cta`
                              
                              let ctaStyle = {}
                              if (hasCustomBackground) ctaStyle.backgroundColor = isCtaHovered ? (section.styling.ctaButtonHoverBackgroundColor || section.styling.ctaButtonBackgroundColor) : section.styling.ctaButtonBackgroundColor
                              if (hasCustomTextColor) ctaStyle.color = isCtaHovered ? (section.styling.ctaButtonHoverTextColor || section.styling.ctaButtonTextColor) : section.styling.ctaButtonTextColor
                              
                              const baseClasses = "px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
                              const defaultBackgroundClasses = hasCustomBackground ? "" : " bg-white"
                              const defaultTextClasses = hasCustomTextColor ? "" : " text-gray-900 hover:bg-gray-100"
                              const ctaClassName = `${baseClasses}${defaultBackgroundClasses}${defaultTextClasses}`
                              
                              return (
                                <a
                                  href={ctaLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className={`${ctaClassName.trim()} transition-colors`}
                                  style={Object.keys(ctaStyle).length ? ctaStyle : undefined}
                                  onMouseEnter={() => setHoveredCtaId(`${section._id}-cta`)}
                                  onMouseLeave={() => setHoveredCtaId(null)}
                                >
                                  {ctaText}
                                </a>
                              )
                            })()
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              )
            }

            // For non-hero sections: build inner content first, then wrap with media/sidebars
            let innerContent = null

            if (sectionType === 'donation') {
              innerContent = (
                <PublicDonationSection
                  website={website}
                  section={section}
                  sectionFormStyling={getFormStyling()}
                  useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
                />
              )
            } else if (sectionType === 'products') {
              innerContent = (
                <>
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-12">
                        {sectionTitle && <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
                      )}
                    </div>
                  )}

                  <PublicProductsSection 
                    websiteSlug={slug} 
                    website={website} 
                    section={section}
                    sectionFormStyling={getFormStyling()}
                    useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
                    sectionType="products"
                    onAddToCart={() => {
                      console.log('[PublicWebsiteViewer] Product added to cart')
                      setCurrentView('cart')
                    }}
                    onViewProduct={(product) => handleViewItem(product)}
                  />
                </>
              )
            } else if (sectionType === 'tickets') {
              innerContent = (
                <>
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-12">
                        {sectionTitle && <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
                      )}
                    </div>
                  )}

                  <PublicTicketsSection
                    websiteSlug={slug}
                    website={website}
                    section={section}
                    sectionFormStyling={getFormStyling()}
                    useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
                    sectionType="tickets"
                    onAddToCart={() => {
                      console.log('[PublicWebsiteViewer] Ticket added to cart')
                      setCurrentView('cart')
                    }}
                  />
                </>
              )
            } else if (sectionType === 'contact') {
              innerContent = (
                <>
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-12 text-center">
                        {sectionTitle && <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">{section.description}</p>
                      )}
                    </div>
                  )}

                  <ContactUsForm
                    website={website}
                    sectionFormStyling={getFormStyling()}
                    useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
                    sectionType="contact"
                    customerProfile={customerAuthUser}
                    customerAuthToken={customerAuthToken}
                  />
                </>
              )
            } else if (sectionType === 'reviews' || section.type === 'reviews') {
              innerContent = (<ReviewsSection website={website} section={section} sectionFormStyling={getFormStyling()} useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false} customerProfile={customerAuthUser} customerAuthToken={customerAuthToken} />)
            } else if (sectionType === 'fixtures') {
              innerContent = (
                <>
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-12">
                        {sectionTitle && <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
                      )}
                    </div>
                  )}
                  <PublicFixturesSection website={website} section={section} sectionFormStyling={getFormStyling()} useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false} sectionType="fixtures" />
                </>
              )
            } else if (sectionType === 'points-table') {
              innerContent = (
                <>
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-12">
                        {sectionTitle && <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
                      )}
                    </div>
                  )}
                  <PublicPointsTable website={website} section={section} sectionFormStyling={getFormStyling()} useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false} sectionType="points-table" />
                </>
              )
            } else if (sectionType === 'resume') {
              innerContent = (
                <>
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-12">
                        {sectionTitle && <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
                      )}
                    </div>
                  )}
                  <PublicResumeUpload website={website} section={section} sectionFormStyling={getFormStyling()} useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false} sectionType="resume" />
                </>
              )
            } else if (sectionType === 'team' || sectionType === 'team-members') {
              const teamMembers = section.content?.teamMembers || []
              const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
              const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, sectionType, section?._id)
              const formStyling = getFormStyling()
              const formStyle = isFormStyling ? {
                backgroundColor: formStyling.backgroundColor,
                color: formStyling.textColor
              } : undefined
              const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
              const hoverFormStyle = isFormStyling ? {
                backgroundColor: formStyling.hoverBackgroundColor || formStyling.backgroundColor,
                color: formStyling.hoverTextColor || formStyling.textColor
              } : undefined
              console.log('[TEAM-SECTION] Styling decision:', { useDefaultFormStyling, isFormStyling, colors: formStyling })
              console.log('PublicWebsiteViewer rendering team section:', section._id, section.title, 'teamMembers:', teamMembers.length, teamMembers.map(m => ({ name: m.name, position: m.position, hasImage: !!m.profileImage?.url })))
              innerContent = (
                <div style={formStyle}>
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-12 text-center">
                        {sectionTitle && <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={textStyle}>{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-base sm:text-lg max-w-2xl mx-auto" style={textStyle}>{section.description}</p>
                      )}
                    </div>
                  )}

                  {teamMembers.length === 0 ? (
                    <div className="rounded-lg p-8 text-center transition-colors" style={isFormStyling ? { backgroundColor: formStyling.backgroundColor, color: formStyling.textColor } : undefined}>
                      <p style={textStyle}>No team members to display.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {teamMembers.map((member) => (
                        <div
                          key={member.id}
                          className="border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300"
                          style={isFormStyling ? {
                            backgroundColor: formStyling.backgroundColor,
                            color: formStyling.textColor
                          } : undefined}
                          onMouseEnter={(e) => {
                            if (isFormStyling && hoverFormStyle) {
                              e.currentTarget.style.backgroundColor = hoverFormStyle.backgroundColor
                              e.currentTarget.style.color = hoverFormStyle.color
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (isFormStyling) {
                              e.currentTarget.style.backgroundColor = formStyling.backgroundColor
                              e.currentTarget.style.color = formStyling.textColor
                            }
                          }}
                        >
                          {/* Profile Image Container */}
                          {member.profileImage?.url ? (
                            <div className="w-full h-64 overflow-hidden bg-gray-100">
                              <img
                                src={member.profileImage.url}
                                alt={member.name || 'Team member'}
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          ) : (
                            <div className="w-full h-64 bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
                              <div className="text-white text-4xl font-bold opacity-50">👤</div>
                            </div>
                          )}

                          {/* Member Info */}
                          <div className="p-4">
                            <h3 className="text-lg font-semibold mb-1" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
                              {member.name || 'Team Member'}
                            </h3>

                            {member.position && (
                              <p className="text-sm font-medium mb-3" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
                                {member.position}
                              </p>
                            )}

                            {member.bio && (
                              <p className="text-sm line-clamp-3" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
                                {member.bio}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            } else if (sectionType === 'audio') {
              const audioItems = section.content?.audio || []
              
              // Log audio section loading
              console.log('[AUDIO-SECTION-LOAD] Audio section loaded:', {
                sectionId: section._id,
                sectionTitle: section.title,
                audioCount: audioItems.length,
                timestamp: new Date().toISOString(),
                audioFiles: audioItems.map((a, idx) => ({
                  index: idx,
                  id: a.id,
                  name: a.name,
                  filename: a.audioFile?.filename,
                  hasSrc: !!a.audioFile?.url,
                  srcLength: a.audioFile?.url?.length || 0,
                  srcPreview: a.audioFile?.url ? a.audioFile.url.substring(0, 100) : 'no src',
                  hasBanner: !!a.banner?.url
                }))
              })
              
              const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
              const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, 'audio', section?._id)
              const formStyling = getFormStyling()
              const formStyle = isFormStyling ? {
                backgroundColor: formStyling.backgroundColor,
                color: formStyling.textColor
              } : undefined
              const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
              const hoverFormStyle = isFormStyling ? {
                backgroundColor: formStyling.hoverBackgroundColor || formStyling.backgroundColor,
                color: formStyling.hoverTextColor || formStyling.textColor
              } : undefined
              
              // Calculate pagination
              const totalAudioPages = Math.ceil(audioItems.length / 10)
              const audioCurrentPage = itemPagination[section._id] || 1
              const audioStartIdx = (audioCurrentPage - 1) * 10
              const audioEndIdx = audioStartIdx + 10
              const paginatedAudioItems = audioItems.slice(audioStartIdx, audioEndIdx)
              
              innerContent = (
                <div style={formStyle}>
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-12 text-center">
                        {sectionTitle && <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={textStyle}>{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-base sm:text-lg max-w-2xl mx-auto" style={textStyle}>{section.description}</p>
                      )}
                    </div>
                  )}

                  {audioItems.length === 0 ? (
                    <div className="rounded-lg p-8 text-center transition-colors" style={isFormStyling ? { backgroundColor: formStyling.backgroundColor, color: formStyling.textColor } : undefined}>
                      <p style={textStyle}>No audio files to display.</p>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {paginatedAudioItems.map((audio) => (
                          <div
                            key={audio.id}
                            className="border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300"
                            style={isFormStyling ? {
                              backgroundColor: formStyling.backgroundColor,
                              color: formStyling.textColor
                            } : undefined}
                            onMouseEnter={(e) => {
                              if (isFormStyling && hoverFormStyle) {
                                e.currentTarget.style.backgroundColor = hoverFormStyle.backgroundColor
                                e.currentTarget.style.color = hoverFormStyle.color
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (isFormStyling) {
                                e.currentTarget.style.backgroundColor = formStyling.backgroundColor
                                e.currentTarget.style.color = formStyling.textColor
                              }
                            }}
                          >
                            {/* Audio Banner */}
                            {audio.banner?.url ? (
                              <div className="w-full h-44 overflow-hidden bg-gray-100">
                                <img
                                  src={audio.banner.url}
                                  alt={audio.banner.filename || audio.name || 'Audio banner'}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="w-full h-44 bg-gradient-to-r from-purple-100 to-indigo-100 flex items-center justify-center">
                                <span className="text-purple-600">No Banner</span>
                              </div>
                            )}

                            {/* Audio Player with Custom Controls */}
                            <div className="p-6">
                              {audio.audioFile?.url ? (
                                <AudioPlayer
                                  src={audio.audioFile.url}
                                  filename={audio.name || audio.audioFile.filename}
                                  formStyle={isFormStyling ? { 
                                    backgroundColor: formStyling.backgroundColor,
                                    color: formStyling.textColor
                                  } : { backgroundColor: '#F3F4F6' }}
                                  textStyle={isFormStyling ? { color: formStyling.textColor } : { color: '#374151' }}
                                  buttonStyle={isFormStyling ? {
                                    backgroundColor: formStyling.buttonBackgroundColor || formStyling.backgroundColor,
                                    color: formStyling.buttonTextColor || formStyling.textColor
                                  } : { backgroundColor: '#3B82F6', color: '#FFFFFF' }}
                                  hoverButtonStyle={isFormStyling ? {
                                    backgroundColor: formStyling.hoverButtonBackgroundColor || formStyling.buttonBackgroundColor,
                                    color: formStyling.hoverButtonTextColor || formStyling.buttonTextColor
                                  } : { backgroundColor: '#2563EB', color: '#FFFFFF' }}
                                  websiteId={effectiveWebsite?._id}
                                  websiteSlug={effectiveWebsite?.slug || slug}
                                />
                              ) : (
                                <div className="flex items-center justify-center h-20 bg-gray-200 rounded">
                                  <span className="text-gray-500">No audio file</span>
                                </div>
                              )}
                            </div>

                            {/* Audio Info */}
                            <div className="p-4">
                              <h3 className="text-lg font-semibold mb-2" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
                                {audio.name || 'Untitled Audio'}
                              </h3>

                              {audio.description && (
                                <p className="text-sm leading-relaxed" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
                                  {audio.description}
                                </p>
                              )}

                              {audio.audioFile?.filename && (
                                <p className="text-xs mt-2" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
                                  {audio.audioFile.filename}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Show pagination if more than one page */}
                      {totalAudioPages > 1 && (
                        <PaginationControls
                          currentPage={audioCurrentPage}
                          totalPages={totalAudioPages}
                          onPageChange={(page) => handleItemPageChange(section._id, page)}
                          sectionFormStyling={getFormStyling()}
                          useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
                          section={section}
                          sectionType={sectionType}
                        />
                      )}
                    </>
                  )}
                </div>
              )
            } else if (sectionType === 'left-sidebar') {
              const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
              const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, 'left-sidebar', section?._id)
              const formStyling = getFormStyling()
              const formStyle = isFormStyling ? {
                backgroundColor: formStyling.backgroundColor,
                color: formStyling.textColor
              } : undefined
              const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
              const hoverFormStyle = isFormStyling ? {
                backgroundColor: formStyling.hoverBackgroundColor || formStyling.backgroundColor,
                color: formStyling.hoverTextColor || formStyling.textColor
              } : undefined
              console.log('[LEFT-SIDEBAR-SECTION] Styling decision:', { useDefaultFormStyling, isFormStyling, colors: formStyling })
              innerContent = (
                <div style={formStyle} className="p-6 rounded-lg">
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-6">
                        {sectionTitle && <h2 className="text-xl font-bold mb-2" style={textStyle}>{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-sm" style={textStyle}>{section.description}</p>
                      )}
                    </div>
                  )}
                  <div className="space-y-4">
                    {section.content?.items?.map((item, index) => (
                      <div
                        key={index}
                        className="p-4 rounded shadow-sm transition-all duration-300 cursor-default"
                        style={isFormStyling ? {
                          backgroundColor: formStyling.backgroundColor,
                          color: formStyling.textColor
                        } : undefined}
                        onMouseEnter={(e) => {
                          if (isFormStyling && hoverFormStyle) {
                            e.currentTarget.style.backgroundColor = hoverFormStyle.backgroundColor
                            e.currentTarget.style.color = hoverFormStyle.color
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (isFormStyling) {
                            e.currentTarget.style.backgroundColor = formStyling.backgroundColor
                            e.currentTarget.style.color = formStyling.textColor
                          }
                        }}
                      >
                        <h3 className="font-semibold" style={isFormStyling ? { color: formStyling.textColor } : undefined}>{item.title}</h3>
                        <p className="text-sm" style={isFormStyling ? { color: formStyling.textColor } : undefined}>{item.description}</p>
                      </div>
                    )) || <p className="text-sm" style={textStyle}>No sidebar content available.</p>}
                  </div>
                </div>
              )
            } else if (sectionType === 'right-sidebar') {
              const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
              const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, 'right-sidebar', section?._id)
              const formStyling = getFormStyling()
              const formStyle = isFormStyling ? {
                backgroundColor: formStyling.backgroundColor,
                color: formStyling.textColor
              } : undefined
              const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
              const hoverFormStyle = isFormStyling ? {
                backgroundColor: formStyling.hoverBackgroundColor || formStyling.backgroundColor,
                color: formStyling.hoverTextColor || formStyling.textColor
              } : undefined
              console.log('[RIGHT-SIDEBAR-SECTION] Styling decision:', { useDefaultFormStyling, isFormStyling, colors: formStyling })
              innerContent = (
                <div style={formStyle} className="p-6 rounded-lg">
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-6">
                        {sectionTitle && <h2 className="text-xl font-bold mb-2" style={textStyle}>{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-sm" style={textStyle}>{section.description}</p>
                      )}
                    </div>
                  )}
                  <div className="space-y-4">
                    {section.content?.items?.map((item, index) => (
                      <div
                        key={index}
                        className="p-4 rounded shadow-sm transition-all duration-300 cursor-default"
                        style={isFormStyling ? {
                          backgroundColor: formStyling.backgroundColor,
                          color: formStyling.textColor
                        } : undefined}
                        onMouseEnter={(e) => {
                          if (isFormStyling && hoverFormStyle) {
                            e.currentTarget.style.backgroundColor = hoverFormStyle.backgroundColor
                            e.currentTarget.style.color = hoverFormStyle.color
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (isFormStyling) {
                            e.currentTarget.style.backgroundColor = formStyling.backgroundColor
                            e.currentTarget.style.color = formStyling.textColor
                          }
                        }}
                      >
                        <h3 className="font-semibold" style={isFormStyling ? { color: formStyling.textColor } : undefined}>{item.title}</h3>
                        <p className="text-sm" style={isFormStyling ? { color: formStyling.textColor } : undefined}>{item.description}</p>
                      </div>
                    )) || <p className="text-sm" style={textStyle}>No sidebar content available.</p>}
                  </div>
                </div>
              )
            } else if (sectionType === 'section-items') {
              const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
              const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, 'section-items', section?._id)
              const useDefaultButtonStyling = effectiveWebsite?.useDefaultStyling !== false
              const isButtonStyling = useDefaultButtonStyling || isButtonStylingApplied(website?.sectionFormStyling, 'section-items', section?._id)
              
              const formStyling = getFormStyling()
              const buttonStyling = getButtonStyling()
              // Ensure styling colors have fallbacks
              const formBgColor = isFormStyling ? formStyling.backgroundColor : undefined
              const formTextColor = isFormStyling ? formStyling.textColor : undefined
              const formHoverBgColor = isFormStyling ? (formStyling.hoverBackgroundColor || formBgColor || '#F9FAFB') : undefined
              const formHoverTextColor = isFormStyling ? (formStyling.hoverTextColor || formTextColor || '#374151') : undefined
              
              const btnBgColor = isButtonStyling ? (buttonStyling.buttonBackgroundColor || buttonStyling.backgroundColor) : undefined
              const btnTextColor = isButtonStyling ? (buttonStyling.buttonTextColor || buttonStyling.textColor) : undefined
              const btnHoverBgColor = isButtonStyling ? (buttonStyling.hoverButtonBackgroundColor || buttonStyling.hoverBackgroundColor || btnBgColor || '#2563EB') : undefined
              const btnHoverTextColor = isButtonStyling ? (buttonStyling.hoverButtonTextColor || buttonStyling.hoverTextColor || btnTextColor || '#FFFFFF') : undefined
               
              const formStyle = isFormStyling ? {
                backgroundColor: formBgColor,
                color: formTextColor
              } : undefined
              const textStyle = isFormStyling ? { 
                color: formTextColor
              } : undefined
              const hoverFormStyle = isFormStyling ? {
                backgroundColor: formHoverBgColor,
                color: formHoverTextColor,
                borderColor: formHoverTextColor
              } : undefined
               
              const buttonStyle = isButtonStyling ? {
                backgroundColor: btnBgColor,
                color: btnTextColor,
                borderColor: btnBgColor
              } : undefined
              const buttonHoverStyle = isButtonStyling ? {
                backgroundColor: btnHoverBgColor,
                color: btnHoverTextColor,
                borderColor: btnHoverBgColor
              } : undefined
               
              const cardStyle = {
                backgroundColor: isFormStyling ? formBgColor : '#ffffff',
                color: isFormStyling ? formTextColor : '#111827',
                borderColor: isFormStyling ? (formTextColor || formBgColor) : '#e5e7eb'
              }
              const cardHoverStyle = isFormStyling ? {
                backgroundColor: formHoverBgColor,
                color: formHoverTextColor,
                borderColor: formHoverTextColor
              } : undefined
               
              const imageBoxStyle = (isHovered) => ({
                backgroundColor: isFormStyling ? (isHovered ? formHoverBgColor : formBgColor) : '#f3f4f6',
                color: isFormStyling ? (isHovered ? formHoverTextColor : formTextColor) : undefined
              })
              const currentPage = itemPagination[section._id] || 1
              const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE)
              const startIdx = (currentPage - 1) * ITEMS_PER_PAGE
              const displayedItems = needsPagination 
                ? filteredItems.slice(startIdx, startIdx + ITEMS_PER_PAGE)
                : filteredItems
              
              innerContent = (
                <div style={formStyle}>
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-12 text-center">
                        {sectionTitle && <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={textStyle}>{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-base sm:text-lg max-w-2xl mx-auto" style={textStyle}>{section.description}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Search Bar */}
                  <div className="mb-6">
                    <input
                      type="text"
                      placeholder="Search items by title or description..."
                      value={itemFilters[section._id] || ''}
                      onChange={(e) => handleItemFilterChange(section._id, e.target.value)}
                      className="input-field w-full border-2 rounded-lg px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                      style={isFormStyling ? {
                        backgroundColor: formStyling.backgroundColor,
                        color: formStyling.textColor,
                        borderColor: formStyling.textColor
                      } : { borderColor: '#d1d5db', color: '#111827', backgroundColor: '#ffffff' }}
                    />
                  </div>
                  
                  {/* Pagination Info */}
                  {needsPagination && (
                    <div className="mb-4 flex items-center justify-between">
                      <div className="text-sm" style={textStyle}>
                        Showing {startIdx + 1} to {Math.min(startIdx + ITEMS_PER_PAGE, filteredItems.length)} of {filteredItems.length} items
                      </div>
                    </div>
                  )}
                  
                  {/* Items Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {displayedItems.map((item, itemIdx) => {
                      const cardKey = `${section._id}-${itemIdx}`
                      return (
                        <div
                          key={cardKey}
                          className={`border-2 rounded-lg overflow-hidden hover:shadow-xl transition-all duration-200 cursor-default ${!isFormStyling ? 'bg-white text-gray-900' : ''}`}
                          style={hoveredSectionItemCards.has(cardKey) && cardHoverStyle ? cardHoverStyle : cardStyle}
                          onMouseEnter={() => setHoveredSectionItemCards(prev => new Set([...prev, cardKey]))}
                          onMouseLeave={() => setHoveredSectionItemCards(prev => { const newSet = new Set(prev); newSet.delete(cardKey); return newSet; })}
                        >
                        {/* Gallery/Image Section */}
                        {(item.gallery && item.gallery.length > 0) ? (
                          <div className="space-y-3 p-3" style={imageBoxStyle(hoveredSectionItemCards.has(cardKey))}>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                try {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                                  handleViewItem(item, 0)
                                } catch (err) {
                                  console.error('[PublicWebsiteViewer] Error opening gallery:', err)
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  handleViewItem(item, 0)
                                }
                              }}
                              className="relative overflow-hidden rounded h-40 cursor-pointer hover:opacity-90 transition-opacity"
                              style={imageBoxStyle(hoveredSectionItemCards.has(cardKey))}
                            >
                              {item.gallery[0].image?.url || item.gallery[0].url ? (
                                <div className="relative h-full w-full">
                                  <img
                                    src={item.gallery[0].image?.url || item.gallery[0].url}
                                    alt={item.gallery[0].title || item.title}
                                    className="w-full h-full object-cover pointer-events-none"
                                    draggable={false}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none'
                                      e.currentTarget.nextElementSibling?.classList.remove('hidden')
                                    }}
                                  />
                                  <div className="hidden h-full w-full items-center justify-center bg-gray-200 px-4 text-center text-xs text-gray-500">Image unavailable</div>
                                </div>
                              ) : getWebsiteUrlFromGalleryItem(item.gallery[0]) ? (
                                <WebsiteUrlCard url={getWebsiteUrlFromGalleryItem(item.gallery[0])} className="h-full" />
                              ) : (item.gallery[0].video?.url || item.gallery[0].videoUrl || item.gallery[0].video) ? (
                                isExternalVideoUrl(item.gallery[0].video || item.gallery[0].videoUrl || item.gallery[0].video) ? (
                                  <VideoUrlFrame
                                    video={item.gallery[0].video || {
                                      url: item.gallery[0].videoUrl || item.gallery[0].video,
                                      videoUrl: item.gallery[0].videoUrl || item.gallery[0].video,
                                      source: 'url'
                                    }}
                                    className="h-full w-full rounded"
                                  />
                                ) : (
                                  <video
                                    src={getVideoUrl(item.gallery[0].video || item.gallery[0].videoUrl || item.gallery[0].video)}
                                    poster={getVideoPoster(item.gallery[0].video || item.gallery[0].videoUrl || item.gallery[0].video)}
                                    className="w-full h-full object-cover pointer-events-none"
                                    playsInline
                                    autoPlay
                                    muted
                                    loop
                                    preload="metadata"
                                    onMouseDown={(e) => e.preventDefault()}
                                  />
                                )
                              ) : null}
                              {/* Top video overlay removed per design: keep other video metadata unchanged */}
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {(item.gallery || []).slice(0, 4).map((g, gi) => {
                                  const hasImage = !!(g.image?.url || g.url)
                                  const isVideo = !!(g.video?.url || g.videoUrl || g.video)
                                  const websiteUrl = getWebsiteUrlFromGalleryItem(g)
                                  const isLink = !!websiteUrl

                                  return (
                                    <button 
                                      key={g.id || gi} 
                                      type="button"
                                      onClick={(e) => {
                                        try {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                                          handleViewItem(item, gi)
                                        } catch (err) {
                                          console.error('[PublicWebsiteViewer] Error opening gallery from thumbnail:', err)
                                        }
                                      }} 
                                      className="w-8 h-8 rounded overflow-hidden border-2 border-gray-200 hover:border-primary-400 transition-colors cursor-pointer"
                                    >
                                      {hasImage ? (
                                        <img src={g.image?.url || g.url} alt={`thumb-${gi}`} className="w-full h-full object-cover pointer-events-none" draggable={false} onMouseDown={(e) => e.preventDefault()} />
                                      ) : isVideo ? (
                                        <VideoThumbnail video={g.video || g.videoUrl || g} alt={`thumb-${gi}`} className="w-full h-full object-cover pointer-events-none" missingFallbackClassName="w-full h-full rounded bg-gray-200 flex items-center justify-center text-[10px] text-gray-500" />
                                      ) : isLink ? (
                                        <WebsiteUrlCard url={websiteUrl} className="h-full rounded-none" />
                                      ) : null}
                                    </button>
                                  )
                                })}
                                {item.gallery.length > 4 && (
                                  <span className="text-sm" style={isFormStyling ? {
                                    color: hoveredSectionItemCards.has(cardKey) ? formHoverTextColor : formTextColor
                                  } : { color: '#4B5563' }}>+{item.gallery.length - 4}</span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                                  handleViewItem(item, 0)
                                }}
                                className={`px-3 py-1 rounded-lg transition-colors text-sm font-medium ${isButtonStyling ? '' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
                                style={hoveredSectionItemButtons.has(`view-${itemIdx}`) && buttonHoverStyle ? buttonHoverStyle : buttonStyle || { backgroundColor: '#0EA5E9', color: 'white' }}
                                onMouseEnter={() => setHoveredSectionItemButtons(prev => new Set([...prev, `view-${itemIdx}`]))}
                                onMouseLeave={() => setHoveredSectionItemButtons(prev => { const newSet = new Set(prev); newSet.delete(`view-${itemIdx}`); return newSet; })}
                              >
                                View
                              </button>
                            </div>
                          </div>
                        ) : item.image ? (
                          <div
                            role="button"
                            tabIndex={0}
                            className="relative overflow-hidden rounded h-40 cursor-pointer hover:opacity-90 transition-opacity m-3"
                            style={imageBoxStyle(hoveredSectionItemCards.has(cardKey))}
                            onClick={(e) => {
                              try {
                                e.preventDefault()
                                e.stopPropagation()
                                if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                                handleViewItem(item, 0)
                              } catch (err) {
                                console.error('[PublicWebsiteViewer] Error opening item from image click:', err)
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                e.stopPropagation()
                                handleViewItem(item, 0)
                              }
                            }}
                          >
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-full h-full object-cover pointer-events-none"
                              draggable={false}
                              onMouseDown={(e) => e.preventDefault()}
                            />
                          </div>
                        ) : (item.video || item.video?.url) ? (
                          isExternalVideoUrl(item.video) ? (
                            <VideoUrlFrame video={item.video} className="m-3 h-40 w-[calc(100%-1.5rem)] rounded" />
                          ) : (
                            <div
                              role="button"
                              tabIndex={0}
                              className="relative overflow-hidden rounded h-40 cursor-pointer hover:opacity-90 transition-opacity m-3"
                              onClick={(e) => {
                                try {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                                  handleViewItem(item, 0)
                                } catch (err) {
                                  console.error('[PublicWebsiteViewer] Error opening video item:', err)
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  handleViewItem(item, 0)
                                }
                              }}
                            >
                              <video
                                src={getVideoUrl(item.video)}
                                poster={getVideoPoster(item.video)}
                                className="w-full h-full object-cover pointer-events-none"
                                playsInline
                                autoPlay
                                muted
                                loop
                                preload="metadata"
                                draggable={false}
                                onMouseDown={(e) => e.preventDefault()}
                              />
                              {/* Top video overlay removed per design: keep other video metadata unchanged */}
                            </div>
                          )
                        ) : null}
                        
                        {/* Content Section */}
                        <div className="p-4" style={isFormStyling ? {
                          backgroundColor: hoveredSectionItemCards.has(cardKey) ? formHoverBgColor : formBgColor,
                          color: hoveredSectionItemCards.has(cardKey) ? formHoverTextColor : formTextColor
                        } : undefined}>
                          {item.title && (
                            <h4 className="text-lg font-semibold mb-2" style={isFormStyling ? {
                              color: hoveredSectionItemCards.has(cardKey) ? formHoverTextColor : formTextColor
                            } : undefined}>
                              {item.title}
                            </h4>
                          )}
                          {item.description && (
                            <p className="text-sm mb-3" style={isFormStyling ? {
                              color: hoveredSectionItemCards.has(cardKey) ? formHoverTextColor : formTextColor
                            } : undefined}>
                              {item.description}
                            </p>
                          )}
                          
                          {/* Price Display */}
                          {item.price !== undefined && item.price !== null && item.price !== '' && (
                            <div className="mb-3">
                              <span className="text-lg font-bold" style={isFormStyling ? {
                                color: hoveredSectionItemCards.has(cardKey) ? formHoverTextColor : formTextColor
                              } : undefined}>
                                {formatPrice(item.price, item.currency)}
                              </span>
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="flex flex-col gap-2">
                            {item.link && (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                                style={hoveredSectionItemButtons.has(`link-${itemIdx}`) && buttonHoverStyle ? buttonHoverStyle : buttonStyle || { backgroundColor: '#0EA5E9', color: 'white' }}
                                onMouseEnter={() => setHoveredSectionItemButtons(prev => new Set([...prev, `link-${itemIdx}`]))}
                                onMouseLeave={() => setHoveredSectionItemButtons(prev => { const newSet = new Set(prev); newSet.delete(`link-${itemIdx}`); return newSet; })}
                              >
                                {item.buttonText || 'View'}
                              </a>
                            )}
                            
                            {(item.whatsappContact || item.whatsappNumber) && (
                              <button
                                onClick={() => {
                                  console.log('[PublicWebsiteViewer] ✅ Section item WhatsApp button clicked:', {
                                    itemTitle: item.title,
                                    itemId: item.id,
                                    sectionType: 'section-items',
                                    phoneNumber: item.whatsappContact || item.whatsappNumber,
                                    timestamp: new Date().toISOString(),
                                    buttonColor: '#10B981',
                                    colorVerified: true,
                                    styling: 'permanent green with !important flags',
                                    deviceWidth: window.innerWidth,
                                    deviceType: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop'
                                  })
                                  handleItemWhatsAppContact(item)
                                }}
                                type="button"
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 hover:shadow-md permanent-whatsapp-button"
                                style={{
                                  backgroundColor: hoveredSectionItemButtons.has(`whatsapp-${itemIdx}`) ? '#059669' : '#10B981',
                                  color: '#FFFFFF',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                                  padding: '0.5rem 0.75rem',
                                  fontSize: '0.875rem',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  whiteSpace: 'nowrap',
                                  transition: 'all 200ms'
                                }}
                                title="Contact via WhatsApp"
                                aria-label={`Contact ${item.title} via WhatsApp`}
                                onMouseEnter={() => setHoveredSectionItemButtons(prev => new Set([...prev, `whatsapp-${itemIdx}`]))}
                                onMouseLeave={() => setHoveredSectionItemButtons(prev => { const newSet = new Set(prev); newSet.delete(`whatsapp-${itemIdx}`); return newSet; })}
                              >
                                <WhatsAppIcon className="w-4 h-4 whatsapp-icon" style={{ color: '#FFFFFF' }} />
                                Contact via WhatsApp
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )})}
                  </div>
                  
                  {/* Pagination Controls */}
                  {needsPagination && (
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(page) => handleItemPageChange(section._id, page)}
                      sectionFormStyling={getFormStyling()}
                      useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
                      section={section}
                      sectionType={sectionType}
                    />
                  )}
                </div>
              )
            } else if (sectionType === 'image-gallery-modal') {
              const images = section.content?.images || []
              const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
              const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, 'image-gallery-modal', section?._id)
              const formStyling = getFormStyling()
              const formStyle = isFormStyling ? {
                backgroundColor: formStyling.backgroundColor,
                color: formStyling.textColor
              } : undefined
              const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
              innerContent = (
                <div style={formStyle}>
                  {!hasTopHeader && (sectionTitle || section.description?.toString().trim()) && (
                    <div className="mb-12 text-center">
                        {sectionTitle && <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={textStyle}>{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-base sm:text-lg max-w-2xl mx-auto" style={textStyle}>{section.description}</p>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {images.map((image, index) => (
                      <div
                        key={index}
                        className="relative group cursor-pointer"
                        style={isFormStyling ? {
                          backgroundColor: formStyling.backgroundColor,
                          color: formStyling.textColor
                        } : undefined}
                        onClick={() => {
                          setGalleryItems(images)
                          setGalleryStartIndex(index)
                          setShowGalleryModal(true)
                        }}
                      >
                        <img
                          src={image.url}
                          alt={image.alt || `Gallery image ${index + 1}`}
                          className="w-full h-48 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
                          <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity">View</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {images.length === 0 && <p className="text-center" style={textStyle}>No images to display.</p>}
                </div>
              )
            } else if (sectionType === 'crypto-product') {
              innerContent = (
                <CryptoSection website={website} websiteId={website._id} section={section} sectionFormStyling={getFormStyling()} useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false} sectionType="crypto-product" customerProfile={customerAuthUser} customerAuthToken={customerAuthToken} />
              )
            } else if (sectionType === 'section-items' || section.type === 'section-items' || section.sectionType === 'section-items') {
              // Fallback handler for section-items if not caught above (shouldn't happen but safety check)
              console.warn('[PublicWebsiteViewer] Section Items caught in fallback handler - should have been caught by explicit handler at line 1618')
              // Re-route to the proper section-items handler above by recursively calling (this is a safety net)
              // For now, render nothing to prevent double rendering
              innerContent = <div className="text-center text-gray-600 py-8">Section Items section not properly rendered</div>
            } else {
              // Default generic content
              let itemsContent = null
              if (section.content.items && section.content.items.length > 0) {
                const q = (itemFilters[section._id] || '').toString().trim().toLowerCase()
                const filteredItems = (section.content.items || []).filter(item => {
                  const title = (item.title || '').toString().toLowerCase()
                  const desc = (item.description || '').toString().toLowerCase()
                  return !q || title.includes(q) || desc.includes(q)
                })

                // Pagination logic: show 10 items per page if more than 10 items
                const needsPagination = filteredItems.length > ITEMS_PER_PAGE
                const currentPage = itemPagination[section._id] || 1
                const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE)
                const startIdx = (currentPage - 1) * ITEMS_PER_PAGE
                const displayedItems = needsPagination 
                  ? filteredItems.slice(startIdx, startIdx + ITEMS_PER_PAGE)
                  : filteredItems

                // Apply section form styling
                const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
                const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, sectionType, section?._id)
                const useDefaultButtonStyling = effectiveWebsite?.useDefaultStyling !== false
                const isButtonStyling = useDefaultButtonStyling || isButtonStylingApplied(website?.sectionFormStyling, sectionType, section?._id)
                const formStyling = getFormStyling()
                const buttonStyling = getButtonStyling()
                const formStyle = isFormStyling ? {
                  backgroundColor: formStyling.backgroundColor,
                  color: formStyling.textColor
                } : undefined
                const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
                const hoverFormStyle = isFormStyling ? {
                  backgroundColor: formStyling.hoverBackgroundColor || formStyling.backgroundColor,
                  color: formStyling.hoverTextColor || formStyling.textColor
                } : undefined
                const buttonStyle = isButtonStyling ? {
                  backgroundColor: buttonStyling.buttonBackgroundColor || buttonStyling.backgroundColor,
                  color: buttonStyling.buttonTextColor || buttonStyling.textColor,
                  borderColor: buttonStyling.buttonBackgroundColor || buttonStyling.backgroundColor
                } : undefined
                const buttonHoverStyle = isButtonStyling ? {
                  backgroundColor: buttonStyling.hoverButtonBackgroundColor || buttonStyling.hoverBackgroundColor || buttonStyling.buttonBackgroundColor || buttonStyling.backgroundColor,
                  color: buttonStyling.hoverButtonTextColor || buttonStyling.hoverTextColor || buttonStyling.buttonTextColor || buttonStyling.textColor
                } : undefined
                const imageWrapperStyle = isFormStyling ? {
                  backgroundColor: formStyling.backgroundColor,
                  color: formStyling.textColor
                } : { backgroundColor: '#f3f4f6' }

                itemsContent = (
                  <div className="mt-4">
                    <div className="mb-4">
                      <input
                        type="text"
                        placeholder="Search items by title or description..."
                        value={itemFilters[section._id] || ''}
                        onChange={(e) => handleItemFilterChange(section._id, e.target.value)}
                        className="input-field w-full"
                        style={isFormStyling ? {
                          backgroundColor: formStyling.backgroundColor,
                          color: formStyling.textColor,
                          borderColor: formStyling.textColor
                        } : undefined}
                      />
                    </div>

                    {needsPagination && (
                      <div className="mb-4 flex items-center justify-between">
                        <div className="text-sm text-gray-600" style={textStyle}>
                          Showing {startIdx + 1} to {Math.min(startIdx + ITEMS_PER_PAGE, filteredItems.length)} of {filteredItems.length} items
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {displayedItems.map((item, itemIdx) => (
                        <div key={itemIdx} className={`rounded-lg shadow p-6 hover:shadow-lg transition-shadow ${isFormStyling ? '' : 'bg-white'}`} style={isFormStyling ? {
                          backgroundColor: formStyling.backgroundColor,
                          color: formStyling.textColor
                        } : undefined}
                        onMouseEnter={(e) => {
                          if (isFormStyling && hoverFormStyle) {
                            e.currentTarget.style.backgroundColor = hoverFormStyle.backgroundColor
                            e.currentTarget.style.color = hoverFormStyle.color
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (isFormStyling) {
                            e.currentTarget.style.backgroundColor = formStyling.backgroundColor
                            e.currentTarget.style.color = formStyling.textColor
                          }
                        }}
                        >
                          {item.gallery && item.gallery.length > 0 ? (
                            <div className="space-y-3 mb-4">
                              <div
                                role="button"
                                tabIndex={0}
                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation() }}
                                onClick={(e) => {
                                  try {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                                    console.log('[PublicWebsiteViewer] Gallery main image clicked:', { title: item.title, itemId: item.id })
                                    handleViewItem(item, 0)
                                  } catch (err) {
                                    console.error('[PublicWebsiteViewer] Error opening gallery:', err)
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleViewItem(item, 0)
                                  }
                                }}
                                className="relative overflow-hidden rounded h-48 cursor-pointer hover:opacity-90 transition-opacity"
                                style={imageWrapperStyle}
                              >
                                {item.gallery[0].image?.url || item.gallery[0].url ? (
                                  <img
                                    src={item.gallery[0].image?.url || item.gallery[0].url}
                                    alt={item.gallery[0].title || item.title}
                                    className="w-full h-full object-cover pointer-events-none"
                                    draggable={false}
                                    onMouseDown={(e) => e.preventDefault()}
                                  />
                                ) : item.gallery[0].link?.url ? (
                                  <WebsiteUrlCard url={item.gallery[0].link.url} className="h-full" />
                                ) : (item.gallery[0].video?.url || item.gallery[0].videoUrl || item.gallery[0].video) ? (
                                  isExternalVideoUrl(item.gallery[0].video || item.gallery[0].videoUrl || item.gallery[0].video) ? (
                                    <VideoUrlFrame
                                      video={item.gallery[0].video || {
                                        url: item.gallery[0].videoUrl || item.gallery[0].video,
                                        videoUrl: item.gallery[0].videoUrl || item.gallery[0].video,
                                        source: 'url'
                                      }}
                                      className="h-full w-full rounded"
                                    />
                                  ) : (
                                    <video
                                      src={getVideoUrl(item.gallery[0].video || item.gallery[0].videoUrl || item.gallery[0].video)}
                                      poster={getVideoPoster(item.gallery[0].video || item.gallery[0].videoUrl || item.gallery[0].video)}
                                      className="w-full h-full object-cover pointer-events-none"
                                      playsInline
                                      autoPlay
                                      muted
                                      loop
                                      preload="metadata"
                                      onMouseDown={(e) => e.preventDefault()}
                                    />
                                  )
                                ) : null}
                                {/* Top video overlay removed per design: keep other video metadata unchanged */}
                              </div>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {(item.gallery || []).slice(0, 4).map((g, gi) => {
                                    const hasImage = !!(g.image?.url || g.url)
                                    const isVideo = !!(g.video?.url || g.videoUrl || g.video)
                                    const isLink = !!g.link?.url

                                    return (
                                      <button 
                                        key={g.id || gi} 
                                        type="button"
                                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation() }}
                                        onClick={(e) => {
                                          try {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                                            console.log('[PublicWebsiteViewer] Gallery thumbnail clicked:', { itemTitle: item.title, thumbIndex: gi, itemId: item.id })
                                            handleViewItem(item, gi)
                                          } catch (err) {
                                            console.error('[PublicWebsiteViewer] Error opening gallery from thumbnail:', err)
                                          }
                                        }} 
                                        className="w-10 h-10 rounded overflow-hidden border-2 border-gray-200 hover:border-primary-400 transition-colors cursor-pointer"
                                      >
                                        {hasImage ? (
                                          <img src={g.image?.url || g.url} alt={`thumb-${gi}`} className="w-full h-full object-cover pointer-events-none" draggable={false} onMouseDown={(e) => e.preventDefault()} />
                                        ) : isVideo ? (
                                          <VideoThumbnail video={g.video || g.videoUrl || g} alt={`thumb-${gi}`} className="w-full h-full object-cover pointer-events-none" missingFallbackClassName="w-full h-full rounded bg-gray-200 flex items-center justify-center text-[10px] text-gray-500" />
                                        ) : isLink ? (
                                          <WebsiteUrlCard url={g.link.url} className="h-full rounded-none" />
                                        ) : (
                                          <div className="w-full h-full rounded bg-gray-200 flex items-center justify-center text-[10px] text-gray-500">Video</div>
                                        )}
                                      </button>
                                    )
                                  })}
                                  {item.gallery.length > 4 && (
                                    <span className="text-sm text-gray-600">+{item.gallery.length - 4}</span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation() }}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                                    handleViewItem(item, 0)
                                  }}
                                  className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium ${isButtonStyling ? '' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
                                  style={buttonStyle || { backgroundColor: '#3B82F6', color: 'white' }}
                                  onMouseEnter={(e) => {
                                    if (isButtonStyling && buttonHoverStyle) {
                                      e.currentTarget.style.backgroundColor = buttonHoverStyle.backgroundColor
                                      e.currentTarget.style.color = buttonHoverStyle.color
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (isButtonStyling && buttonStyle) {
                                      e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor
                                      e.currentTarget.style.color = buttonStyle.color
                                    }
                                  }}
                                >
                                  View
                                </button>
                              </div>
                            </div>
                          ) : item.image ? (
                            <div
                              role="button"
                              tabIndex={0}
                              className="relative overflow-hidden rounded h-48 cursor-pointer hover:opacity-90 transition-opacity mb-4"
                              style={imageWrapperStyle}
                              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation() }}
                              onClick={(e) => {
                                try {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                                  console.log('[PublicWebsiteViewer] Item single image clicked:', { title: item.title, itemId: item.id, image: item.image })
                                  handleViewItem(item, 0)
                                } catch (err) {
                                  console.error('[PublicWebsiteViewer] Error opening item from image click:', err)
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  handleViewItem(item, 0)
                                }
                              }}
                            >
                              <img
                                src={item.image}
                                alt={item.title}
                                className="w-full h-full object-cover pointer-events-none"
                                draggable={false}
                                onMouseDown={(e) => e.preventDefault()}
                              />
                            </div>
                          ) : (item.video || item.video?.url) ? (
                            isExternalVideoUrl(item.video) ? (
                              <VideoUrlFrame video={item.video} className="mb-4 h-48 w-full rounded" />
                            ) : (
                              <div
                                role="button"
                                tabIndex={0}
                                className="relative overflow-hidden rounded h-48 cursor-pointer hover:opacity-90 transition-opacity mb-4"
                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation() }}
                                onClick={(e) => {
                                  try {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                                    handleViewItem(item, 0)
                                  } catch (err) {
                                    console.error('[PublicWebsiteViewer] Error opening video item:', err)
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleViewItem(item, 0)
                                  }
                                }}
                              >
                                <video
                                  src={getVideoUrl(item.video)}
                                  poster={getVideoPoster(item.video)}
                                  className="w-full h-full object-cover pointer-events-none"
                                  playsInline
                                  autoPlay
                                  muted
                                  loop
                                  preload="metadata"
                                  draggable={false}
                                  onMouseDown={(e) => e.preventDefault()}
                                />
                                {/* Top video overlay removed per design: keep other video metadata unchanged */}
                              </div>
                            )
                          ) : null}
                          
                          {item.title && (
                            <h4 className="text-xl font-semibold text-gray-900 mb-2" style={textStyle}>
                              {item.title}
                            </h4>
                          )}
                          {item.description && (
                            <p className="text-gray-600" style={textStyle}>
                              {item.description}
                            </p>
                          )}
                          {item.price !== undefined && item.price !== null && item.price !== '' && (
                            <div className="mt-2">
                              <span className="text-lg font-bold text-green-600" style={textStyle}>
                                {formatPrice(item.price, item.currency)}
                              </span>
                            </div>
                          )}
                          {(item.whatsappContact || item.whatsappNumber) && (
                            <button
                              onClick={() => {
                                console.log('[PublicWebsiteViewer] ✅ Section items WhatsApp button clicked:', {
                                  itemTitle: item.name || item.title,
                                  itemId: item._id,
                                  sectionType: 'section-items',
                                  phoneNumber: item.whatsappContact || item.whatsappNumber,
                                  timestamp: new Date().toISOString(),
                                  buttonColor: '#10B981',
                                  colorVerified: true,
                                  styling: 'permanent green with !important flags',
                                  deviceWidth: window.innerWidth,
                                  deviceType: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop'
                                })
                                handleItemWhatsAppContact(item)
                              }}
                              type="button"
                              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 hover:shadow-md permanent-whatsapp-button"
                              style={{
                                backgroundColor: hoveredSectionItemButtons.has(`whatsapp-list-${itemIdx}`) ? '#059669' : '#10B981',
                                color: '#FFFFFF',
                                border: 'none',
                                cursor: 'pointer',
                                fontWeight: 600,
                                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                                padding: '0.5rem 0.75rem',
                                fontSize: '0.875rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                whiteSpace: 'nowrap',
                                transition: 'all 200ms'
                              }}
                              title="Contact via WhatsApp"
                              aria-label={`Contact ${item.name || item.title} via WhatsApp`}
                              onMouseEnter={() => setHoveredSectionItemButtons(prev => new Set([...prev, `whatsapp-list-${itemIdx}`]))}
                              onMouseLeave={() => setHoveredSectionItemButtons(prev => { const newSet = new Set(prev); newSet.delete(`whatsapp-list-${itemIdx}`); return newSet; })}
                            >
                              <WhatsAppIcon className="w-4 h-4 whatsapp-icon" style={{ color: '#FFFFFF' }} />
                              Contact via WhatsApp
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {needsPagination && (
                      <PaginationControls
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(page) => handleItemPageChange(section._id, page)}
                        sectionFormStyling={getFormStyling()}
                        useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
                        section={section}
                        sectionType={sectionType}
                      />
                    )}
                  </div>
                )
              }
              innerContent = (
                <>
                  {!hasTopHeader && (sectionTitle || section.description) && (
                    <div className="mb-8">
                      {sectionTitle && <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{sectionTitle}</h2>}
                      {section.description && (
                        <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
                      )}
                    </div>
                  )}

                  {section.content && (
                    <div className="space-y-6">
                      {sectionHeading && (
                        <h3 className="text-2xl font-semibold text-gray-800" style={{ textAlign: section.content.headingPosition || 'left' }}>
                          {sectionHeading}
                        </h3>
                      )}

                      {section.content.subheading && (
                        <p className={`text-gray-600 text-lg leading-relaxed ${section.content.subheadingTextStyle === 'bold' ? 'font-bold' : section.content.subheadingTextStyle === 'slightly-bold' ? 'font-medium' : ''}`} style={{ textAlign: section.content.subheadingPosition || 'left' }}>
                          {section.content.subheading}
                        </p>
                      )}

                      {itemsContent}
                    </div>
                  )}
                </>
              )
            }

            // Build wrapper layout with optional sidebars and special media
            const getSpecialMediaStyles = (m) => {
              const isMobile = window.innerWidth < 768 // Mobile breakpoint
              const baseWidth = m.style?.width || '100%'
              const baseHeight = m.style?.height || 'auto'

              return {
                width: isMobile ? '100%' : baseWidth, // Force full width on mobile for better fit
                height: isMobile ? 'auto' : baseHeight, // Auto height on mobile
                maxWidth: '100%',
                maxHeight: isMobile ? '60vh' : '100%', // Limit height on mobile
                paddingLeft: m.style?.paddingLeft || '0px',
                paddingRight: m.style?.paddingRight || '0px',
                marginLeft: m.style?.marginLeft || '0px',
                marginRight: m.style?.marginRight || '0px',
                objectFit: 'contain'
              }
            }

            const getFullMediaWrapperStyle = (m) => {
              const isMobile = window.innerWidth < 768

              return {
                position: 'relative', // Changed from absolute for better mobile support
                top: 0,
                left: 0,
                width: '100%',
                height: isMobile ? '40vh' : (m.style?.height || '60vh'), // Smaller height on mobile
                minHeight: isMobile ? '200px' : '300px', // Smaller min height on mobile
                maxHeight: isMobile ? '50vh' : '80vh', // Smaller max height on mobile
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'none',
                zIndex: 0
              }
            }

            const getFullMediaStyles = (m) => {
              const isMobile = window.innerWidth < 768

              return {
                width: isMobile ? '100%' : (m.style?.width || '100%'), // Full width on mobile
                height: isMobile ? '100%' : (m.style?.height || '100%'), // Full height within container on mobile
                maxWidth: '100%',
                maxHeight: '100%',
                paddingLeft: m.style?.paddingLeft || '0px',
                paddingRight: m.style?.paddingRight || '0px',
                marginLeft: m.style?.marginLeft || '0px',
                marginRight: m.style?.marginRight || '0px',
                objectFit: 'contain'
              }
            }

            const hasFullMedia = specialMedia.some(m => m.display === 'full')
            const hasMultipleSpecialMedia = specialMedia.length > 1
            const renderSpecialMediaImages = (mediaItems, display) => {
              const images = mediaItems.filter(m => m.type === 'image' && m.url)
              if (images.length === 0) return null

              return (
                <SpecialMediaImageCarousel
                  images={images}
                  display={display}
                  getImageStyle={display === 'full' ? getFullMediaStyles : getSpecialMediaStyles}
                  getWrapperStyle={getFullMediaWrapperStyle}
                />
              )
            }

            return (
              <section 
                key={section._id} 
                id={`section-${idx}`} 
                className={`relative ${headerOrder === idx ? 'overflow-visible' : 'overflow-hidden'} py-8 sm:py-12 md:py-16 border-b border-gray-200 last:border-b-0 scroll-mt-20 transition-colors${section.styling?.textColor ? ' public-section-text-override' : ''}`}
                onMouseEnter={() => setHoveredSectionId(section._id)}
                onMouseLeave={() => setHoveredSectionId(null)} 
                style={{
                  paddingTop: firstSectionHeaderOffset,
                  minHeight: hasFullMedia ? 'auto' : 'auto',
                  backgroundColor: hoveredSectionId === section._id ? (getBodyStyling()?.hoverBackgroundColor || getBodyStyling()?.backgroundColor || 'transparent') : (getBodyStyling()?.backgroundColor || 'transparent'),
                  color: hoveredSectionId === section._id ? (getBodyStyling()?.hoverTextColor || getBodyStyling()?.textColor || 'inherit') : (getBodyStyling()?.textColor || 'inherit'),
                  '--section-text-color': hoveredSectionId === section._id ? (getBodyStyling()?.hoverTextColor || getBodyStyling()?.textColor || 'inherit') : (getBodyStyling()?.textColor || 'inherit')
                }}
              >
                {headerOrder > 0 && headerOrder === idx && <div id="public-header-slot" className="relative z-50 pb-8 sm:pb-10 lg:pb-12" />}
                {hasTopHeader && (
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4 sm:mb-6 md:mb-8">
                    {sectionTitle && (
                      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">{sectionTitle}</h2>
                    )}
                    {section.description && (
                      <p className="text-sm sm:text-base md:text-lg text-gray-600">{section.description}</p>
                    )}
                  </div>
                )}

                {/* Multiple Special Media items share one existing carousel. */}
                {hasMultipleSpecialMedia && hasFullMedia ? (
                  <SpecialMediaImageCarousel
                    images={specialMedia}
                    display="full"
                    getImageStyle={(media) => media.display === 'full' ? getFullMediaStyles(media) : getSpecialMediaStyles(media)}
                    getWrapperStyle={getFullMediaWrapperStyle}
                    onLocalVideoOpen={openSpecialMediaVideo}
                  />
                ) : (
                  <>
                  {/* Full-width media (display='full') */}
                  {!hasMultipleSpecialMedia && renderSpecialMediaImages(specialMedia.filter(m => m.display === 'full'), 'full')}
                  {!hasMultipleSpecialMedia && specialMedia.filter(m => m.display === 'full' && m.type === 'video').map(m => (
                    <div key={m.id} style={getFullMediaWrapperStyle(m)}>
                      {m.embedUrl || isExternalVideoUrl(m) ? <VideoUrlFrame video={m} className="w-full h-full" /> : (
                        <SpecialMediaVideoPreview media={m} onOpen={openSpecialMediaVideo} className="h-full w-full" />
                      )}
                    </div>
                  ))}
                  {!hasMultipleSpecialMedia && specialMedia.filter(m => m.display === 'full' && m.type === 'link' && m.url).map(m => (
                    <div key={m.id} style={getFullMediaWrapperStyle(m)}><WebsiteUrlCard url={m.url} className="w-full" /></div>
                  ))}
                  </>
                )}

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    {hasLeft && (
                      <aside className="flex flex-shrink-0 w-full md:w-[250px]">
                        <div className={`space-y-4 ${left.textStyle === 'bold' ? 'font-bold' : left.textStyle === 'slightly-bold' ? 'font-medium' : ''}`}>
                          {/* Render left sidebar media */}
                          {(left.media || []).map(m => (
                            <div key={m.id} className="relative w-full flex-shrink-0 rounded-lg bg-gray-100">
                              {m.type === 'image' ? (
                                <img src={m.url} alt={m.alt || ''} className="block w-full h-auto max-w-full object-contain" />
                              ) : m.type === 'link' ? (
                                <WebsiteUrlCard url={m.url} className="w-full" />
                              ) : m.embedUrl || isExternalVideoUrl(m) ? (
                                <VideoUrlFrame video={m} className="w-full" />
                              ) : (
                                <video className="block w-full h-auto max-w-full object-contain" autoPlay muted loop playsInline preload="metadata">
                                  <source src={m.url} type={getVideoMimeType(m.url)} />
                                  Your browser does not support video playback.
                                </video>
                              )}
                            </div>
                          ))}

                          {left.contentText && <div className="text-sm text-gray-700">{left.contentText}</div>}
                        </div>
                      </aside>
                    )}

                    {hasMiddle && (
                      <aside className="flex flex-shrink-0 w-full md:flex-1 md:min-w-0">
                        <div className={`space-y-4 w-full ${middle.textStyle === 'bold' ? 'font-bold' : middle.textStyle === 'slightly-bold' ? 'font-medium' : ''}`}>
                          {(middle.media || []).map(m => (
                            <div key={m.id} className="relative w-full flex-shrink-0 rounded-lg bg-gray-100">
                              {m.type === 'image' ? (
                                <img src={m.url} alt={m.alt || ''} className="block w-full h-auto max-w-full object-contain" />
                              ) : m.type === 'link' ? (
                                <WebsiteUrlCard url={m.url} className="w-full" />
                              ) : m.embedUrl || isExternalVideoUrl(m) ? (
                                <VideoUrlFrame video={m} className="w-full" />
                              ) : (
                                <video className="block w-full h-auto max-w-full object-contain" autoPlay muted loop playsInline preload="metadata">
                                  <source src={m.url} type={getVideoMimeType(m.url)} />
                                  Your browser does not support video playback.
                                </video>
                              )}
                            </div>
                          ))}
                          {middle.contentText && <div className="text-sm text-gray-700 whitespace-pre-wrap">{middle.contentText}</div>}
                        </div>
                      </aside>
                    )}

                    <div className="flex-1 relative w-full">
                      {/* Content-width media (display='content') rendered as content */}
                      {hasMultipleSpecialMedia && !hasFullMedia && (
                        <SpecialMediaImageCarousel
                          images={specialMedia}
                          display="content"
                          getImageStyle={getSpecialMediaStyles}
                          getWrapperStyle={getFullMediaWrapperStyle}
                          onLocalVideoOpen={openSpecialMediaVideo}
                        />
                      )}
                      {!hasMultipleSpecialMedia && renderSpecialMediaImages(specialMedia.filter(m => m.display === 'content'), 'content')}
                      {!hasMultipleSpecialMedia && (specialMedia.filter(m => m.display === 'content' && m.type === 'video') || []).map(m => (
                        <div key={m.id} className="mb-4">
                          {m.embedUrl || isExternalVideoUrl(m) ? <VideoUrlFrame video={m} /> : (
                            <SpecialMediaVideoPreview media={m} onOpen={openSpecialMediaVideo} className="min-h-[180px] w-full" />
                          )}
                        </div>
                      ))}
                      {!hasMultipleSpecialMedia && (specialMedia.filter(m => m.display === 'content' && m.type === 'link' && m.url) || []).map(m => (
                        <div key={m.id} className="mb-4"><WebsiteUrlCard url={m.url} /></div>
                      ))}

                      <div className="mb-8 relative z-10">
                        {innerContent}
                      </div>

                      {renderCTA(section)}
                    </div>

                    {hasRight && (
                      <aside className="flex flex-shrink-0 w-full md:w-[250px]">
                        <div className={`space-y-4 ${right.textStyle === 'bold' ? 'font-bold' : right.textStyle === 'slightly-bold' ? 'font-medium' : ''}`}>
                          {/* Render right sidebar media */}
                          {(right.media || []).map(m => (
                            <div key={m.id} className="relative w-full flex-shrink-0 rounded-lg bg-gray-100">
                              {m.type === 'image' ? (
                                <img src={m.url} alt={m.alt || ''} className="block w-full h-auto max-w-full object-contain" />
                              ) : m.type === 'link' ? (
                                <WebsiteUrlCard url={m.url} className="w-full" />
                              ) : m.embedUrl || isExternalVideoUrl(m) ? (
                                <VideoUrlFrame video={m} className="w-full" />
                              ) : (
                                <video className="block w-full h-auto max-w-full object-contain" autoPlay muted loop playsInline preload="metadata">
                                  <source src={m.url} type={getVideoMimeType(m.url)} />
                                  Your browser does not support video playback.
                                </video>
                              )}
                            </div>
                          ))}

                          {right.contentText && <div className="text-sm text-gray-700">{right.contentText}</div>}
                        </div>
                      </aside>
                    )}
                  </div>
                </div>
              </section>
            )
              })
            }
            return (
              <div className="py-16 text-center">
                <p className="text-gray-500 text-lg">No sections configured for this website yet.</p>
              </div>
            )
          })()
        )}
      </main>

      {itemDetailsOpen && selectedItem && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" 
          onClick={() => {
            console.log('[PublicWebsiteViewer] Closing item details modal')
            setItemDetailsOpen(false)
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">
                  {selectedItem.title || selectedItem.name || 'Item Details'}
                </h3>
                {selectedItem.description && (
                  <p className="text-gray-600 mt-1">{selectedItem.description}</p>
                )}
              </div>
              <button 
                onClick={() => {
                  console.log('[PublicWebsiteViewer] Close button clicked')
                  setItemDetailsOpen(false)
                }} 
                className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {selectedItemPreviewMedia && (
              <div className="mb-6 w-full h-[60vh] sm:h-[70vh] md:h-[80vh] rounded-lg overflow-hidden bg-black flex items-center justify-center p-2 sm:p-3">
                {selectedItemPreviewMedia.type === 'image' ? (
                  <img
                    src={selectedItemPreviewMedia.url}
                    alt={selectedItem.title || 'Item image'}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => console.error('[PublicWebsiteViewer] Failed to load selected item preview image:', e)}
                    style={{ cursor: 'default' }}
                    draggable={false}
                  />
                ) : selectedItemPreviewMedia.type === 'link' ? (
                  <WebsiteUrlCard url={selectedItemPreviewMedia.url} className="max-w-full" />
                ) : selectedItemPreviewMedia.type === 'video' && (selectedItemPreviewMedia.embedUrl || isExternalVideoUrl(selectedItemPreviewMedia)) ? (
                  <VideoUrlFrame video={selectedItemPreviewMedia} className="max-w-full max-h-full" />
                ) : selectedItemPreviewMedia.type === 'video' ? (
                  <video
                    ref={selectedPreviewVideoRef}
                    key={selectedItemPreviewMedia.url}
                    controls
                    poster={selectedItemPreviewVideoPoster || selectedItemPreviewMedia.poster || ''}
                    className="w-full h-full max-w-full max-h-full object-contain"
                    playsInline
                    autoPlay
                    muted
                    loop
                    preload="metadata"
                    onClick={(e) => e.stopPropagation()}
                    onLoadStart={() => {
                      console.log('[PublicWebsiteViewer] Video loading started:', selectedItemPreviewMedia.url)
                    }}
                    onCanPlay={() => {
                      console.log('[PublicWebsiteViewer] Video can play:', selectedItemPreviewMedia.url)
                    }}
                    onError={(e) => {
                      console.error('[PublicWebsiteViewer] Video playback error:', {
                        url: selectedItemPreviewMedia.url,
                        mimeType: getVideoMimeType(selectedItemPreviewMedia.url),
                        error: e.target.error
                      })
                    }}
                  >
                    <source src={selectedItemPreviewMedia.url} type={getVideoMimeType(selectedItemPreviewMedia.url)} />
                    Your browser does not support video playback.
                  </video>
                ) : null}
              </div>
            )}

            {selectedItem.gallery && selectedItem.gallery.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-6 mb-6">
                {selectedItem.gallery.map((g, i) => {
                  const previewUrl = g.image?.url || g.url || g.video?.url || g.videoUrl || getWebsiteUrlFromGalleryItem(g)
                  if (!previewUrl) return null
                  return (
                    <div
                      key={`${previewUrl}-${i}`}
                      className={`cursor-pointer rounded overflow-hidden border ${i === selectedItemGalleryIndex ? 'border-primary-600 ring-2 ring-primary-200' : 'border-gray-200'} bg-gray-100`}
                      onClick={() => {
                        console.log('[PublicWebsiteViewer] Gallery thumbnail clicked:', {
                          galleryIndex: i,
                          mediaUrl: previewUrl,
                          mediaType: (g.image?.url || g.url) ? 'image' : 'video',
                          itemId: selectedItem.id,
                          itemTitle: selectedItem.title
                        })
                        setSelectedItemGalleryIndex(i)
                      }}
                    >
                      {g.image?.url || g.url ? (
                        <img src={previewUrl} alt={`Gallery ${i + 1}`} className="w-full h-24 object-cover" />
                      ) : getWebsiteUrlFromGalleryItem(g) ? (
                        <WebsiteUrlCard url={getWebsiteUrlFromGalleryItem(g)} className="h-24 rounded-none" />
                        ) : (
                        <VideoThumbnail 
                          video={g.video || g.videoUrl || g} 
                          alt={`Gallery video ${i + 1}`} 
                          className="w-full h-24 object-cover pointer-events-none" 
                          missingFallbackClassName="w-full h-24 bg-gray-200 flex items-center justify-center text-xs text-gray-500"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )}
            
            {selectedItem.video && (
              <video
                key={getVideoUrl(selectedItem.video)}
                controls
                poster={getVideoPoster(selectedItem.video)}
                className="w-full h-auto rounded mb-4"
                playsInline
                autoPlay
                muted
                loop
                preload="metadata"
              >
                <source
                  src={typeof selectedItem.video === 'string' ? selectedItem.video : selectedItem.video?.url}
                  type={getVideoMimeType(typeof selectedItem.video === 'string' ? selectedItem.video : selectedItem.video?.url)}
                />
                Your browser does not support video playback.
              </video>
            )}

            <div className="flex flex-wrap gap-3 mt-6">
              {(selectedItem.whatsappContact || selectedItem.whatsappNumber) && (
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors duration-200 permanent-whatsapp-button"
                  onClick={() => {
                    console.log('[PublicWebsiteViewer] ✅ Selected item WhatsApp button clicked:', {
                      itemTitle: selectedItem.name || selectedItem.title,
                      itemId: selectedItem._id,
                      sectionType: 'section-items',
                      phoneNumber: selectedItem.whatsappContact || selectedItem.whatsappNumber,
                      timestamp: new Date().toISOString(),
                      buttonColor: '#10B981',
                      colorVerified: true,
                      styling: 'permanent green with !important flags',
                      deviceWidth: window.innerWidth,
                      deviceType: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop'
                    })
                    handleItemWhatsAppContact(selectedItem)
                  }}
                  style={{
                    backgroundColor: isHoveredWhatsAppModal ? '#059669' : '#10B981',
                    color: '#FFFFFF',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 600,
                    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    whiteSpace: 'nowrap',
                    transition: 'all 200ms'
                  }}
                  onMouseEnter={() => setIsHoveredWhatsAppModal(true)}
                  onMouseLeave={() => setIsHoveredWhatsAppModal(false)}
                >
                  <WhatsAppIcon className="w-4 h-4 whatsapp-icon" style={{ color: '#FFFFFF' }} />
                  Contact via WhatsApp
                </button>
              )}

              {selectedItem.price !== undefined && selectedItem.price !== null && selectedItem.price !== '' && (
                <span className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-lg font-semibold text-green-600">
                  {formatPrice(selectedItem.price, selectedItem.currency)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <footer
        className="py-8 mt-12 transition-colors"
        style={{
          backgroundColor: isFooterHovered ? (getFooterStyling()?.hoverBackgroundColor || '#1F2937') : (getFooterStyling()?.backgroundColor || '#111827'),
          color: isFooterHovered ? (getFooterStyling()?.hoverTextColor || '#F9FAFB') : (getFooterStyling()?.textColor || '#FFFFFF')
        }}
        onMouseEnter={() => setIsFooterHovered(true)}
        onMouseLeave={() => setIsFooterHovered(false)}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <div className="flex flex-wrap justify-center gap-3">
            {socialMediaLinks.map((link) => {
              const Icon = link.icon
              return (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors"
                  style={{
                    backgroundColor: `${isFooterHovered ? (getFooterStyling()?.hoverTextColor || '#F9FAFB') : (getFooterStyling()?.textColor || '#FFFFFF')}20`,
                    color: isFooterHovered ? (getFooterStyling()?.hoverTextColor || '#F9FAFB') : (getFooterStyling()?.textColor || '#FFFFFF')
                  }}
                  aria-label={link.name}
                >
                  <Icon className="w-5 h-5" />
                </a>
              )
            })}
          </div>
          <p>&copy; 2026 {website?.name || ''}. All rights reserved.</p>
          {/* <p className="text-gray-400 text-sm mt-2">Powered by MarketBook Solution</p> */}
        </div>
      </footer>

      <ReportContentModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        website={website}
        sectionFormStyling={getFormStyling()}
        useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
        sectionType="report"
      />

      <ImageGalleryModal
        isOpen={showGalleryModal}
        images={galleryItems}
        onClose={closeGalleryModal}
        startIndex={galleryStartIndex}
        sectionFormStyling={getFormStyling()}
        useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
        sectionType="image-gallery-modal"
      />

      {specialMediaVideo && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-3 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Special Media video"
          onClick={closeSpecialMediaVideo}
        >
          <div className="relative w-full max-w-5xl overflow-hidden rounded-lg bg-black shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              onClick={closeSpecialMediaVideo}
              className="absolute right-3 top-3 z-10 rounded-full bg-black/70 px-3 py-1 text-2xl leading-none text-white hover:bg-black"
              aria-label="Close video"
            >
              x
            </button>
            {specialMediaVideoLoading && !specialMediaVideoError && (
              <div className="absolute inset-0 z-[1] flex items-center justify-center text-sm text-white/80">Loading video...</div>
            )}
            {specialMediaVideoError ? (
              <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 px-6 text-center text-white">
                <p className="font-semibold">Video Preview Error</p>
                <p className="text-sm text-white/70">The video was uploaded, but it could not be played in this browser.</p>
                <button type="button" onClick={closeSpecialMediaVideo} className="rounded bg-white px-4 py-2 text-sm font-semibold text-gray-900">Close</button>
              </div>
            ) : (
              <video
                key={specialMediaVideo.url}
                src={specialMediaVideo.url}
                poster={specialMediaVideo.poster || specialMediaVideo.thumbnail || undefined}
                controls
                playsInline
                autoPlay
                className="max-h-[85vh] w-full object-contain"
                onLoadedData={() => setSpecialMediaVideoLoading(false)}
                onCanPlay={() => setSpecialMediaVideoLoading(false)}
                onError={() => {
                  setSpecialMediaVideoLoading(false)
                  setSpecialMediaVideoError('Unable to play video')
                }}
              />
            )}
          </div>
        </div>
      )}
      

      {/* Buyer Info Modal */}
      {showBuyerForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Enter Your Details</h3>
            <form onSubmit={(e) => { e.preventDefault(); buyTicket(); }}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={buyerInfo.name}
                  onChange={(e) => setBuyerInfo({ ...buyerInfo, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={buyerInfo.email}
                  onChange={(e) => setBuyerInfo({ ...buyerInfo, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="tel"
                  value={buyerInfo.phone}
                  onChange={(e) => setBuyerInfo({ ...buyerInfo, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={buyingTicket}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {buyingTicket ? 'Processing...' : 'Proceed to Payment'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowBuyerForm(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  )
}

export default function PublicWebsiteViewer({ prefetchedWebsite, prefetchedWebsiteError }) {
  return (
    <CartProvider>
      <PublicWebsiteViewerContent prefetchedWebsite={prefetchedWebsite} prefetchedWebsiteError={prefetchedWebsiteError} />
    </CartProvider>
  )
}




























// ..................................................................
// import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
// import { useParams, useNavigate, useLocation } from 'react-router-dom'
// import axios from 'axios'
// import toast from 'react-hot-toast'
// import { Helmet } from 'react-helmet-async'
// import { FiArrowLeft, FiFlag, FiShoppingCart, FiMenu, FiX } from 'react-icons/fi'
// import { FaInstagram, FaFacebook, FaTiktok, FaTwitter } from 'react-icons/fa'

// // Professional WhatsApp SVG Icon
// const WhatsAppIcon = ({ className }) => (
//   <svg
//     className={className}
//     viewBox="0 0 32 32"
//     fill="currentColor"
//     xmlns="http://www.w3.org/2000/svg"
//   >
//     <path d="M16 0C7.163 0 0 7.163 0 16c0 2.766.662 5.391 1.961 7.738L0 32l8.594-2.219C11.266 31.208 13.578 32 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.5c-2.062 0-4.084-.507-5.912-1.464l-.424-.254-4.391 1.137 1.159-4.237-.275-.436c-1.107-1.755-1.693-3.786-1.693-5.846 0-7.339 5.973-13.312 13.312-13.312 3.567 0 6.922 1.391 9.447 3.917 2.524 2.524 3.917 5.879 3.917 9.447 0 7.339-5.974 13.312-13.312 13.312zm7.251-9.945c-.396-.2-2.343-1.157-2.707-1.288-.364-.132-.631-.198-.896.199-.264.396-1.026 1.292-1.259 1.558-.233.265-.466.298-.862.099-.397-.199-1.676-.62-3.198-1.975-1.183-1.055-1.982-2.359-2.215-2.756-.233-.396-.024-.611.175-.809.18-.176.396-.466.595-.699.198-.233.264-.397.396-.661.132-.265.066-.497-.033-.697-.099-.199-.896-2.158-1.227-2.955-.324-.783-.654-.676-.896-.689-.232-.012-.496-.015-.759-.015-.264 0-.696.099-.96.497-.265.397-1.01 1.157-1.01 2.116 0 .959 1.034 2.459 1.175 2.623.14.165 1.988 3.048 4.818 4.275 1.349.574 2.407.737 3.261.947.682.176 1.302.151 1.793.092.546-.068 1.677-.685 1.913-1.348.237-.662.237-1.229.166-1.348-.07-.118-.268-.187-.664-.387z"/>
//   </svg>
// )
// import ReportContentModal from '../../components/public/ReportContentModal'
// import ImageGalleryModal from '../../components/public/ImageGalleryModal'
// import AudioPlayer from '../../components/public/AudioPlayer'
// import PublicProductsSection from '../../components/public/PublicProductsSection'

// const WHATSAPP_BUTTON_OVERRIDE_CSS = `
// button.permanent-whatsapp-button,
// button.permanent-whatsapp-button:hover,
// button.permanent-whatsapp-button:active,
// button.permanent-whatsapp-button:focus,
// button.permanent-whatsapp-button:visited,
// button.permanent-whatsapp-button:disabled {
//   background: #10B981 !important;
//   background-color: #10B981 !important;
//   background-image: none !important;
//   color: #FFFFFF !important;
//   border: 1px solid transparent !important;
//   box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3) !important;
//   outline: none !important;
//   text-decoration: none !important;
// }
// button.permanent-whatsapp-button:hover {
//   background: #059669 !important;
//   background-color: #059669 !important;
//   background-image: none !important;
// }
// button.permanent-whatsapp-button:active {
//   background: #047857 !important;
//   background-color: #047857 !important;
//   background-image: none !important;
// }
// button.permanent-whatsapp-button:focus {
//   background: #047857 !important;
//   background-color: #047857 !important;
//   background-image: none !important;
// }
// button.permanent-whatsapp-button:visited {
//   background: #10B981 !important;
//   background-color: #10B981 !important;
//   background-image: none !important;
// }
// button.permanent-whatsapp-button:disabled {
//   opacity: 0.65 !important;
//   cursor: not-allowed !important;
// }
// button.permanent-whatsapp-button .whatsapp-icon {
//   color: #FFFFFF !important;
// }
// `

// function normalizeSectionType(sectionType) {
//   return (sectionType || '')
//     .toString()
//     .trim()
//     .toLowerCase()
//     .replace(/\s+/g, '-')
//     .replace(/_/g, '-')
// }

// const getVideoMimeType = (url) => {
//   if (!url) return undefined
//   const extension = url.split('?')[0].split('.').pop().toLowerCase()
//   const mimeMap = {
//     mp4: 'video/mp4',
//     webm: 'video/webm',
//     ogg: 'video/ogg',
//     ogv: 'video/ogg',
//     mov: 'video/quicktime',
//     avi: 'video/x-msvideo',
//     mkv: 'video/x-matroska',
//     flv: 'video/x-flv',
//     wmv: 'video/x-ms-wmv',
//     m4v: 'video/x-m4v',
//     mpeg: 'video/mpeg',
//     mpg: 'video/mpeg',
//     '3gp': 'video/3gpp',
//     '3g2': 'video/3gpp2'
//   }
//   return mimeMap[extension]
// }

// const getVideoUrl = (video) => {
//   if (!video) return ''
//   if (typeof video === 'string') return video
//   return video.url || video.videoUrl || ''
// }

// const getVideoPoster = (video) => {
//   if (!video) return ''
//   if (typeof video === 'string') return ''
//   return video.thumbnail || video.poster || video.previewImage || video.image?.url || ''
// }

// import PublicTicketsSection from '../../components/public/PublicTicketsSection'
// import ShoppingCart from '../../components/public/ShoppingCart'
// import Checkout from '../../components/public/Checkout'
// import ContactUsForm from '../../components/public/ContactUsForm'
// import ReviewsSection from '../../components/public/ReviewsSection'
// import PublicFixturesSection from '../../components/public/PublicFixturesSection'
// import PublicPointsTable from '../../components/public/PublicPointsTable'
// import PublicResumeUpload from '../../components/public/PublicResumeUpload'
// import HeroCarousel from '../../components/public/HeroCarousel'
// import CryptoSection from '../../components/public/CryptoSection'
// import CustomerContactChats from '../../components/public/CustomerContactChats'
// import CustomerNotificationCenter from '../../components/public/CustomerNotificationCenter'
// import PaginationControls from '../../components/public/PaginationControls'
// import { CartProvider, useCart } from '../../context/CartContext'
// import { formatPrice } from '../../utils/currency'
// import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'
// import { buildPublicSiteSeo } from '../../utils/seo'

// // Inner component that uses cart context
// function PublicWebsiteViewerContent({ prefetchedWebsite, prefetchedWebsiteError }) {
//   const params = useParams()
//   const { slug, verificationId: paramsVerificationId } = params
//   const navigate = useNavigate()
//   const { cartItems, itemCount } = useCart()
//   const location = useLocation()
//   const effectiveSlug = slug || prefetchedWebsite?.slug

//   const [verificationId, setVerificationId] = useState(paramsVerificationId || '')
//   const [verificationResult, setVerificationResult] = useState(null)
//   const [verificationLoading, setVerificationLoading] = useState(false)
//   const [website, setWebsite] = useState(null)
//   const [sections, setSections] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(prefetchedWebsiteError || null)
//   const [ticketDetail, setTicketDetail] = useState(null)
//   const [ticketDetailError, setTicketDetailError] = useState(null)
//   const [reportModalOpen, setReportModalOpen] = useState(false)
//   const [currentView, setCurrentView] = useState('website')
//   const [customerActivity, setCustomerActivity] = useState([])
//   const [customerActivityLoading, setCustomerActivityLoading] = useState(false)
//   const [customerActivityError, setCustomerActivityError] = useState('')
//   const [customerActivityPage, setCustomerActivityPage] = useState(1)
//   const [customerActivityTotal, setCustomerActivityTotal] = useState(0)
//   const [accountSection, setAccountSection] = useState('profile')
//   const CUSTOMER_ACTIVITY_LIMIT = 10

//   // Gallery modal state for viewing all images/videos of an item
//   const [showGalleryModal, setShowGalleryModal] = useState(false)
//   const [galleryItems, setGalleryItems] = useState([])
//   const [galleryStartIndex, setGalleryStartIndex] = useState(0)
//   const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
//   const [buyingTicket, setBuyingTicket] = useState(false)
//   const [heroAspectRatios, setHeroAspectRatios] = useState({})
//   const [logoLoadError, setLogoLoadError] = useState(false)
//   const [logoRetryCount, setLogoRetryCount] = useState(0)
//   const [showBuyerForm, setShowBuyerForm] = useState(false)
//   const [buyerInfo, setBuyerInfo] = useState({ name: '', email: '', phone: '' })
//   const [selectedItem, setSelectedItem] = useState(null)
//   const [selectedItemGalleryIndex, setSelectedItemGalleryIndex] = useState(0)
//   const [itemDetailsOpen, setItemDetailsOpen] = useState(false)
//   const [isFooterHovered, setIsFooterHovered] = useState(false)
//   const [isHeaderHovered, setIsHeaderHovered] = useState(false)
//   const [isBodyHovered, setIsBodyHovered] = useState(false)
//   const [hoveredSectionId, setHoveredSectionId] = useState(null)
//   const [hoveredCtaId, setHoveredCtaId] = useState(null)
//   const [customerAuthOpen, setCustomerAuthOpen] = useState(false)
//   const [customerAuthMode, setCustomerAuthMode] = useState('login')
//   const [customerVerificationNotice, setCustomerVerificationNotice] = useState(null)
//   const [selectedConversationIdFromNotification, setSelectedConversationIdFromNotification] = useState(null)
//   const [customerAuthForm, setCustomerAuthForm] = useState({
//     firstName: '',
//     lastName: '',
//     email: '',
//     phone: '',
//     password: '',
//     confirmPassword: ''
//   })
//   const [customerAuthLoading, setCustomerAuthLoading] = useState(false)
//   const [customerAuthMessage, setCustomerAuthMessage] = useState('')
//   const [customerAuthError, setCustomerAuthError] = useState('')
//   const [customerAuthToken, setCustomerAuthToken] = useState('')
//   const [customerAuthUser, setCustomerAuthUser] = useState(null)
//   const [customerProfileForm, setCustomerProfileForm] = useState({
//     firstName: '',
//     lastName: '',
//     email: '',
//     phone: '',
//     shippingAddress: '',
//     shippingCity: '',
//     shippingState: '',
//     shippingZip: '',
//     billingAddress: '',
//     billingCity: '',
//     billingState: '',
//     billingZip: ''
//   })
//   const [profileSaving, setProfileSaving] = useState(false)
//   const [resendingVerification, setResendingVerification] = useState(false)

//   const customerAuthEnabled = useMemo(() => {
//     return Boolean(website?.customerAuth?.enabled || website?.customerAuthConfig?.enabled)
//   }, [website?.customerAuth, website?.customerAuthConfig])

//   const hasCartFeature = useMemo(() => {
//     const normalizeType = (section) => (section?.sectionType || section?.type || '').toString().trim().toLowerCase()

//     const isCartRelated = (section) => {
//       const type = normalizeType(section)
//       return ['cart', 'checkout', 'products', 'tickets'].includes(type)
//     }

//     if (Array.isArray(sections) && sections.some(isCartRelated)) return true
//     if (Array.isArray(website?.sections) && website.sections.some(isCartRelated)) return true
//     return false
//   }, [sections, website?.sections])

//   const closeGalleryModal = useCallback(() => setShowGalleryModal(false), [])

//   const resetCustomerAuthForm = () => {
//     setCustomerAuthForm({
//       firstName: '',
//       lastName: '',
//       email: '',
//       phone: '',
//       password: '',
//       confirmPassword: ''
//     })
//   }

//   const openCustomerAuthModal = (mode) => {
//     setCustomerAuthMode(mode)
//     setCustomerAuthError('')
//     setCustomerAuthMessage('')
//     resetCustomerAuthForm()
//     setCustomerAuthOpen(true)
//   }

//   const closeCustomerAuthModal = () => {
//     setCustomerAuthOpen(false)
//     setCustomerAuthError('')
//     setCustomerAuthMessage('')
//   }

//   const handleCustomerAuthFormChange = (e) => {
//     const { name, value } = e.target
//     setCustomerAuthForm((prev) => ({ ...prev, [name]: value }))
//   }

//   const getCustomerAuthStorageKey = () => {
//     if (!website?._id) return null
//     return `customerAuthToken:${website._id}`
//   }

//   useEffect(() => {
//     const params = new URLSearchParams(location.search)
//     const verificationStatus = params.get('customerVerificationStatus')
//     const verificationMessage = params.get('customerVerificationMessage')

//     if (verificationStatus || verificationMessage) {
//       setCustomerVerificationNotice({
//         type: verificationStatus === 'success' ? 'success' : 'error',
//         message: verificationMessage || 'Verification request completed.'
//       })
//     }
//   }, [location.search])

//   useEffect(() => {
//     if (!website?._id) return
//     const tokenKey = getCustomerAuthStorageKey()
//     const storedToken = tokenKey ? localStorage.getItem(tokenKey) : null
//     if (!storedToken) return

//     const verifyCustomerAuth = async () => {
//       try {
//         const response = await axios.get('/customer-auth/me', {
//           headers: {
//             Authorization: `Bearer ${storedToken}`
//           }
//         })

//         setCustomerAuthToken(storedToken)
//         setCustomerAuthUser(response.data.customer)
//         setCustomerProfileForm({
//           firstName: response.data.customer?.firstName || '',
//           lastName: response.data.customer?.lastName || '',
//           email: response.data.customer?.email || '',
//           phone: response.data.customer?.phone || '',
//           shippingAddress: response.data.customer?.shippingAddress || '',
//           shippingCity: response.data.customer?.shippingCity || '',
//           shippingState: response.data.customer?.shippingState || '',
//           shippingZip: response.data.customer?.shippingZip || '',
//           billingAddress: response.data.customer?.billingAddress || '',
//           billingCity: response.data.customer?.billingCity || '',
//           billingState: response.data.customer?.billingState || '',
//           billingZip: response.data.customer?.billingZip || ''
//         })
//       } catch (error) {
//         console.warn('[PublicWebsiteViewer] Stored customer auth token invalid, clearing it', error?.response?.data || error?.message)
//         localStorage.removeItem(tokenKey)
//         setCustomerAuthToken('')
//         setCustomerAuthUser(null)
//       }
//     }

//     verifyCustomerAuth()
//   }, [website?._id])

//   const handleCustomerProfileChange = (e) => {
//     const { name, value } = e.target
//     setCustomerProfileForm((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleCustomerProfileSave = async (e) => {
//     e.preventDefault()
//     if (!customerAuthToken) return
//     setProfileSaving(true)
//     try {
//       const response = await axios.put('/customer-profile/me', customerProfileForm, {
//         headers: { Authorization: `Bearer ${customerAuthToken}` }
//       })
//       setCustomerAuthUser(response.data.customer)
//       setCustomerProfileForm({
//         firstName: response.data.customer?.firstName || '',
//         lastName: response.data.customer?.lastName || '',
//         email: response.data.customer?.email || '',
//         phone: response.data.customer?.phone || '',
//         shippingAddress: response.data.customer?.shippingAddress || '',
//         shippingCity: response.data.customer?.shippingCity || '',
//         shippingState: response.data.customer?.shippingState || '',
//         shippingZip: response.data.customer?.shippingZip || '',
//         billingAddress: response.data.customer?.billingAddress || '',
//         billingCity: response.data.customer?.billingCity || '',
//         billingState: response.data.customer?.billingState || '',
//         billingZip: response.data.customer?.billingZip || ''
//       })
//       toast.success('Profile updated successfully')
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Unable to save profile')
//     } finally {
//       setProfileSaving(false)
//     }
//   }

//   const handleCustomerAuthSubmit = async (event) => {
//     event.preventDefault()
//     if (!website?._id) {
//       setCustomerAuthError('Website information is missing.')
//       return
//     }

//     setCustomerAuthLoading(true)
//     setCustomerAuthError('')
//     setCustomerAuthMessage('')

//     try {
//       let endpoint = '/customer-auth/login'
//       let payload = {
//         websiteId: website._id,
//         email: customerAuthForm.email?.trim(),
//         password: customerAuthForm.password
//       }

//       if (customerAuthMode === 'register') {
//         endpoint = '/customer-auth/register'
//         payload = {
//           websiteId: website._id,
//           firstName: customerAuthForm.firstName.trim(),
//           lastName: customerAuthForm.lastName.trim(),
//           email: customerAuthForm.email.trim(),
//           phone: customerAuthForm.phone.trim(),
//           password: customerAuthForm.password,
//           confirmPassword: customerAuthForm.confirmPassword
//         }
//       }

//       if (customerAuthMode === 'forgot') {
//         endpoint = '/customer-auth/forgot-password'
//         payload = {
//           websiteId: website._id,
//           email: customerAuthForm.email.trim()
//         }
//       }

//       const response = await axios.post(endpoint, payload)

//       if (customerAuthMode === 'login') {
//         const token = response.data.token
//         if (token) {
//           const tokenKey = getCustomerAuthStorageKey()
//           if (tokenKey) {
//             localStorage.setItem(tokenKey, token)
//           }
//           setCustomerAuthToken(token)
//           setCustomerAuthUser(response.data.customer)
//           setCustomerProfileForm({
//             firstName: response.data.customer?.firstName || '',
//             lastName: response.data.customer?.lastName || '',
//             email: response.data.customer?.email || '',
//             phone: response.data.customer?.phone || '',
//             shippingAddress: response.data.customer?.shippingAddress || '',
//             shippingCity: response.data.customer?.shippingCity || '',
//             shippingState: response.data.customer?.shippingState || '',
//             shippingZip: response.data.customer?.shippingZip || '',
//             billingAddress: response.data.customer?.billingAddress || '',
//             billingCity: response.data.customer?.billingCity || '',
//             billingState: response.data.customer?.billingState || '',
//             billingZip: response.data.customer?.billingZip || ''
//           })
//           setCustomerAuthMessage('You are now logged in.')
//           setCustomerAuthOpen(false)
//         } else {
//           setCustomerAuthMessage(response.data.message || 'Login completed.')
//         }
//       } else {
//         setCustomerAuthMessage(response.data.message || 'Request completed successfully.')
//         if (customerAuthMode === 'register') {
//           if (!response.data.requireEmailVerification) {
//             setCustomerAuthMode('login')
//           }
//         }
//       }
//     } catch (error) {
//       const message = error.response?.data?.message || error.message || 'Authentication failed.'
//       setCustomerAuthError(message)
//     } finally {
//       setCustomerAuthLoading(false)
//     }
//   }

//   const handleResendVerification = async () => {
//     if (!customerAuthToken || !website?._id) return

//     try {
//       setResendingVerification(true)
//       setCustomerAuthError('')
//       setCustomerAuthMessage('')
//       const response = await axios.post('/customer-auth/resend-verification', {
//         websiteId: website._id,
//         email: customerAuthUser?.email
//       }, {
//         headers: {
//           Authorization: `Bearer ${customerAuthToken}`
//         }
//       })
//       setCustomerAuthMessage(response.data.message || 'Verification email resent successfully.')
//     } catch (error) {
//       const message = error.response?.data?.message || 'Verification email could not be sent. Please try again.'
//       setCustomerAuthError(message)
//     } finally {
//       setResendingVerification(false)
//     }
//   }

//   const handleCustomerAuthLogout = async () => {
//     if (!customerAuthToken) {
//       setCustomerAuthUser(null)
//       return
//     }

//     try {
//       await axios.post('/customer-auth/logout', null, {
//         headers: {
//           Authorization: `Bearer ${customerAuthToken}`
//         }
//       })
//     } catch (error) {
//       console.warn('[PublicWebsiteViewer] Logout request failed', error?.response?.data || error?.message)
//     }

//     const tokenKey = getCustomerAuthStorageKey()
//     if (tokenKey) {
//       localStorage.removeItem(tokenKey)
//     }
//     setCustomerAuthToken('')
//     setCustomerAuthUser(null)
//     setCustomerActivity([])
//     setCustomerActivityPage(1)
//     setCustomerActivityTotal(0)
//     setCustomerActivityError('')
//     setCustomerAuthMessage('You have been logged out.')
//   }

//   const handleOpenConversationFromNotification = (conversationId) => {
//     setAccountSection('messages')
//     setSelectedConversationIdFromNotification(conversationId)
//   }

//   const fetchCustomerActivity = useCallback(async (page = 1) => {
//     if (!customerAuthToken || !website?._id) return

//     setCustomerActivityLoading(true)
//     setCustomerActivityError('')

//     try {
//       const response = await axios.get('/customer-auth/activity', {
//         headers: {
//           Authorization: `Bearer ${customerAuthToken}`
//         },
//         params: {
//           page,
//           limit: CUSTOMER_ACTIVITY_LIMIT
//         }
//       })

//       setCustomerActivity(response.data.activities || [])
//       setCustomerActivityPage(page)
//       setCustomerActivityTotal(response.data.pagination?.total || 0)
//     } catch (error) {
//       const message = error.response?.data?.message || error.message || 'Unable to load activity history'
//       setCustomerActivityError(message)
//       if (error.response?.status === 401) {
//         const tokenKey = getCustomerAuthStorageKey()
//         if (tokenKey) localStorage.removeItem(tokenKey)
//         setCustomerAuthToken('')
//         setCustomerAuthUser(null)
//       }
//     } finally {
//       setCustomerActivityLoading(false)
//     }
//   }, [customerAuthToken, website?._id])

//   useEffect(() => {
//     if (!customerAuthUser) return
//     if (currentView !== 'account') return

//     fetchCustomerActivity(customerActivityPage)
//   }, [customerAuthUser, currentView, customerActivityPage, fetchCustomerActivity])

//   const buyTicket = async () => {
//     if (!ticketDetail || !website || !buyerInfo.email) return
    
//     setBuyingTicket(true)
//     try {
//       // Determine payment gateway
//       const paymentGateway = website?.paymentSettings?.paymentGateway || 'paystack'
//       const endpoint = `/ticket-payments/${paymentGateway}/checkout`
      
//       const response = await axios.post(endpoint, {
//         websiteId: website._id,
//         ticketSlug: ticketDetail.slug,
//         quantity: 1,
//         buyerEmail: buyerInfo.email,
//         buyerName: buyerInfo.name,
//         buyerPhone: buyerInfo.phone
//       })
      
//       const { paymentUrl } = response.data
//       if (paymentUrl) {
//         window.location.href = paymentUrl
//       } else {
//         toast.error('Payment URL not received')
//       }
//     } catch (err) {
//       console.error('Ticket purchase error:', err)
//       toast.error(err.response?.data?.message || 'Failed to initiate ticket purchase')
//     } finally {
//       setBuyingTicket(false)
//       setShowBuyerForm(false)
//     }
//   }

//   const formatWhatsAppNumber = (raw) => {
//     if (!raw) return ''
//     const cleaned = raw.toString().replace(/[^0-9]/g, '')
//     return cleaned
//   }

//   const formatSocialUrl = (url) => {
//     if (!url || typeof url !== 'string') return ''
//     const trimmed = url.trim()
//     if (!trimmed) return ''
//     if (/^(https?:)?\/\//i.test(trimmed)) return trimmed.startsWith('http') ? trimmed : `https:${trimmed}`
//     return `https://${trimmed}`
//   }

//   const getItemWhatsAppMessage = (item) => {
//     if (!item) return ''

//     let messageParts = [
//       `Hi! I'm interested in this item:`,
//       `Title: ${item.title || item.name || 'N/A'}`,
//       `Description: ${item.description || 'N/A'}`
//     ]

//     // Include price if available
//     if (item.price !== undefined && item.price !== null && item.price !== '') {
//       const formattedPrice = formatPrice(item.price, item.currency)
//       messageParts.push(`Price: ${formattedPrice}`)
//     }

//     if (item.link) {
//       messageParts.push(`Link: ${item.link}`)
//     }

//     // Handle gallery items with better URL management
//     if (item.gallery && item.gallery.length > 0) {
//       const externalUrls = []
      
//       item.gallery.forEach((g, i) => {
//         const mediaUrl = g.image?.url || g.url || g.video?.url || g.videoUrl
//         // Separate base64 from external URLs
//         if (mediaUrl && !mediaUrl.startsWith('data:')) {
//           externalUrls.push(`${i + 1}. ${mediaUrl}`)
//         }
//       })
      
//       // Add gallery header
//       messageParts.push(`Gallery (${item.gallery.length} items):`)
      
//       // Include external URLs if available
//       if (externalUrls.length > 0) {
//         externalUrls.forEach(url => messageParts.push(url))
//       } else {
//         // If only base64 images, mention they're available on website
//         messageParts.push(`Images/videos are available for preview on the website`)
//       }
//     }

//     // Include any other shallow key values to supplement details
//     const otherKeys = Object.keys(item).filter(key => !['title', 'name', 'description', 'price', 'currency', 'link', 'gallery', 'image', 'video', 'whatsappContact', 'whatsappNumber', 'id'].includes(key))
//     otherKeys.forEach((key) => {
//       const value = item[key]
//       if (value && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
//         messageParts.push(`${key}: ${value}`)
//       }
//     })

//     messageParts.push('')
//     messageParts.push('Please provide more information and availability.')

//     return messageParts.join('\n')
//   }

//   const handleItemWhatsAppContact = (item) => {
//     const rawNumber = item.whatsappContact || item.whatsappNumber
//     const number = formatWhatsAppNumber(rawNumber)
//     if (!number) return

//     const message = getItemWhatsAppMessage(item)
//     const whatsappUrl = `https://wa.me/${number}?text=${encodeURIComponent(message)}`
    
//     // Get button styling info for logging
//     const screenWidth = window.innerWidth
//     const buttonStyle = {
//       backgroundColor: '#10B981',
//       color: '#FFFFFF',
//       hasImportant: true
//     }
    
//     window.open(whatsappUrl, '_blank')
    
//     console.log('[PublicWebsiteViewer] Section item WhatsApp button clicked:', {
//       itemTitle: item.name || item.title,
//       itemId: item._id,
//       phoneNumber: number,
//       timestamp: new Date().toISOString(),
//       buttonColor: 'permanent green (#10B981)',
//       styling: 'inline with !important flags for override',
//       deviceType: screenWidth < 768 ? 'mobile' : screenWidth < 1024 ? 'tablet' : 'desktop',
//       screenWidth: screenWidth
//     })
    
//     // Log WhatsApp interaction to backend with comprehensive styling verification
//     try {
//       fetch('/websites-public/log/whatsapp-contact', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           websiteId: website?._id,
//           websiteSlug: website?.publicUrl,
//           sectionType: 'section-items',
//           itemName: item.name || item.title,
//           itemId: item._id,
//           phoneNumber: number,
//           timestamp: new Date().toISOString(),
//           buttonClass: 'permanent-whatsapp-button',
//           buttonStyling: buttonStyle,
//           buttonState: 'default',
//           deviceType: screenWidth < 768 ? 'mobile' : screenWidth < 1024 ? 'tablet' : 'desktop',
//           screenWidth: screenWidth
//         })
//       }).catch(err => console.warn('[PublicWebsiteViewer] Failed to log WhatsApp contact:', err.message))
//     } catch (err) {
//       console.warn('[PublicWebsiteViewer] Error logging WhatsApp contact:', err.message)
//     }
//   }

//   const socialMediaLinks = [
//     {
//       name: 'whatsapp',
//       href: formatSocialUrl(website?.socialLinks?.whatsapp),
//       icon: WhatsAppIcon
//     },
//     {
//       name: 'instagram',
//       href: formatSocialUrl(website?.socialLinks?.instagram),
//       icon: FaInstagram
//     },
//     {
//       name: 'facebook',
//       href: formatSocialUrl(website?.socialLinks?.facebook),
//       icon: FaFacebook
//     },
//     {
//       name: 'tiktok',
//       href: formatSocialUrl(website?.socialLinks?.tiktok),
//       icon: FaTiktok
//     },
//     {
//       name: 'twitter',
//       href: formatSocialUrl(website?.socialLinks?.twitter),
//       icon: FaTwitter
//     }
//   ].filter(link => link.href)

//   // Fetch public website data by slug or use prefetchedWebsite if provided
//   useEffect(() => {
//     let mounted = true

//     const fetchWebsite = async () => {
//       try {
//         setError(null)

//         if (prefetchedWebsite) {
//           if (!mounted) return
//           setWebsite(prefetchedWebsite)
//           setSections((prefetchedWebsite && prefetchedWebsite.sections) || [])
//           setLoading(false)
//           console.log('[PublicWebsiteViewer] Using prefetched website data:', {
//             slug: prefetchedWebsite?.slug,
//             websiteId: prefetchedWebsite?._id,
//             sections: (prefetchedWebsite?.sections || []).length,
//             paymentGateway: prefetchedWebsite?.paymentSettings?.paymentGateway
//           })
//           console.log('[PublicWebsiteViewer] ⭐ STYLING CONFIGURATION (PREFETCHED):', {
//             useDefaultStyling: prefetchedWebsite?.useDefaultStyling,
//             useDefaultStylingType: typeof prefetchedWebsite?.useDefaultStyling,
//             willUseDefaults: prefetchedWebsite?.useDefaultStyling !== false
//           })
//           console.log('[PublicWebsiteViewer] Prefetched website logo data:', prefetchedWebsite?.logo)

//           if (prefetchedWebsite?.slug) {
//             try {
//               const response = await axios.get(`/websites-public/${prefetchedWebsite.slug}`)
//               if (!mounted) return
//               const w = response.data.website
//               if (w) {
//                 setWebsite(w)
//                 setSections((w && w.sections) || [])
//                 console.log('[PublicWebsiteViewer] Refreshed prefetched website from backend:', { slug: w?.slug, websiteId: w?._id, sections: (w?.sections || []).length })
//               }
//             } catch (refreshError) {
//               console.warn('[PublicWebsiteViewer] Failed to refresh prefetched website:', refreshError.response?.data || refreshError.message)
//               if (mounted && refreshError.response?.status === 404) {
//                 const message = refreshError.response?.data?.message || 'Website not found or not published'
//                 setError(message)
//                 setWebsite(null)
//                 setSections([])
//               }
//             }
//           }

//           return
//         }

//         if (!effectiveSlug) return

//         setLoading(true)
//         const response = await axios.get(`/websites-public/${effectiveSlug}`)
//         const w = response.data.website
//         if (!mounted) return
//         setWebsite(w)
//         setSections((w && w.sections) || [])
//         console.log('[PublicWebsiteViewer] Website loaded:', { slug: w?.slug, websiteId: w?._id, sections: (w?.sections || []).length, paymentGateway: w?.paymentSettings?.paymentGateway })
//         console.log('[PublicWebsiteViewer] ⭐ STYLING CONFIGURATION (FETCHED):', {
//           useDefaultStyling: w?.useDefaultStyling,
//           useDefaultStylingType: typeof w?.useDefaultStyling,
//           willUseDefaults: w?.useDefaultStyling !== false,
//           hasCustomHeader: !!w?.headerStyling,
//           hasCustomFooter: !!w?.footerStyling,
//           hasCustomBody: !!w?.bodyStyling,
//           hasCustomForm: !!w?.sectionFormStyling
//         })
//         console.log('[PublicWebsiteViewer] Website logo data:', w?.logo)
//       } catch (err) {
//         console.error('[PublicWebsiteViewer] Failed to load website:', err)
//         const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load website'
//         if (mounted) {
//           setError(msg)
//           setWebsite(null)
//           setSections([])
//         }
//       } finally {
//         if (mounted && !prefetchedWebsite) setLoading(false)
//       }
//     }

//     fetchWebsite()

//     return () => { mounted = false }
//   }, [effectiveSlug, prefetchedWebsite])

//   // Refetch website data when page becomes visible (user returns to tab)
//   useEffect(() => {
//     const handleVisibilityChange = () => {
//       if (!document.hidden && website && effectiveSlug) {
//         console.log('[PublicWebsiteViewer] Page became visible, refetching website data')
//         // Refetch website data
//         const refetchWebsite = async () => {
//           try {
//             const response = await axios.get(`/websites-public/${effectiveSlug}`)
//             const w = response.data.website
//             setWebsite(w)
//             setSections((w && w.sections) || [])
//             console.log('[PublicWebsiteViewer] Website refetched:', { slug: w?.slug, websiteId: w?._id, sections: (w?.sections || []).length })
//           } catch (err) {
//             console.error('[PublicWebsiteViewer] Failed to refetch website:', err)
//             if (err.response?.status === 404) {
//               const message = err.response?.data?.message || 'Website not found or not published'
//               setError(message)
//               setWebsite(null)
//               setSections([])
//             }
//           }
//         }
//         refetchWebsite()
//       }
//     }

//     document.addEventListener('visibilitychange', handleVisibilityChange)
//     return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
//   }, [website, effectiveSlug])

//   // Determine verificationId when the route or path changes (supports /store/:slug/verify/:id and custom domain /verify/:id)
//   useEffect(() => {
//     if (paramsVerificationId) {
//       setVerificationId(paramsVerificationId)
//     } else if (location.pathname.startsWith('/verify/')) {
//       setVerificationId(location.pathname.replace('/verify/', ''))
//     } else {
//       setVerificationId('')
//       setVerificationResult(null)
//     }
//   }, [paramsVerificationId, location.pathname])

//   // Fetch QR verification result when verificationId is present
//   useEffect(() => {
//     if (!verificationId) {
//       setVerificationResult(null)
//       return
//     }

//     const verifyCard = async () => {
//       setVerificationLoading(true)
//       try {
//         const response = await axios.get(`/cards/verify/${verificationId}`)
//         if (response.data.success) {
//           setVerificationResult(response.data.verification)
//         } else {
//           setVerificationResult({ error: response.data.message || 'Verification failed' })
//         }
//       } catch (error) {
//         const message = error.response?.data?.message || error.message || 'Verification failed'
//         setVerificationResult({ error: message })
//       } finally {
//         setVerificationLoading(false)
//       }
//     }

//     verifyCard()
//   }, [verificationId])

//   // Set meta title, description, and favicon
//   useEffect(() => {
//     const currentWebsite = website || prefetchedWebsite
//     console.log('[PublicWebsiteViewer] Setting meta data for website:', currentWebsite?.name, 'prefetched:', !!prefetchedWebsite)
//     if (currentWebsite) {
//       try {
//         document.title = currentWebsite.seo?.metaTitle || currentWebsite.name || 'Website'
//         const metaDesc = document.querySelector('meta[name="description"]')
//         if (metaDesc) {
//           metaDesc.content = currentWebsite.seo?.metaDescription || currentWebsite.description || ''
//         }

//         // Set favicon using uploaded logo
//         if (currentWebsite.logo?.url) {
//           const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//           const rawLogoUrl = currentWebsite.logo.url
//           let faviconSrc = rawLogoUrl

//           // Convert relative URLs to absolute
//           if (!rawLogoUrl.startsWith('http') && !rawLogoUrl.startsWith('data:') && !rawLogoUrl.startsWith('blob:')) {
//             const pathUrl = rawLogoUrl.startsWith('/') ? rawLogoUrl : `/${rawLogoUrl}`
//             faviconSrc = backendOrigin ? `${backendOrigin}${pathUrl}` : pathUrl
//           }

//           const faviconLink = document.querySelector('link[rel="icon"]')
//           if (faviconLink && faviconSrc) {
//             faviconLink.href = faviconSrc
//             console.log('[PublicWebsiteViewer] Updated favicon:', faviconSrc)
//           }
//         }
//       } catch (error) {
//         console.error('[PublicWebsiteViewer] Error setting meta data:', error)
//       }
//     }
//   }, [prefetchedWebsite, website])

//   // Log styling configuration when website changes
//   useEffect(() => {
//     const currentWebsite = website || prefetchedWebsite
//     if (currentWebsite) {
//       const useDefault = currentWebsite?.useDefaultStyling !== false
//       console.log('⭐ [STYLING-CONFIG] Website styling configuration updated:', {
//         websiteId: currentWebsite?._id,
//         slug: currentWebsite?.slug,
//         useDefaultStyling: currentWebsite?.useDefaultStyling,
//         willUseDefaults: useDefault,
//         message: useDefault ? '✅ DEFAULT design will be applied' : '🎨 CUSTOM design will be applied',
//         timestamp: new Date().toISOString()
//       })
//     }
//   }, [prefetchedWebsite, website])

//   // Ticket detail by query param
//   useEffect(() => {
//     const query = new URLSearchParams(location.search)
//     const ticketSlug = query.get('ticket')

//     if (!ticketSlug || !website) {
//       setTicketDetail(null)
//       setTicketDetailError(null)
//       return
//     }

//     const fetchTicketDetail = async () => {
//       try {
//         let response;
//         if (slug) {
//           // Regular domain - use slug-based route
//           response = await axios.get(`/event-tickets/store/${slug}/${ticketSlug}`)
//         } else if (website?._id) {
//           // Custom domain - use website ID-based route
//           response = await axios.get(`/event-tickets/website-public/${website._id}/${ticketSlug}`)
//         }
//         setTicketDetail(response.data.ticket)
//         setTicketDetailError(null)
//       } catch (err) {
//         console.error('[PublicWebsiteViewer] fetchTicketDetail', err)
//         setTicketDetail(null)
//         setTicketDetailError(err.response?.data?.message || 'Unable to load ticket details')
//       }
//     }

//     fetchTicketDetail()
//   }, [location.search, website])

//   const handleOpenGallery = useCallback((item, startIndex = 0) => {
//     if (!item) return
//     let imgs = []
//     if (item.gallery && item.gallery.length > 0) {
//       imgs = item.gallery.filter((g) => (g.image?.url || g.url || g.video?.url || g.videoUrl))
//     } else if (item.image) {
//       const imageUrl = typeof item.image === 'string' ? item.image : item.image?.url
//       if (imageUrl) imgs.push({ image: { url: imageUrl } })
//     } else if (item.video) {
//       const videoUrl = typeof item.video === 'string' ? item.video : item.video?.url
//       if (videoUrl) imgs.push({ video: { url: videoUrl } })
//     }
//     if (imgs.length === 0) return
//     setGalleryItems(imgs)
//     setGalleryStartIndex(startIndex || 0)
//     setShowGalleryModal(true)
//   }, [])

//   const handleHeroImageLoad = (sectionId, naturalWidth, naturalHeight) => {
//     if (!sectionId || !naturalWidth || !naturalHeight) return

//     setHeroAspectRatios((prev) => ({
//       ...prev,
//       [sectionId]: naturalHeight / naturalWidth,
//     }))
//   }

//   const handleViewItem = useCallback((item, startIndex = 0) => {
//     if (!item) {
//       console.warn('[PublicWebsiteViewer] handleViewItem called with empty item')
//       return
//     }
//     const normalizedStartIndex = Number.isFinite(startIndex) ? Math.max(0, Math.floor(startIndex)) : 0
//     console.log('[PublicWebsiteViewer] Opening item details modal for:', { title: item.title, id: item.id, startIndex: normalizedStartIndex })
//     setSelectedItem(item)
//     setSelectedItemGalleryIndex(normalizedStartIndex)
//     setItemDetailsOpen(true)
//   }, [])

//   // Prepare preview media for the selected item modal
//   const selectedItemPreviewMedia = useMemo(() => {
//     if (!selectedItem) return null

//     // Prefer gallery media when a gallery exists, so clicking thumbnails updates the preview immediately.
//     if (Array.isArray(selectedItem.gallery) && selectedItem.gallery.length > 0) {
//       const idx = Math.min(Math.max(0, selectedItemGalleryIndex || 0), selectedItem.gallery.length - 1)
//       console.log('[selectedItemPreviewMedia] Gallery preview:', {
//         itemId: selectedItem.id,
//         selectedIndex: selectedItemGalleryIndex,
//         normalizedIndex: idx,
//         galleryLength: selectedItem.gallery.length,
//         selectedMediaStructure: selectedItem.gallery[idx]
//       })

//       const selectedMedia = selectedItem.gallery[idx]
//       if (selectedMedia) {
//         const galleryVideo = getVideoUrl(selectedMedia.video || selectedMedia.videoUrl)
//         const galleryPoster = getVideoPoster(selectedMedia.video || selectedMedia.videoUrl)
//         const galleryImage = selectedMedia.image?.url || selectedMedia.url

//         console.log('[selectedItemPreviewMedia] Selected media analysis:', {
//           hasImage: !!galleryImage,
//           hasVideo: !!galleryVideo,
//           galleryImage,
//           galleryVideo
//         })

//         if (galleryVideo) {
//           console.log('[selectedItemPreviewMedia] Returning gallery video:', galleryVideo)
//           return { type: 'video', url: galleryVideo, poster: galleryPoster }
//         }

//         if (galleryImage) {
//           console.log('[selectedItemPreviewMedia] Returning gallery image:', galleryImage)
//           return { type: 'image', url: galleryImage }
//         }
//       }

//       console.log('[selectedItemPreviewMedia] Selected item gallery has no valid media, using fallback')
//       const fallback = selectedItem.gallery[0]
//       const fallbackVideo = getVideoUrl(fallback?.video || fallback?.videoUrl)
//       const fallbackPoster = getVideoPoster(fallback?.video || fallback?.videoUrl)
//       if (fallbackVideo) {
//         console.log('[selectedItemPreviewMedia] Fallback video:', fallbackVideo)
//         return { type: 'video', url: fallbackVideo, poster: fallbackPoster }
//       }
//       const fallbackImage = fallback?.image?.url || fallback?.url
//       if (fallbackImage) {
//         console.log('[selectedItemPreviewMedia] Fallback image:', fallbackImage)
//         return { type: 'image', url: fallbackImage }
//       }
//     }

//     // Direct image fallback
//     if (selectedItem.image) {
//       const imageUrl = typeof selectedItem.image === 'string' ? selectedItem.image : selectedItem.image?.url
//       if (imageUrl) {
//         console.log('[selectedItemPreviewMedia] Selected item has direct image:', { itemId: selectedItem.id, imageUrl })
//         return { type: 'image', url: imageUrl }
//       }
//     }

//     // Direct video fallback
//     if (selectedItem.video) {
//       const videoUrl = getVideoUrl(selectedItem.video)
//       const videoPoster = getVideoPoster(selectedItem.video)
//       if (videoUrl) {
//         console.log('[selectedItemPreviewMedia] Selected item has direct video:', videoUrl)
//         return { type: 'video', url: videoUrl, poster: videoPoster }
//       }
//     }

//     console.log('[selectedItemPreviewMedia] No media found for item:', selectedItem.id)
//     return null
//   }, [selectedItem, selectedItemGalleryIndex])

//   // Reset gallery index when a new item is selected so preview starts at first media
//   useEffect(() => {
//     if (selectedItem) {
//       console.log('[PublicWebsiteViewer] Item selected, resetting gallery index to 0:', { 
//         itemId: selectedItem.id, 
//         itemTitle: selectedItem.title,
//         galleryLength: selectedItem.gallery?.length || 0
//       })
//       setSelectedItemGalleryIndex(0)
//     }
//   }, [selectedItem])

//   // Ref to the main preview video so we can force reload/play when source changes
//   const selectedPreviewVideoRef = useRef(null)

//   useEffect(() => {
//     const videoEl = selectedPreviewVideoRef.current
//     const url = selectedItemPreviewMedia?.type === 'video' ? selectedItemPreviewMedia.url : null
//     if (!videoEl) return
//     // Force reload of the new source
//     try {
//       videoEl.load()
//       videoEl.currentTime = 0
//       const p = videoEl.play()
//       if (p && typeof p.then === 'function') p.catch(() => { /* autoplay blocked, user can click */ })
//       console.log('[PublicWebsiteViewer] triggered load/play for preview video:', url)
//     } catch (err) {
//       console.warn('[PublicWebsiteViewer] failed to reload/play preview video', err)
//     }
//   }, [selectedItemPreviewMedia?.url])

//   // Unified section item open behavior. Prefer gallery if media exists, fallback to details modal.
//   const openSectionItem = useCallback((item, startIndex = 0) => {
//     if (!item) return

//     const hasGallery = Array.isArray(item.gallery) && item.gallery.length > 0
//     const hasImage = !!(item.image || item.image?.url)
//     const hasVideo = !!(item.video || item.video?.url)

//     if (hasGallery || hasImage || hasVideo) {
//       console.log('[PublicWebsiteViewer] openSectionItem (gallery preferred):', { itemId: item.id || item._id, title: item.title || item.name, startIndex, hasGallery, hasImage, hasVideo })
//       handleOpenGallery(item, startIndex)
//       return
//     }

//     console.log('[PublicWebsiteViewer] openSectionItem no media, model details instead:', { itemId: item.id || item._id, title: item.title || item.name })
//     handleViewItem(item)
//   }, [handleOpenGallery, handleViewItem])

//   // Per-section item search filters (search by title or description)
//   const [itemFilters, setItemFilters] = useState({})

//   const handleItemFilterChange = (sectionId, value) => {
//     setItemFilters((prev) => ({ ...prev, [sectionId]: value }))
//     // Reset to page 1 when filter changes
//     setItemPagination((prev) => ({ ...prev, [sectionId]: 1 }))
//   }

//   // Per-section pagination (page number for items when 10+ items exist)
//   const [itemPagination, setItemPagination] = useState({})
//   const [hoveredSectionItemCards, setHoveredSectionItemCards] = useState(new Set())
//   const [hoveredSectionItemButtons, setHoveredSectionItemButtons] = useState(new Set())
//   const [isHoveredWhatsAppModal, setIsHoveredWhatsAppModal] = useState(false)
//   const ITEMS_PER_PAGE = 10

//   const handleItemPageChange = (sectionId, page) => {
//     setItemPagination((prev) => ({ ...prev, [sectionId]: page }))
//   }

//   const handleContinueShopping = () => {
//     setCurrentView('website')
    
//     // Find the products section and scroll to it
//     setTimeout(() => {
//       const sections = website?.sections || []
//       const productsSectionIndex = sections.findIndex(section => 
//         section.type === 'products' || section.content?.type === 'products'
//       )
      
//       if (productsSectionIndex !== -1) {
//         const el = document.getElementById(`section-${productsSectionIndex}`)
//         if (el) {
//           el.scrollIntoView({ behavior: 'smooth', block: 'start' })
//         }
//       }
//     }, 100) // Small delay to ensure the view has switched
//   }

//   const renderCTA = (section) => {
//     const ctaText = (section.content?.ctaText || section.ctaText || section.cta?.text || '').toString().trim()
//     const ctaLink = (section.content?.ctaLink || section.ctaLink || section.cta?.link || '').toString().trim()
    
//     if (!ctaText || !ctaLink) return null
    
//     const ctaStyle = {}
//     const hasCustomBackground = !!section.styling?.ctaButtonBackgroundColor
//     const hasCustomTextColor = !!section.styling?.ctaButtonTextColor
//     const isHovered = hoveredCtaId === section._id

//     if (hasCustomBackground) {
//       ctaStyle.backgroundColor = isHovered ? (section.styling.ctaButtonHoverBackgroundColor || section.styling.ctaButtonBackgroundColor) : section.styling.ctaButtonBackgroundColor
//     }
//     if (hasCustomTextColor) {
//       ctaStyle.color = isHovered ? (section.styling.ctaButtonHoverTextColor || section.styling.ctaButtonTextColor) : section.styling.ctaButtonTextColor
//     }

//     // Build className conditionally. If a custom background color is provided,
//     // do not apply default background/hover classes that would override it.
//     const baseClasses = "px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-lg font-semibold transition-colors text-sm sm:text-base"
//     const defaultBackgroundClasses = hasCustomBackground ? "" : " bg-primary-600 hover:bg-primary-700"
//     const defaultTextClasses = hasCustomTextColor ? "" : " text-white"
//     const ctaClassName = `${baseClasses}${defaultBackgroundClasses}${defaultTextClasses}`
    
//     return (
//       <div className="mt-8 text-center">
//         <a
//           href={ctaLink}
//           target="_blank"
//           rel="noopener noreferrer"
//           className={`${ctaClassName.trim()} transition-colors`}
//           style={Object.keys(ctaStyle).length ? ctaStyle : undefined}
//           onMouseEnter={() => setHoveredCtaId(section._id)}
//           onMouseLeave={() => setHoveredCtaId(null)}
//         >
//           {ctaText}
//         </a>
//       </div>
//     )
//   }

//   // Resolve uploaded logo URL to an absolute backend URL so the browser can fetch it.
//   // ONLY use website-level uploaded logo from backend (no local preview data URLs)
//   const effectiveWebsite = website || prefetchedWebsite
//   const seoMeta = useMemo(() => buildPublicSiteSeo({
//     website: effectiveWebsite,
//     host: typeof window !== 'undefined' ? window.location.hostname : '',
//     pathname: location.pathname,
//     search: location.search,
//     protocol: typeof window !== 'undefined' ? window.location.protocol.replace(':', '') : 'https'
//   }), [effectiveWebsite, location.pathname, location.search])
//   const effectiveSections = sections

//   // Default styling constants for professional appearance
//   const DEFAULT_STYLING = {
//     header: {
//       backgroundColor: '#FFFFFF',
//       textColor: '#000000',
//       hoverBackgroundColor: '#F3F4F6',
//       hoverTextColor: '#374151'
//     },
//     footer: {
//       backgroundColor: '#111827',
//       textColor: '#FFFFFF',
//       hoverBackgroundColor: '#1F2937',
//       hoverTextColor: '#F9FAFB'
//     },
//     body: {
//       backgroundColor: '#FFFFFF',
//       hoverBackgroundColor: '#F3F4F6'
//     },
//     form: {
//       backgroundColor: '#FFFFFF',
//       textColor: '#111827',
//       hoverBackgroundColor: '#F9FAFB',
//       hoverTextColor: '#374151',
//       buttonBackgroundColor: '#3B82F6',
//       buttonTextColor: '#FFFFFF',
//       hoverButtonBackgroundColor: '#2563eb',
//       hoverButtonTextColor: '#ffffff'
//     },
//     cta: {
//       backgroundColor: '#3B82F6',
//       textColor: '#FFFFFF',
//       hoverBackgroundColor: '#2563eb',
//       hoverTextColor: '#FFFFFF'
//     }
//   }

//   // Helper to get styling - returns default if useDefaultStyling is true, otherwise returns custom
//   const getHeaderStyling = () => {
//     const useDefault = effectiveWebsite?.useDefaultStyling !== false
//     if (useDefault) {
//       console.log('🎨 [STYLING] Header: Using DEFAULT styling', {
//         flag: effectiveWebsite?.useDefaultStyling,
//         colors: DEFAULT_STYLING.header
//       })
//       return DEFAULT_STYLING.header
//     }
//     console.log('🎨 [STYLING] Header: Using CUSTOM styling', {
//       flag: effectiveWebsite?.useDefaultStyling,
//       colors: effectiveWebsite?.headerStyling
//     })
//     return effectiveWebsite?.headerStyling || DEFAULT_STYLING.header
//   }

//   const getFooterStyling = () => {
//     const useDefault = effectiveWebsite?.useDefaultStyling !== false
//     if (useDefault) {
//       console.log('🎨 [STYLING] Footer: Using DEFAULT styling', {
//         flag: effectiveWebsite?.useDefaultStyling,
//         colors: DEFAULT_STYLING.footer
//       })
//       return DEFAULT_STYLING.footer
//     }
//     console.log('🎨 [STYLING] Footer: Using CUSTOM styling', {
//       flag: effectiveWebsite?.useDefaultStyling,
//       colors: effectiveWebsite?.footerStyling
//     })
//     return effectiveWebsite?.footerStyling || DEFAULT_STYLING.footer
//   }

//   const getBodyStyling = () => {
//     const useDefault = effectiveWebsite?.useDefaultStyling !== false
//     if (useDefault) {
//       console.log('🎨 [STYLING] Body: Using DEFAULT styling', {
//         flag: effectiveWebsite?.useDefaultStyling,
//         colors: DEFAULT_STYLING.body
//       })
//       return DEFAULT_STYLING.body
//     }
//     console.log('🎨 [STYLING] Body: Using CUSTOM styling', {
//       flag: effectiveWebsite?.useDefaultStyling,
//       colors: effectiveWebsite?.bodyStyling
//     })
//     return effectiveWebsite?.bodyStyling || DEFAULT_STYLING.body
//   }

//   const getFormStyling = () => {
//     const useDefault = effectiveWebsite?.useDefaultStyling !== false
//     if (useDefault) {
//       console.log('🎨 [STYLING] Form: Using DEFAULT styling', {
//         flag: effectiveWebsite?.useDefaultStyling,
//         colors: DEFAULT_STYLING.form
//       })
//       return DEFAULT_STYLING.form
//     }
//     console.log('🎨 [STYLING] Form: Using CUSTOM styling', {
//       flag: effectiveWebsite?.useDefaultStyling,
//       colors: effectiveWebsite?.sectionFormStyling
//     })
//     return effectiveWebsite?.sectionFormStyling || DEFAULT_STYLING.form
//   }

//   const getButtonStyling = () => {
//     const useDefault = effectiveWebsite?.useDefaultStyling !== false
//     if (useDefault) {
//       console.log('🎨 [STYLING] Button: Using DEFAULT styling', {
//         flag: effectiveWebsite?.useDefaultStyling,
//         colors: DEFAULT_STYLING.form
//       })
//       return DEFAULT_STYLING.form
//     }
//     console.log('🎨 [STYLING] Button: Using CUSTOM styling', {
//       flag: effectiveWebsite?.useDefaultStyling,
//       colors: effectiveWebsite?.sectionFormStyling
//     })
//     return effectiveWebsite?.sectionFormStyling || DEFAULT_STYLING.form
//   }

//   const getCtaStyling = (section, isHovered) => {
//     const useDefault = effectiveWebsite?.useDefaultStyling !== false
//     if (useDefault) {
//       console.log('🎨 [STYLING] CTA: Using DEFAULT styling', {
//         flag: effectiveWebsite?.useDefaultStyling,
//         isHovered: isHovered
//       })
//       return {
//         backgroundColor: isHovered ? DEFAULT_STYLING.cta.hoverBackgroundColor : DEFAULT_STYLING.cta.backgroundColor,
//         color: isHovered ? DEFAULT_STYLING.cta.hoverTextColor : DEFAULT_STYLING.cta.textColor
//       }
//     }

//     const hasCustomBackground = !!section?.styling?.ctaButtonBackgroundColor
//     const hasCustomTextColor = !!section?.styling?.ctaButtonTextColor

//     console.log('🎨 [STYLING] CTA: Using CUSTOM styling', {
//       flag: effectiveWebsite?.useDefaultStyling,
//       hasCustomBackground: hasCustomBackground,
//       hasCustomTextColor: hasCustomTextColor
//     })

//     let ctaStyle = {}
//     if (hasCustomBackground) {
//       ctaStyle.backgroundColor = isHovered ? (section.styling.ctaButtonHoverBackgroundColor || section.styling.ctaButtonBackgroundColor) : section.styling.ctaButtonBackgroundColor
//     }
//     if (hasCustomTextColor) {
//       ctaStyle.color = isHovered ? (section.styling.ctaButtonHoverTextColor || section.styling.ctaButtonTextColor) : section.styling.ctaButtonTextColor
//     }

//     // Apply defaults if custom styling not fully set
//     if (!hasCustomBackground) ctaStyle.backgroundColor = isHovered ? DEFAULT_STYLING.cta.hoverBackgroundColor : DEFAULT_STYLING.cta.backgroundColor
//     if (!hasCustomTextColor) ctaStyle.color = isHovered ? DEFAULT_STYLING.cta.hoverTextColor : DEFAULT_STYLING.cta.textColor

//     return ctaStyle
//   }

//   const normalizeLogoUrl = (logoUrl) => {
//     if (!logoUrl || typeof logoUrl !== 'string') return null
//     let normalized = logoUrl.trim().replace(/\s+/g, '')

//     if (normalized.startsWith('//')) {
//       normalized = `${window.location.protocol}${normalized}`
//     }

//     if (normalized.startsWith('data:') || normalized.startsWith('blob:')) {
//       return normalized
//     }

//     if (!/^https?:\/\//i.test(normalized)) {
//       const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//       const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : ''
//       const origin = backendOrigin || fallbackOrigin
//       const pathUrl = normalized.startsWith('/') ? normalized : `/${normalized}`
//       return origin ? `${origin}${pathUrl}` : pathUrl
//     }

//     return normalized
//   }

//   let logoSrc = null
//   if (effectiveWebsite?.logo?.url) {
//     logoSrc = normalizeLogoUrl(effectiveWebsite.logo.url)
//     console.log('[PublicWebsite] Website logo object (normalized):', { raw: effectiveWebsite.logo.url, normalized: logoSrc })
//   }

//   // Fallback: if website-level logo not present, check first section with a logo
//   if (!logoSrc) {
//     const sectionLogo = effectiveSections?.find(s => s.content?.logo?.url)?.content?.logo?.url
//     if (sectionLogo) {
//       logoSrc = normalizeLogoUrl(sectionLogo)
//       console.log('[PublicWebsite] Section logo object (normalized fallback):', { raw: sectionLogo, normalized: logoSrc })
//     }
//   }

//   if (logoSrc) {
//     console.log('[PublicWebsite] Final logo URL:', logoSrc)
//   } else {
//     console.log('[PublicWebsite] No logo URL available, will use website name fallback')
//   }

//   // Reset logo error on URL update (important for mobile/hydration cases)
//   useEffect(() => {
//     setLogoLoadError(false)
//     setLogoRetryCount(0)
//   }, [logoSrc])

//   if (loading) {
//     return (
//       <div className="min-h-screen flex items-center justify-center">
//         <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
//       </div>
//     )
//   }

//   // For custom domains, show network error if prefetchedWebsiteError is set
//   if (prefetchedWebsite === null && prefetchedWebsiteError) {
//     return (
//       <div className="min-h-screen flex items-center justify-center p-6">
//         <div className="text-center max-w-md">
//           <div className="mb-4">
//             <div className="text-4xl font-bold text-red-500 mb-2">⚠️</div>
//             <p className="text-xl font-semibold text-gray-900 mb-2">Network Error</p>
//           </div>
//           <p className="text-gray-600 mb-4">{prefetchedWebsiteError}</p>
//           <p className="text-sm text-gray-500 mb-6">Please check your internet connection and try again.</p>
//           <button
//             onClick={() => window.location.reload()}
//             className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
//           >
//             Retry
//           </button>
//         </div>
//       </div>
//     )
//   }

//   if (error && !prefetchedWebsite) {
//     return (
//       <div className="min-h-screen flex items-center justify-center p-6">
//         <div className="text-center">
//           <p className="text-red-600 font-semibold mb-2">Failed to load website</p>
//           <p className="text-sm text-gray-600">{error}</p>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div
//       className="min-h-screen w-full overflow-x-hidden"
//       style={{
//         backgroundColor: isBodyHovered ? (getBodyStyling()?.hoverBackgroundColor || '#F3F4F6') : (getBodyStyling()?.backgroundColor || '#FFFFFF')
//       }}
//       onMouseEnter={() => setIsBodyHovered(true)}
//       onMouseLeave={() => setIsBodyHovered(false)}
//     >
//       <Helmet>
//         <title>{seoMeta.title}</title>
//         <meta name="description" content={seoMeta.description} />
//         {seoMeta.keywords && <meta name="keywords" content={seoMeta.keywords} />}
//         <link rel="canonical" href={seoMeta.canonicalUrl} />
//         <meta property="og:title" content={seoMeta.ogTitle} />
//         <meta property="og:description" content={seoMeta.ogDescription} />
//         <meta property="og:url" content={seoMeta.ogUrl} />
//         {seoMeta.ogImage && <meta property="og:image" content={seoMeta.ogImage} />}
//         <meta name="twitter:card" content={seoMeta.twitterCard} />
//         <meta name="twitter:title" content={seoMeta.twitterTitle} />
//         <meta name="twitter:description" content={seoMeta.twitterDescription} />
//         {seoMeta.twitterImage && <meta name="twitter:image" content={seoMeta.twitterImage} />}
//         <meta name="robots" content={seoMeta.robots} />
//         {(seoMeta.ogImage || logoSrc) && <link rel="icon" type="image/png" href={seoMeta.ogImage || logoSrc} />}
//       </Helmet>
//       <style>{WHATSAPP_BUTTON_OVERRIDE_CSS}</style>
//       {customerVerificationNotice && (
//         <div className={`mx-4 mt-4 rounded-lg border p-4 text-sm ${customerVerificationNotice.type === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
//           {customerVerificationNotice.message}
//         </div>
//       )}
//       {customerAuthOpen && (
//         <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
//           <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl overflow-hidden">
//             <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
//               <div>
//                 <h2 className="text-xl font-semibold text-gray-900">
//                   {customerAuthMode === 'login' ? (website?.customerAuth?.loginButtonText || 'Login') : customerAuthMode === 'register' ? (website?.customerAuth?.signupButtonText || 'Sign Up') : 'Forgot Password'}
//                 </h2>
//                 <p className="text-sm text-gray-500 mt-1">
//                   {customerAuthMode === 'login'
//                     ? 'Sign in to access customer-only features.'
//                     : customerAuthMode === 'register'
//                       ? 'Create a customer account for this website.'
//                       : 'Enter your email to receive a password reset link.'}
//                 </p>
//               </div>
//               <button onClick={closeCustomerAuthModal} className="text-gray-500 hover:text-gray-900">✕</button>
//             </div>
//             <form onSubmit={handleCustomerAuthSubmit} className="px-6 py-6 space-y-4">
//               {customerAuthError && (
//                 <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
//                   {customerAuthError}
//                 </div>
//               )}
//               {(customerAuthMessage || customerVerificationNotice) && (
//                 <div className={`rounded-md border p-3 text-sm ${customerVerificationNotice?.type === 'error' || customerAuthError ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
//                   {customerVerificationNotice?.message || customerAuthMessage}
//                 </div>
//               )}

//               {customerAuthMode === 'register' && (
//                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                   <label className="block">
//                     <span className="text-sm font-medium text-gray-700">First Name</span>
//                     <input
//                       name="firstName"
//                       value={customerAuthForm.firstName}
//                       onChange={handleCustomerAuthFormChange}
//                       className="input-field w-full"
//                       required
//                     />
//                   </label>
//                   <label className="block">
//                     <span className="text-sm font-medium text-gray-700">Last Name</span>
//                     <input
//                       name="lastName"
//                       value={customerAuthForm.lastName}
//                       onChange={handleCustomerAuthFormChange}
//                       className="input-field w-full"
//                       required
//                     />
//                   </label>
//                 </div>
//               )}

//               {customerAuthMode === 'register' && (
//                 <label className="block">
//                   <span className="text-sm font-medium text-gray-700">Phone</span>
//                   <input
//                     name="phone"
//                     value={customerAuthForm.phone}
//                     onChange={handleCustomerAuthFormChange}
//                     className="input-field w-full"
//                     placeholder="Optional"
//                   />
//                 </label>
//               )}

//               <label className="block">
//                 <span className="text-sm font-medium text-gray-700">Email</span>
//                 <input
//                   name="email"
//                   type="email"
//                   value={customerAuthForm.email}
//                   onChange={handleCustomerAuthFormChange}
//                   className="input-field w-full"
//                   required
//                 />
//               </label>

//               {(customerAuthMode === 'login' || customerAuthMode === 'register') && (
//                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                   <label className="block">
//                     <span className="text-sm font-medium text-gray-700">Password</span>
//                     <input
//                       name="password"
//                       type="password"
//                       value={customerAuthForm.password}
//                       onChange={handleCustomerAuthFormChange}
//                       className="input-field w-full"
//                       required
//                     />
//                   </label>
//                   <label className="block">
//                     <span className="text-sm font-medium text-gray-700">Confirm Password</span>
//                     <input
//                       name="confirmPassword"
//                       type="password"
//                       value={customerAuthForm.confirmPassword}
//                       onChange={handleCustomerAuthFormChange}
//                       className="input-field w-full"
//                       required={customerAuthMode === 'register'}
//                       disabled={false}
//                     />
//                   </label>
//                 </div>
//               )}

//               <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
//                 <button
//                   type="submit"
//                   disabled={customerAuthLoading}
//                   className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-400"
//                 >
//                   {customerAuthLoading ? 'Processing...' : customerAuthMode === 'login' ? 'Login' : customerAuthMode === 'register' ? 'Create Account' : 'Send Reset Link'}
//                 </button>
//                 {customerAuthMode === 'login' && website?.customerAuth?.enableForgotPassword && (
//                   <button
//                     type="button"
//                     onClick={() => setCustomerAuthMode('forgot')}
//                     className="w-full sm:w-auto rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
//                   >
//                     Forgot password?
//                   </button>
//                 )}
//               </div>

//               {customerAuthMode !== 'login' && (
//                 <div className="text-sm text-gray-600">
//                   {customerAuthMode === 'register' ? (
//                     <>
//                       Already have an account?{' '}
//                       <button type="button" onClick={() => setCustomerAuthMode('login')} className="text-primary-600 hover:text-primary-700 font-medium">Login</button>
//                     </>
//                   ) : (
//                     <>
//                       Remembered your password?{' '}
//                       <button type="button" onClick={() => setCustomerAuthMode('login')} className="text-primary-600 hover:text-primary-700 font-medium">Back to login</button>
//                     </>
//                   )}
//                 </div>
//               )}
//             </form>
//           </div>
//         </div>
//       )}
//       {verificationId && (
//         <div className="max-w-7xl mx-auto px-4 py-3">
//           <div className={`p-3 rounded-lg border ${verificationResult?.error ? 'border-red-300 bg-red-50' : 'border-emerald-300 bg-emerald-50'}`}>
//             {verificationLoading ? (
//               <p className="text-sm text-gray-700">Verifying card... please wait.</p>
//             ) : verificationResult?.error ? (
//               <p className="text-sm text-red-700">Verification failed: {verificationResult.error}</p>
//             ) : verificationResult ? (
//               <div>
//                 <p className="text-sm font-semibold text-gray-900">Card verification {verificationResult.isValid ? 'successful' : 'failed'}</p>
//                 <p className="text-sm text-gray-700">Card: {verificationResult.holderName || 'Unknown'}</p>
//                 <p className="text-sm text-gray-700">Status: <span className={verificationResult.isValid ? 'text-emerald-700' : 'text-red-700'}>{verificationResult.status}</span></p>
//                 <p className="text-sm text-gray-700">URL: <a href={verificationResult.verificationUrl} target="_blank" rel="noopener noreferrer" className="underline text-blue-600">View</a></p>
//               </div>
//             ) : null}
//           </div>
//         </div>
//       )}

//       {currentView === 'cart' ? (
//         <ShoppingCart
//           website={website}
//           sectionFormStyling={getFormStyling()}
//           useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
//           sectionType="cart"
//           onContinueShopping={handleContinueShopping}
//           onCheckout={() => setCurrentView('checkout')}
//         />
//       ) : currentView === 'checkout' ? (
//         <Checkout
//           website={website}
//           sectionFormStyling={getFormStyling()}
//           useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
//           onBack={() => setCurrentView('cart')}
//           customerProfile={customerAuthUser}
//           customerAuthToken={customerAuthToken}
//         />
//       ) : (
//         <>
//           {/* Header with Logo or Website Name */}
//           {website && (
//             <header
//               className="sticky top-0 z-50 border-b border-gray-200 shadow-sm transition-colors"
//               style={{
//                 backgroundColor: isHeaderHovered ? (getHeaderStyling()?.hoverBackgroundColor || '#F3F4F6') : (getHeaderStyling()?.backgroundColor || '#ffffff'),
//                 color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit')
//               }}
//               onMouseEnter={() => setIsHeaderHovered(true)}
//               onMouseLeave={() => setIsHeaderHovered(false)}
//             >
//               <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
//                 <div className="flex items-center justify-between">
//                   {/* Logo or Website Name */}
//                   <div
//                     className="flex items-center gap-2 sm:gap-3 min-h-[32px] sm:min-h-[40px] flex-shrink-0 cursor-pointer"
//                     onClick={() => {
//                       const el = document.getElementById('section-0');
//                       if (el) el.scrollIntoView({ behavior: 'smooth' });
//                     }}
//                     style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
//                   >
//                     {logoSrc && !logoLoadError ? (
//                       <div className="relative flex-shrink-0">
//                         <img
//                           key={`${logoSrc}-${logoRetryCount}`}
//                           src={logoSrc}
//                           alt={website?.name || 'Logo'}
//                           crossOrigin="anonymous"
//                           loading="lazy"
//                           className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto max-w-[64px] sm:max-w-[80px] md:max-w-[120px] lg:max-w-[160px] object-contain"
//                           style={{
//                             minHeight: '24px',
//                             maxHeight: '56px',
//                             width: 'auto',
//                             height: 'auto',
//                             display: 'block'
//                           }}
//                           onLoad={(e) => {
//                             console.log('[PublicWebsite] Logo loaded successfully:', logoSrc, 'Size:', e.target.naturalWidth, 'x', e.target.naturalHeight)
//                             setLogoLoadError(false)
//                           }}
//                           onError={(e) => {
//                             console.error('[PublicWebsite] Logo failed to load on mobile/desktop:', logoSrc, e)

//                             if (logoRetryCount < 2) {
//                               setLogoRetryCount((prev) => prev + 1)
//                               const retryUrl = `${logoSrc}${logoSrc.includes('?') ? '&' : '?'}_r=${Date.now()}`
//                               e.target.src = normalizeLogoUrl(retryUrl)
//                               console.log('[PublicWebsite] Retrying logo load with cache-busted URL:', e.target.src)
//                               return
//                             }

//                             setLogoLoadError(true)
//                             console.warn('[PublicWebsite] Logo permanently failed to load after retries, showing website name fallback')
//                           }}
//                         />
//                       </div>
//                     ) : (
//                       <div className="text-sm sm:text-base md:text-lg lg:text-xl font-bold truncate max-w-[200px] sm:max-w-none" style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}>
//                         {website?.name || ''}
//                       </div>
//                     )}
//                   </div>

//                   {/* Desktop Navigation */}
//                   {sections && sections.length > 0 && (
//                     <nav className="hidden md:flex items-center gap-4">
//                       {sections.filter(s => !s.disabled).map((s, i) => {
//                         const title = (s.title || s.content?.heading || `Section ${i+1}`).toString()
//                         return (
//                           <a
//                             key={s._id || i}
//                             href={`#section-${i}`}
//                             onClick={(e) => {
//                               e.preventDefault()
//                               const el = document.getElementById(`section-${i}`)
//                               if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
//                             }}
//                             className="text-sm px-3 py-2 rounded-md"
//                             style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
//                           >
//                             {title}
//                           </a>
//                         )
//                       })}
//                     </nav>
//                   )}

//                   {/* Mobile Menu Button and Actions */}
//                   <div className="flex items-center gap-4">
//                     {!prefetchedWebsite && (
//                       <button
//                         onClick={() => setReportModalOpen(true)}
//                         className="p-2 hover:bg-gray-100 rounded"
//                         title="Report this website"
//                         style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
//                       >
//                         <FiFlag className="w-6 h-6" />
//                       </button>
//                     )}
//                     {customerAuthEnabled && !customerAuthUser && (
//                       <>
//                         <button
//                           onClick={() => openCustomerAuthModal('login')}
//                           className="hidden md:inline-flex px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
//                           style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
//                         >
//                           {website?.customerAuth?.loginButtonText || 'Login'}
//                         </button>
//                         <button
//                           onClick={() => openCustomerAuthModal('register')}
//                           className="hidden md:inline-flex px-3 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
//                         >
//                           {website?.customerAuth?.signupButtonText || 'Sign Up'}
//                         </button>
//                       </>
//                     )}
//                     {customerAuthEnabled && customerAuthUser && (
//                       <>
//                         <button
//                           onClick={() => {
//                             setCustomerActivityPage(1)
//                             setCurrentView('account')
//                           }}
//                           className="hidden md:inline-flex px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
//                           style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
//                         >
//                           My Account
//                         </button>
//                         <button
//                           onClick={handleCustomerAuthLogout}
//                           className="hidden md:inline-flex px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
//                           style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
//                         >
//                           Logout
//                         </button>
//                       </>
//                     )}
//                     {hasCartFeature && (
//                       <button
//                         onClick={() => setCurrentView(currentView === 'cart' ? 'website' : 'cart')}
//                         className="relative p-2 hover:bg-gray-100 rounded"
//                         style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
//                       >
//                         <FiShoppingCart className="w-6 h-6" />
//                         {itemCount > 0 && (
//                           <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
//                             {itemCount}
//                           </span>
//                         )}
//                       </button>
//                     )}
//                     {sections && sections.length > 0 && (
//                       <button
//                         onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
//                         className="md:hidden p-2 hover:bg-gray-100 rounded"
//                         style={{ color: isHeaderHovered ? (getHeaderStyling()?.hoverTextColor || '#374151') : (getHeaderStyling()?.textColor || 'inherit') }}
//                       >
//                         {mobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
//                       </button>
//                     )}
//                   </div>
//                 </div>

//                 {/* Mobile Navigation Menu */}
//                 {mobileMenuOpen && sections && sections.length > 0 && (
//                   <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
//                     <nav className="flex flex-col gap-2">
//                       {sections.filter(s => !s.disabled).map((s, i) => {
//                         const title = (s.title || s.content?.heading || `Section ${i+1}`).toString()
//                         return (
//                           <a
//                             key={s._id || i}
//                             href={`#section-${i}`}
//                             onClick={(e) => {
//                               e.preventDefault()
//                               setMobileMenuOpen(false)
//                               const el = document.getElementById(`section-${i}`)
//                               if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
//                             }}
//                             className="text-sm text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-100"
//                           >
//                             {title}
//                           </a>
//                         )
//                       })}
//                     </nav>
//                   </div>
//                 )}
//                 {mobileMenuOpen && customerAuthEnabled && !customerAuthUser && (
//                   <div className="md:hidden mt-4 pb-4 border-t border-gray-200 space-y-2">
//                     <button
//                       onClick={() => {
//                         setMobileMenuOpen(false)
//                         openCustomerAuthModal('login')
//                       }}
//                       className="w-full text-left px-3 py-2 rounded-md bg-white border border-gray-300 text-sm font-medium hover:bg-gray-50"
//                     >
//                       {website?.customerAuth?.loginButtonText || 'Login'}
//                     </button>
//                     <button
//                       onClick={() => {
//                         setMobileMenuOpen(false)
//                         openCustomerAuthModal('register')
//                       }}
//                       className="w-full text-left px-3 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
//                     >
//                       {website?.customerAuth?.signupButtonText || 'Sign Up'}
//                     </button>
//                   </div>
//                 )}
//                 {mobileMenuOpen && customerAuthEnabled && customerAuthUser && (
//                   <div className="md:hidden mt-4 pb-4 border-t border-gray-200 space-y-2">
//                     <button
//                       onClick={() => {
//                         setMobileMenuOpen(false)
//                         setCurrentView('account')
//                       }}
//                       className="w-full text-left px-3 py-2 rounded-md bg-white border border-gray-300 text-sm font-medium hover:bg-gray-50"
//                     >
//                       My Account
//                     </button>
//                     <button
//                       onClick={() => {
//                         setMobileMenuOpen(false)
//                         handleCustomerAuthLogout()
//                       }}
//                       className="w-full text-left px-3 py-2 rounded-md bg-white border border-gray-300 text-sm font-medium hover:bg-gray-50"
//                     >
//                       Logout
//                     </button>
//                   </div>
//                 )}
//               </div>
//             </header>
//           )}

//       <main>
//         {ticketDetailError && (
//           <div className="p-6 text-center text-red-600 bg-red-100 border border-red-200 rounded mb-4">
//             {ticketDetailError}
//           </div>
//         )}
//         {currentView === 'account' && customerAuthUser ? (
//           <section className="max-w-6xl mx-auto p-6">
//             <div className="bg-white border rounded-lg shadow-sm p-6 space-y-6">
//               <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
//                 <div className="min-w-0">
//                   <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">My Account</h2>
//                   <p className="text-sm text-gray-600 max-w-2xl">Welcome back, {customerAuthUser.firstName || customerAuthUser.email}.</p>
//                 </div>
//                 <div className="flex flex-wrap gap-3">
//                   <button
//                     onClick={() => setCurrentView('website')}
//                     className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
//                   >
//                     Back to website
//                   </button>
//                   <button
//                     onClick={handleCustomerAuthLogout}
//                     className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
//                   >
//                     Logout
//                   </button>
//                 </div>
//               </div>

//               <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//                 <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
//                   <h3 className="text-lg font-semibold text-gray-900 mb-3">Profile</h3>
//                   <div className="text-sm text-gray-700 space-y-2">
//                     <div><span className="font-semibold">Name:</span> {customerAuthUser.firstName} {customerAuthUser.lastName}</div>
//                     <div><span className="font-semibold">Email:</span> {customerAuthUser.email}</div>
//                     {customerAuthUser.phone && <div><span className="font-semibold">Phone:</span> {customerAuthUser.phone}</div>}
//                     <div><span className="font-semibold">Status:</span> {customerAuthUser.status || 'active'}</div>
//                     <div><span className="font-semibold">Email verified:</span> {customerAuthUser.isEmailVerified ? 'Yes' : 'No'}</div>
//                   </div>

//                   {!customerAuthUser.isEmailVerified && (
//                     <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
//                       <p className="font-semibold">Your account is awaiting email verification.</p>
//                       <button
//                         type="button"
//                         onClick={handleResendVerification}
//                         disabled={resendingVerification}
//                         className="mt-2 inline-flex items-center rounded bg-amber-600 px-3 py-2 text-white hover:bg-amber-700 disabled:bg-gray-400"
//                       >
//                         {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
//                       </button>
//                     </div>
//                   )}
//                 </div>

//                 <div className="md:col-span-2 rounded-lg border border-gray-200 p-4 bg-white">
//                   <div className="flex flex-wrap gap-2 mb-6 items-center justify-between">
//                     <div className="flex flex-wrap gap-2">
//                       {[
//                         { key: 'profile', label: 'Profile' },
//                         { key: 'messages', label: 'Messages' },
//                         { key: 'activity', label: 'Activity' }
//                       ].map((tab) => (
//                         <button
//                           key={tab.key}
//                           type="button"
//                           onClick={() => setAccountSection(tab.key)}
//                           className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${accountSection === tab.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
//                         >
//                           {tab.label}
//                         </button>
//                       ))}
//                     </div>
//                     {accountSection === 'messages' && website && customerAuthToken && customerAuthUser && (
//                       <CustomerNotificationCenter 
//                         website={website} 
//                         customerAuthToken={customerAuthToken} 
//                         customerAuthUser={customerAuthUser} 
//                         onOpenConversation={handleOpenConversationFromNotification}
//                       />
//                     )}
//                   </div>

//                   {accountSection === 'profile' && (
//                     <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
//                       <div className="flex items-center justify-between gap-3 mb-4">
//                         <div>
//                           <h3 className="text-lg font-semibold text-gray-900">Update profile</h3>
//                           <p className="text-sm text-gray-500">Keep your account details up to date for future purchases and communications.</p>
//                         </div>
//                       </div>

//                       <form onSubmit={handleCustomerProfileSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         <label className="text-sm text-gray-700">
//                           <span className="mb-1 block font-medium">First name</span>
//                           <input name="firstName" value={customerProfileForm.firstName} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <label className="text-sm text-gray-700">
//                           <span className="mb-1 block font-medium">Last name</span>
//                           <input name="lastName" value={customerProfileForm.lastName} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <label className="text-sm text-gray-700">
//                           <span className="mb-1 block font-medium">Email</span>
//                           <input name="email" type="email" value={customerProfileForm.email} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <label className="text-sm text-gray-700">
//                           <span className="mb-1 block font-medium">Phone</span>
//                           <input name="phone" value={customerProfileForm.phone} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <label className="text-sm text-gray-700 md:col-span-2">
//                           <span className="mb-1 block font-medium">Shipping address</span>
//                           <input name="shippingAddress" value={customerProfileForm.shippingAddress} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <label className="text-sm text-gray-700">
//                           <span className="mb-1 block font-medium">Shipping city</span>
//                           <input name="shippingCity" value={customerProfileForm.shippingCity} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <label className="text-sm text-gray-700">
//                           <span className="mb-1 block font-medium">Shipping state</span>
//                           <input name="shippingState" value={customerProfileForm.shippingState} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <label className="text-sm text-gray-700">
//                           <span className="mb-1 block font-medium">Shipping ZIP</span>
//                           <input name="shippingZip" value={customerProfileForm.shippingZip} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <label className="text-sm text-gray-700 md:col-span-2">
//                           <span className="mb-1 block font-medium">Billing address</span>
//                           <input name="billingAddress" value={customerProfileForm.billingAddress} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <label className="text-sm text-gray-700">
//                           <span className="mb-1 block font-medium">Billing city</span>
//                           <input name="billingCity" value={customerProfileForm.billingCity} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <label className="text-sm text-gray-700">
//                           <span className="mb-1 block font-medium">Billing state</span>
//                           <input name="billingState" value={customerProfileForm.billingState} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <label className="text-sm text-gray-700">
//                           <span className="mb-1 block font-medium">Billing ZIP</span>
//                           <input name="billingZip" value={customerProfileForm.billingZip} onChange={handleCustomerProfileChange} className="input-field w-full" />
//                         </label>
//                         <div className="md:col-span-2 flex justify-end">
//                           <button type="submit" disabled={profileSaving} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-400">
//                             {profileSaving ? 'Saving...' : 'Save profile'}
//                           </button>
//                         </div>
//                       </form>
//                     </div>
//                   )}

//                   {accountSection === 'messages' && (
//                     <div className="mb-6">
//                       <CustomerContactChats 
//                         website={website} 
//                         customerAuthToken={customerAuthToken} 
//                         customerAuthUser={customerAuthUser}
//                         selectedConversationId={selectedConversationIdFromNotification}
//                         onSelectConversation={setSelectedConversationIdFromNotification}
//                       />
//                     </div>
//                   )}

//                   {accountSection === 'activity' && (
//                     <div>
//                       <div className="flex items-center justify-between mb-4">
//                         <div>
//                           <h3 className="text-lg font-semibold text-gray-900">Recent activity</h3>
//                           <p className="text-sm text-gray-500">Your latest account actions and history.</p>
//                         </div>
//                         <button
//                           onClick={() => fetchCustomerActivity(1)}
//                           className="text-sm text-primary-600 hover:text-primary-700"
//                         >
//                           Refresh
//                         </button>
//                       </div>

//                       {customerActivityLoading ? (
//                         <div className="py-12 text-center text-gray-600">Loading activity...</div>
//                       ) : customerActivityError ? (
//                         <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{customerActivityError}</div>
//                       ) : customerActivity.length === 0 ? (
//                         <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
//                           No activity has been recorded for your account yet.
//                         </div>
//                       ) : (
//                         <div className="space-y-4">
//                           {customerActivity.map((item) => (
//                             <div key={item._id} className="rounded-lg border border-gray-200 p-4 bg-gray-50">
//                               <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
//                                 <div>
//                                   <p className="font-semibold text-gray-900">{item.activityType}</p>
//                                   <p className="text-sm text-gray-600">{item.description}</p>
//                                 </div>
//                                 <div className="text-sm text-gray-500">
//                                   {new Date(item.createdAt).toLocaleString()}
//                                 </div>
//                               </div>
//                               {item.status && <div className="mt-2 text-xs uppercase tracking-wide text-gray-500">Status: {item.status}</div>}
//                               {item.metadata && Object.keys(item.metadata).length > 0 && (
//                                 <div className="mt-3 text-xs text-gray-500">
//                                   {Object.entries(item.metadata).map(([key, value]) => (
//                                     <span key={key} className="mr-3">{key}: {String(value)}</span>
//                                   ))}
//                                 </div>
//                               )}
//                             </div>
//                           ))}

//                           <div className="flex flex-wrap items-center justify-between gap-3">
//                             <span className="text-sm text-gray-600">Showing {customerActivity.length} of {customerActivityTotal} activities</span>
//                             <div className="flex items-center gap-2">
//                               <button
//                                 onClick={() => fetchCustomerActivity(Math.max(1, customerActivityPage - 1))}
//                                 disabled={customerActivityPage <= 1}
//                                 className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
//                               >
//                                 Previous
//                               </button>
//                               <button
//                                 onClick={() => fetchCustomerActivity(customerActivityPage + 1)}
//                                 disabled={(customerActivityPage * CUSTOMER_ACTIVITY_LIMIT) >= customerActivityTotal}
//                                 className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
//                               >
//                                 Next
//                               </button>
//                             </div>
//                           </div>
//                         </div>
//                       )}
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>
//           </section>
//         ) : ticketDetail ? (
//           <section className="max-w-6xl mx-auto p-6">
//             <div className="bg-white border rounded-lg shadow-sm p-6">
//               <div className="flex flex-wrap gap-6">
//                 <div className="w-full md:w-1/3">
//                   {ticketDetail.eventBanner?.url ? (
//                     <img src={ticketDetail.eventBanner.url} alt={ticketDetail.eventName} className="w-full h-56 object-cover rounded" />
//                   ) : (
//                     <div className="w-full h-56 bg-gray-100 rounded flex items-center justify-center">No banner</div>
//                   )}
//                 </div>

//                 <div className="w-full md:w-2/3">
//                   <h2 className="text-3xl font-bold">{ticketDetail.eventName}</h2>
//                   <p className="text-lg text-gray-500 mb-2">{ticketDetail.eventCategory} • {ticketDetail.ticketType}</p>
//                   <p className="text-sm text-gray-600 mb-3">{ticketDetail.venueName} • {ticketDetail.city}, {ticketDetail.country}</p>
//                   <p className="mb-3">{ticketDetail.eventDescription || ticketDetail.shortDescription}</p>

//                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-sm text-gray-700 mb-4">
//                     <div><strong>Date:</strong> {(() => {
//                       if (ticketDetail.eventDate) {
//                         const [year, month, day] = ticketDetail.eventDate.split('-');
//                         const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
//                         return `${monthNames[parseInt(month)-1]} ${parseInt(day)}, ${year}`;
//                       }
//                       return new Date(ticketDetail.eventDate).toLocaleDateString();
//                     })()}</div>
//                     <div><strong>Time:</strong> {ticketDetail.startTime} - {ticketDetail.endTime}</div>
//                     <div><strong>Doors Open:</strong> {ticketDetail.doorsOpenTime || 'N/A'}</div>
//                     <div><strong>Price:</strong> {ticketDetail.currency} {ticketDetail.price}</div>
//                     <div><strong>Remaining:</strong> {ticketDetail.totalQuantity - ticketDetail.soldQuantity}</div>
//                     <div><strong>Status:</strong> {ticketDetail.salesOpen ? 'Open' : 'Closed'}</div>
//                   </div>

//                   {ticketDetail.features?.length > 0 && (
//                     <div className="mb-4">
//                       <h3 className="font-semibold">Features</h3>
//                       <ul className="list-disc ml-5 text-sm text-gray-700">
//                         {ticketDetail.features.map((feature, i) => <li key={i}>{feature}</li>)}
//                       </ul>
//                     </div>
//                   )}

//                   <div className="flex items-center gap-3">
//                     <button 
//                       onClick={() => setShowBuyerForm(true)} 
//                       disabled={!ticketDetail?.salesOpen}
//                       className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
//                     >
//                       Buy Ticket
//                     </button>
//                     <button onClick={() => { setTicketDetail(null); setTicketDetailError(null); }} className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold">Back</button>
//                   </div>
//                 </div>
//               </div>

//               {ticketDetail.ticketLink && (
//                 <div className="mt-4">
//                   <a href={ticketDetail.ticketLink} className="text-blue-600">Event Link</a>
//                 </div>
//               )}
//             </div>
//           </section>
//         ) : (
//           (() => {
//             if (sections && sections.length > 0) {
//               return sections.map((section, idx) => {
//               if (section.disabled) return null

//               const sectionType = normalizeSectionType(section.type || section.sectionType || '')
//               const specialMedia = section.content?.specialMedia || []
//               const left = section.content?.leftSidebar
//               const right = section.content?.rightSidebar
//               const hasLeft = left && ((left.media && left.media.length > 0) || (left.contentText && left.contentText.trim()))
//               const hasRight = right && ((right.media && right.media.length > 0) || (right.contentText && right.contentText.trim()))
//               const hasSpecialMedia = specialMedia.length > 0
//               const hasTopHeader = hasSpecialMedia || hasLeft || hasRight

//               // Keep hero rendering unchanged
//               if (sectionType === 'hero' || section.isHeroSection) {
//                 const heroImages = section.content?.heroImages || []
//                 const heroImage = section.content?.heroImage?.url || section.content?.backgroundImage?.url
//                 const ctaText = (section.content?.ctaText || section.ctaText || section.cta?.text || '').toString().trim()
//                 const ctaLink = (section.content?.ctaLink || section.ctaLink || section.cta?.link || '').toString().trim()
//                 const ctaBackgroundColor = section.styling?.ctaButtonBackgroundColor
//                 const ctaTextColor = section.styling?.ctaButtonTextColor
//                 const heroButtonLink = Array.isArray(section.content?.heroButtons)
//                   ? section.content.heroButtons.find((btn) => btn?.link?.toString().trim())?.link?.toString().trim()
//                   : ''
//                 const heroClickLink = ctaLink || heroButtonLink

//                 // Use carousel if multiple images exist, otherwise use single image
//                 if (heroImages.length > 1) {
//                   return (
//                     <HeroCarousel
//                       key={section._id}
//                       images={heroImages}
//                       settings={section.content?.carouselSettings}
//                       heading={section.content?.heading}
//                       subheading={section.content?.subheading}
//                       heroButtons={section.content?.heroButtons}
//                       overlay={section.content?.heroOverlay}
//                       ctaText={ctaText}
//                       ctaLink={ctaLink}
//                       ctaBackgroundColor={ctaBackgroundColor}
//                       ctaTextColor={ctaTextColor}
//                       displayMode={section.content?.heroDisplayMode || 'contain'}
//                     />
//                   )
//                 }
                
//                 // Fallback to single image or first carousel image
//                 const displayImage = heroImages.length === 1 
//                   ? heroImages[0]?.url 
//                   : heroImage
                
//                 const navigateToLink = (link) => {
//                   if (!link) return
//                   const trimmedLink = link.toString().trim()
//                   if (!trimmedLink) return

//                   if (trimmedLink.startsWith('#')) {
//                     window.location.hash = trimmedLink
//                     return
//                   }

//                   try {
//                     const url = new URL(trimmedLink, window.location.href)
//                     if (url.origin === window.location.origin) {
//                       window.location.href = url.href
//                     } else {
//                       window.open(url.href, '_blank', 'noopener,noreferrer')
//                     }
//                   } catch (error) {
//                     window.location.href = trimmedLink
//                   }
//                 }

//                 return (
//                   <section
//                   key={section._id}
//                   id={`section-${idx}`}
//                   className={`relative w-full flex items-center justify-center scroll-mt-20 ${heroClickLink ? 'cursor-pointer' : ''}`}
//                   onClick={(e) => {
//                     if (e.target.closest('[role="dialog"]')) {
//                       e.stopPropagation()
//                       return
//                     }
//                     navigateToLink(heroClickLink)
//                   }}
//                   role={heroClickLink ? 'link' : undefined}
//                   tabIndex={heroClickLink ? 0 : undefined}
//                   onKeyDown={(e) => {
//                     if (heroClickLink && (e.key === 'Enter' || e.key === ' ')) {
//                       e.preventDefault()
//                       navigateToLink(heroClickLink)
//                     }
//                   }}
//                 >
//                   {/* Hero Background Image (mode-aware, responsive) */}
//                   {displayImage && (() => {
//                     const mode = (section.content && section.content.heroDisplayMode) ? section.content.heroDisplayMode : 'contain'
//                     const heroRatio = heroAspectRatios[section._id]
//                     const heroContainerStyle = {
//                       zIndex: 0,
//                       width: '100%',
//                       minHeight: '40vh',
//                       maxHeight: '90vh',
//                       aspectRatio: mode === 'cover' ? undefined : (heroRatio || '16 / 9'),
//                       overflow: 'hidden',
//                       position: 'relative'
//                     }

//                     if (mode === 'cover') {
//                       return (
//                         <div className="absolute inset-0 w-full h-full overflow-hidden" style={{ zIndex: 0 }}>
//                           <img
//                             src={displayImage}
//                             alt="Hero background"
//                             className="w-full h-full object-cover object-center"
//                             onError={(e) => console.error('[PublicWebsiteViewer] Hero image failed to load:', displayImage.substring(0, 100) + '...')}
//                           />
//                         </div>
//                       )
//                     }

//                     return (
//                       <div className="relative w-full mx-auto bg-black/5" style={heroContainerStyle}>
//                         <img
//                           src={displayImage}
//                           alt="Hero background"
//                           className="absolute inset-0 w-full h-full object-contain object-center"
//                           style={{ display: 'block' }}
//                           onLoad={(e) => handleHeroImageLoad(section._id, e.target.naturalWidth, e.target.naturalHeight)}
//                           onError={(e) => console.error('[PublicWebsiteViewer] Hero image failed to load:', displayImage.substring(0, 100) + '...')}
//                         />
//                       </div>
//                     )
//                   })()}

//                   {/* Hero Overlay */}
//                   {section.content?.heroOverlay?.enabled && (
//                     <div
//                       className="absolute inset-0"
//                       style={{
//                         backgroundColor: section.content.heroOverlay.color || '#000000',
//                         opacity: section.content.heroOverlay.opacity || 0.3,
//                         zIndex: 1
//                       }}
//                     />
//                   )}

//                   {/* Hero Content */}
//                   <div className="relative z-10 text-center text-white px-4 py-10 sm:px-6 sm:py-12 lg:px-8 pointer-events-none">
//                     {section.content?.heading && (
//                       <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 drop-shadow-lg leading-tight">
//                         {section.content.heading}
//                       </h1>
//                     )}
//                     {section.content?.subheading && (
//                       <p className="text-base sm:text-lg md:text-xl mb-8 drop-shadow-md leading-relaxed max-w-3xl mx-auto">
//                         {section.content.subheading}
//                       </p>
//                     )}

//                     <div className="flex gap-4 justify-center flex-wrap">
//                       {section.content?.heroButtons && section.content.heroButtons.length > 0 && (
//                         section.content.heroButtons.map((btn, btnIndex) => {
//                           const hasCustomBackground = section.styling?.ctaButtonBackgroundColor
//                           const hasCustomTextColor = section.styling?.ctaButtonTextColor
//                           const isBtnHovered = hoveredCtaId === `${section._id}-btn-${btnIndex}`
                          
//                           let buttonStyle = {}
//                           if (hasCustomBackground) buttonStyle.backgroundColor = isBtnHovered ? (section.styling.ctaButtonHoverBackgroundColor || section.styling.ctaButtonBackgroundColor) : section.styling.ctaButtonBackgroundColor
//                           if (hasCustomTextColor) buttonStyle.color = isBtnHovered ? (section.styling.ctaButtonHoverTextColor || section.styling.ctaButtonTextColor) : section.styling.ctaButtonTextColor
                          
//                           // Build className conditionally - exclude default colors if custom colors are set
//                           const baseClasses = "px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
//                           const defaultStyles = {
//                             primary: {
//                               bg: "bg-white",
//                               text: "text-gray-900",
//                               hover: "hover:bg-gray-100"
//                             },
//                             secondary: {
//                               bg: "bg-gray-600",
//                               text: "text-white",
//                               hover: "hover:bg-gray-700"
//                             },
//                             outline: {
//                               bg: "border-2 border-white",
//                               text: "text-white",
//                               hover: "hover:bg-white hover:text-gray-900"
//                             }
//                           }
//                           const buttonType = btn.style || 'primary'
//                           const styleDefaults = defaultStyles[buttonType] || defaultStyles.primary

//                           const buttonClassParts = [baseClasses]
//                           if (!hasCustomBackground) buttonClassParts.push(styleDefaults.bg)
//                           if (!hasCustomTextColor) buttonClassParts.push(styleDefaults.text)
//                           if (!hasCustomBackground) buttonClassParts.push(styleDefaults.hover)

//                           const buttonClassName = buttonClassParts.join(' ')
                          
//                           return (
//                             <a
//                               key={btn.id}
//                               href={btn.link || '#'}
//                               onClick={(e) => e.stopPropagation()}
//                               className={`${buttonClassName.trim()} transition-colors`}
//                               style={Object.keys(buttonStyle).length ? buttonStyle : undefined}
//                               onMouseEnter={() => setHoveredCtaId(`${section._id}-btn-${btnIndex}`)}
//                               onMouseLeave={() => setHoveredCtaId(null)}
//                             >
//                               {btn.text}
//                             </a>
//                           )
//                         })
//                       )}
//                       {ctaText && ctaLink && (
//                         (() => {
//                           const hasCustomBackground = section.styling?.ctaButtonBackgroundColor
//                           const hasCustomTextColor = section.styling?.ctaButtonTextColor
//                           const isCtaHovered = hoveredCtaId === `${section._id}-cta`
                          
//                           let ctaStyle = {}
//                           if (hasCustomBackground) ctaStyle.backgroundColor = isCtaHovered ? (section.styling.ctaButtonHoverBackgroundColor || section.styling.ctaButtonBackgroundColor) : section.styling.ctaButtonBackgroundColor
//                           if (hasCustomTextColor) ctaStyle.color = isCtaHovered ? (section.styling.ctaButtonHoverTextColor || section.styling.ctaButtonTextColor) : section.styling.ctaButtonTextColor
                          
//                           // Build className conditionally - exclude default colors if custom colors are set
//                           const baseClasses = "px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
//                           const defaultBackgroundClasses = hasCustomBackground ? "" : " bg-white"
//                           const defaultTextClasses = hasCustomTextColor ? "" : " text-gray-900 hover:bg-gray-100"
//                           const ctaClassName = `${baseClasses}${defaultBackgroundClasses}${defaultTextClasses}`
                          
//                           return (
//                             <a
//                               href={ctaLink}
//                               target="_blank"
//                               rel="noopener noreferrer"
//                               onClick={(e) => e.stopPropagation()}
//                               className={`${ctaClassName.trim()} transition-colors`}
//                               style={Object.keys(ctaStyle).length ? ctaStyle : undefined}
//                               onMouseEnter={() => setHoveredCtaId(`${section._id}-cta`)}
//                               onMouseLeave={() => setHoveredCtaId(null)}
//                             >
//                               {ctaText}
//                             </a>
//                           )
//                         })()
//                       )}
//                     </div>
//                   </div>
//                 </section>
//               )
//             }

//             // For non-hero sections: build inner content first, then wrap with media/sidebars
//             let innerContent = null

//             if (sectionType === 'products') {
//               innerContent = (
//                 <>
//                   {!hasTopHeader && (
//                     <div className="mb-12">
//                       <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{section.title}</h2>
//                       {section.description && (
//                         <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
//                       )}
//                     </div>
//                   )}

//                   <PublicProductsSection 
//                     websiteSlug={slug} 
//                     website={website} 
//                     section={section}
//                     sectionFormStyling={getFormStyling()}
//                     useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
//                     sectionType="products"
//                     onAddToCart={() => {
//                       console.log('[PublicWebsiteViewer] Product added to cart')
//                       setCurrentView('cart')
//                     }}
//                     onViewProduct={(product) => handleViewItem(product)}
//                   />
//                 </>
//               )
//             } else if (sectionType === 'tickets') {
//               innerContent = (
//                 <>
//                   {!hasTopHeader && (
//                     <div className="mb-12">
//                       <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{section.title}</h2>
//                       {section.description && (
//                         <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
//                       )}
//                     </div>
//                   )}

//                   <PublicTicketsSection
//                     websiteSlug={slug}
//                     website={website}
//                     section={section}
//                     sectionFormStyling={getFormStyling()}
//                     useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
//                     sectionType="tickets"
//                     onAddToCart={() => {
//                       console.log('[PublicWebsiteViewer] Ticket added to cart')
//                       setCurrentView('cart')
//                     }}
//                   />
//                 </>
//               )
//             } else if (sectionType === 'contact') {
//               innerContent = (
//                 <>
//                   {!hasTopHeader && (
//                     <div className="mb-12 text-center">
//                       <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{section.title}</h2>
//                       {section.description && (
//                         <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">{section.description}</p>
//                       )}
//                     </div>
//                   )}

//                   <ContactUsForm
//                     website={website}
//                     sectionFormStyling={getFormStyling()}
//                     useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
//                     sectionType="contact"
//                     customerProfile={customerAuthUser}
//                     customerAuthToken={customerAuthToken}
//                   />
//                 </>
//               )
//             } else if (sectionType === 'reviews' || section.type === 'reviews') {
//               innerContent = (<ReviewsSection website={website} section={section} sectionFormStyling={getFormStyling()} useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false} customerProfile={customerAuthUser} customerAuthToken={customerAuthToken} />)
//             } else if (sectionType === 'fixtures') {
//               innerContent = (
//                 <>
//                   {!hasTopHeader && (
//                     <div className="mb-12">
//                       <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{section.title}</h2>
//                       {section.description && (
//                         <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
//                       )}
//                     </div>
//                   )}
//                   <PublicFixturesSection website={website} section={section} sectionFormStyling={getFormStyling()} useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false} sectionType="fixtures" />
//                 </>
//               )
//             } else if (sectionType === 'points-table') {
//               innerContent = (
//                 <>
//                   {!hasTopHeader && (
//                     <div className="mb-12">
//                       <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{section.title}</h2>
//                       {section.description && (
//                         <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
//                       )}
//                     </div>
//                   )}
//                   <PublicPointsTable website={website} section={section} sectionFormStyling={getFormStyling()} useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false} sectionType="points-table" />
//                 </>
//               )
//             } else if (sectionType === 'resume') {
//               innerContent = (
//                 <>
//                   {!hasTopHeader && (
//                     <div className="mb-12">
//                       <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{section.title}</h2>
//                       {section.description && (
//                         <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
//                       )}
//                     </div>
//                   )}
//                   <PublicResumeUpload website={website} section={section} sectionFormStyling={getFormStyling()} useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false} sectionType="resume" />
//                 </>
//               )
//             } else if (sectionType === 'team' || sectionType === 'team-members') {
//               const teamMembers = section.content?.teamMembers || []
//               const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
//               const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, sectionType, section?._id)
//               const formStyling = getFormStyling()
//               const formStyle = isFormStyling ? {
//                 backgroundColor: formStyling.backgroundColor,
//                 color: formStyling.textColor
//               } : undefined
//               const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
//               const hoverFormStyle = isFormStyling ? {
//                 backgroundColor: formStyling.hoverBackgroundColor || formStyling.backgroundColor,
//                 color: formStyling.hoverTextColor || formStyling.textColor
//               } : undefined
//               console.log('[TEAM-SECTION] Styling decision:', { useDefaultFormStyling, isFormStyling, colors: formStyling })
//               console.log('PublicWebsiteViewer rendering team section:', section._id, section.title, 'teamMembers:', teamMembers.length, teamMembers.map(m => ({ name: m.name, position: m.position, hasImage: !!m.profileImage?.url })))
//               innerContent = (
//                 <div style={formStyle}>
//                   {!hasTopHeader && (
//                     <div className="mb-12 text-center">
//                       <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={textStyle}>{section.title}</h2>
//                       {section.description && (
//                         <p className="text-base sm:text-lg max-w-2xl mx-auto" style={textStyle}>{section.description}</p>
//                       )}
//                     </div>
//                   )}

//                   {teamMembers.length === 0 ? (
//                     <div className="rounded-lg p-8 text-center transition-colors" style={isFormStyling ? { backgroundColor: formStyling.backgroundColor, color: formStyling.textColor } : undefined}>
//                       <p style={textStyle}>No team members to display.</p>
//                     </div>
//                   ) : (
//                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
//                       {teamMembers.map((member) => (
//                         <div
//                           key={member.id}
//                           className="border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300"
//                           style={isFormStyling ? {
//                             backgroundColor: formStyling.backgroundColor,
//                             color: formStyling.textColor
//                           } : undefined}
//                           onMouseEnter={(e) => {
//                             if (isFormStyling && hoverFormStyle) {
//                               e.currentTarget.style.backgroundColor = hoverFormStyle.backgroundColor
//                               e.currentTarget.style.color = hoverFormStyle.color
//                             }
//                           }}
//                           onMouseLeave={(e) => {
//                             if (isFormStyling) {
//                               e.currentTarget.style.backgroundColor = formStyling.backgroundColor
//                               e.currentTarget.style.color = formStyling.textColor
//                             }
//                           }}
//                         >
//                           {/* Profile Image Container */}
//                           {member.profileImage?.url ? (
//                             <div className="w-full h-64 overflow-hidden bg-gray-100">
//                               <img
//                                 src={member.profileImage.url}
//                                 alt={member.name || 'Team member'}
//                                 className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
//                               />
//                             </div>
//                           ) : (
//                             <div className="w-full h-64 bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
//                               <div className="text-white text-4xl font-bold opacity-50">👤</div>
//                             </div>
//                           )}

//                           {/* Member Info */}
//                           <div className="p-4">
//                             <h3 className="text-lg font-semibold mb-1" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
//                               {member.name || 'Team Member'}
//                             </h3>

//                             {member.position && (
//                               <p className="text-sm font-medium mb-3" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
//                                 {member.position}
//                               </p>
//                             )}

//                             {member.bio && (
//                               <p className="text-sm line-clamp-3" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
//                                 {member.bio}
//                               </p>
//                             )}
//                           </div>
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               )
//             } else if (sectionType === 'audio') {
//               const audioItems = section.content?.audio || []
              
//               // Log audio section loading
//               console.log('[AUDIO-SECTION-LOAD] Audio section loaded:', {
//                 sectionId: section._id,
//                 sectionTitle: section.title,
//                 audioCount: audioItems.length,
//                 timestamp: new Date().toISOString(),
//                 audioFiles: audioItems.map((a, idx) => ({
//                   index: idx,
//                   id: a.id,
//                   name: a.name,
//                   filename: a.audioFile?.filename,
//                   hasSrc: !!a.audioFile?.url,
//                   srcLength: a.audioFile?.url?.length || 0,
//                   srcPreview: a.audioFile?.url ? a.audioFile.url.substring(0, 100) : 'no src',
//                   hasBanner: !!a.banner?.url
//                 }))
//               })
              
//               const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
//               const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, 'audio', section?._id)
//               const formStyling = getFormStyling()
//               const formStyle = isFormStyling ? {
//                 backgroundColor: formStyling.backgroundColor,
//                 color: formStyling.textColor
//               } : undefined
//               const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
//               const hoverFormStyle = isFormStyling ? {
//                 backgroundColor: formStyling.hoverBackgroundColor || formStyling.backgroundColor,
//                 color: formStyling.hoverTextColor || formStyling.textColor
//               } : undefined
              
//               // Calculate pagination
//               const totalAudioPages = Math.ceil(audioItems.length / 10)
//               const audioCurrentPage = itemPagination[section._id] || 1
//               const audioStartIdx = (audioCurrentPage - 1) * 10
//               const audioEndIdx = audioStartIdx + 10
//               const paginatedAudioItems = audioItems.slice(audioStartIdx, audioEndIdx)
              
//               innerContent = (
//                 <div style={formStyle}>
//                   {!hasTopHeader && (
//                     <div className="mb-12 text-center">
//                       <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={textStyle}>{section.title}</h2>
//                       {section.description && (
//                         <p className="text-base sm:text-lg max-w-2xl mx-auto" style={textStyle}>{section.description}</p>
//                       )}
//                     </div>
//                   )}

//                   {audioItems.length === 0 ? (
//                     <div className="rounded-lg p-8 text-center transition-colors" style={isFormStyling ? { backgroundColor: formStyling.backgroundColor, color: formStyling.textColor } : undefined}>
//                       <p style={textStyle}>No audio files to display.</p>
//                     </div>
//                   ) : (
//                     <>
//                       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//                         {paginatedAudioItems.map((audio) => (
//                           <div
//                             key={audio.id}
//                             className="border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300"
//                             style={isFormStyling ? {
//                               backgroundColor: formStyling.backgroundColor,
//                               color: formStyling.textColor
//                             } : undefined}
//                             onMouseEnter={(e) => {
//                               if (isFormStyling && hoverFormStyle) {
//                                 e.currentTarget.style.backgroundColor = hoverFormStyle.backgroundColor
//                                 e.currentTarget.style.color = hoverFormStyle.color
//                               }
//                             }}
//                             onMouseLeave={(e) => {
//                               if (isFormStyling) {
//                                 e.currentTarget.style.backgroundColor = formStyling.backgroundColor
//                                 e.currentTarget.style.color = formStyling.textColor
//                               }
//                             }}
//                           >
//                             {/* Audio Banner */}
//                             {audio.banner?.url ? (
//                               <div className="w-full h-44 overflow-hidden bg-gray-100">
//                                 <img
//                                   src={audio.banner.url}
//                                   alt={audio.banner.filename || audio.name || 'Audio banner'}
//                                   className="w-full h-full object-cover"
//                                 />
//                               </div>
//                             ) : (
//                               <div className="w-full h-44 bg-gradient-to-r from-purple-100 to-indigo-100 flex items-center justify-center">
//                                 <span className="text-purple-600">No Banner</span>
//                               </div>
//                             )}

//                             {/* Audio Player with Custom Controls */}
//                             <div className="p-6">
//                               {audio.audioFile?.url ? (
//                                 <AudioPlayer
//                                   src={audio.audioFile.url}
//                                   filename={audio.name || audio.audioFile.filename}
//                                   formStyle={isFormStyling ? { 
//                                     backgroundColor: formStyling.backgroundColor,
//                                     color: formStyling.textColor
//                                   } : { backgroundColor: '#F3F4F6' }}
//                                   textStyle={isFormStyling ? { color: formStyling.textColor } : { color: '#374151' }}
//                                   buttonStyle={isFormStyling ? {
//                                     backgroundColor: formStyling.buttonBackgroundColor || formStyling.backgroundColor,
//                                     color: formStyling.buttonTextColor || formStyling.textColor
//                                   } : { backgroundColor: '#3B82F6', color: '#FFFFFF' }}
//                                   hoverButtonStyle={isFormStyling ? {
//                                     backgroundColor: formStyling.hoverButtonBackgroundColor || formStyling.buttonBackgroundColor,
//                                     color: formStyling.hoverButtonTextColor || formStyling.buttonTextColor
//                                   } : { backgroundColor: '#2563EB', color: '#FFFFFF' }}
//                                   websiteId={effectiveWebsite?._id}
//                                   websiteSlug={effectiveWebsite?.slug || slug}
//                                 />
//                               ) : (
//                                 <div className="flex items-center justify-center h-20 bg-gray-200 rounded">
//                                   <span className="text-gray-500">No audio file</span>
//                                 </div>
//                               )}
//                             </div>

//                             {/* Audio Info */}
//                             <div className="p-4">
//                               <h3 className="text-lg font-semibold mb-2" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
//                                 {audio.name || 'Untitled Audio'}
//                               </h3>

//                               {audio.description && (
//                                 <p className="text-sm leading-relaxed" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
//                                   {audio.description}
//                                 </p>
//                               )}

//                               {audio.audioFile?.filename && (
//                                 <p className="text-xs mt-2" style={isFormStyling ? { color: formStyling.textColor } : undefined}>
//                                   {audio.audioFile.filename}
//                                 </p>
//                               )}
//                             </div>
//                           </div>
//                         ))}
//                       </div>

//                       {/* Show pagination if more than one page */}
//                       {totalAudioPages > 1 && (
//                         <PaginationControls
//                           currentPage={audioCurrentPage}
//                           totalPages={totalAudioPages}
//                           onPageChange={(page) => handleItemPageChange(section._id, page)}
//                           sectionFormStyling={getFormStyling()}
//                           useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
//                           section={section}
//                           sectionType={sectionType}
//                         />
//                       )}
//                     </>
//                   )}
//                 </div>
//               )
//             } else if (sectionType === 'left-sidebar') {
//               const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
//               const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, 'left-sidebar', section?._id)
//               const formStyling = getFormStyling()
//               const formStyle = isFormStyling ? {
//                 backgroundColor: formStyling.backgroundColor,
//                 color: formStyling.textColor
//               } : undefined
//               const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
//               const hoverFormStyle = isFormStyling ? {
//                 backgroundColor: formStyling.hoverBackgroundColor || formStyling.backgroundColor,
//                 color: formStyling.hoverTextColor || formStyling.textColor
//               } : undefined
//               console.log('[LEFT-SIDEBAR-SECTION] Styling decision:', { useDefaultFormStyling, isFormStyling, colors: formStyling })
//               innerContent = (
//                 <div style={formStyle} className="p-6 rounded-lg">
//                   {!hasTopHeader && (
//                     <div className="mb-6">
//                       <h2 className="text-xl font-bold mb-2" style={textStyle}>{section.title}</h2>
//                       {section.description && (
//                         <p className="text-sm" style={textStyle}>{section.description}</p>
//                       )}
//                     </div>
//                   )}
//                   <div className="space-y-4">
//                     {section.content?.items?.map((item, index) => (
//                       <div
//                         key={index}
//                         className="p-4 rounded shadow-sm transition-all duration-300 cursor-default"
//                         style={isFormStyling ? {
//                           backgroundColor: formStyling.backgroundColor,
//                           color: formStyling.textColor
//                         } : undefined}
//                         onMouseEnter={(e) => {
//                           if (isFormStyling && hoverFormStyle) {
//                             e.currentTarget.style.backgroundColor = hoverFormStyle.backgroundColor
//                             e.currentTarget.style.color = hoverFormStyle.color
//                           }
//                         }}
//                         onMouseLeave={(e) => {
//                           if (isFormStyling) {
//                             e.currentTarget.style.backgroundColor = formStyling.backgroundColor
//                             e.currentTarget.style.color = formStyling.textColor
//                           }
//                         }}
//                       >
//                         <h3 className="font-semibold" style={isFormStyling ? { color: formStyling.textColor } : undefined}>{item.title}</h3>
//                         <p className="text-sm" style={isFormStyling ? { color: formStyling.textColor } : undefined}>{item.description}</p>
//                       </div>
//                     )) || <p className="text-sm" style={textStyle}>No sidebar content available.</p>}
//                   </div>
//                 </div>
//               )
//             } else if (sectionType === 'right-sidebar') {
//               const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
//               const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, 'right-sidebar', section?._id)
//               const formStyling = getFormStyling()
//               const formStyle = isFormStyling ? {
//                 backgroundColor: formStyling.backgroundColor,
//                 color: formStyling.textColor
//               } : undefined
//               const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
//               const hoverFormStyle = isFormStyling ? {
//                 backgroundColor: formStyling.hoverBackgroundColor || formStyling.backgroundColor,
//                 color: formStyling.hoverTextColor || formStyling.textColor
//               } : undefined
//               console.log('[RIGHT-SIDEBAR-SECTION] Styling decision:', { useDefaultFormStyling, isFormStyling, colors: formStyling })
//               innerContent = (
//                 <div style={formStyle} className="p-6 rounded-lg">
//                   {!hasTopHeader && (
//                     <div className="mb-6">
//                       <h2 className="text-xl font-bold mb-2" style={textStyle}>{section.title}</h2>
//                       {section.description && (
//                         <p className="text-sm" style={textStyle}>{section.description}</p>
//                       )}
//                     </div>
//                   )}
//                   <div className="space-y-4">
//                     {section.content?.items?.map((item, index) => (
//                       <div
//                         key={index}
//                         className="p-4 rounded shadow-sm transition-all duration-300 cursor-default"
//                         style={isFormStyling ? {
//                           backgroundColor: formStyling.backgroundColor,
//                           color: formStyling.textColor
//                         } : undefined}
//                         onMouseEnter={(e) => {
//                           if (isFormStyling && hoverFormStyle) {
//                             e.currentTarget.style.backgroundColor = hoverFormStyle.backgroundColor
//                             e.currentTarget.style.color = hoverFormStyle.color
//                           }
//                         }}
//                         onMouseLeave={(e) => {
//                           if (isFormStyling) {
//                             e.currentTarget.style.backgroundColor = formStyling.backgroundColor
//                             e.currentTarget.style.color = formStyling.textColor
//                           }
//                         }}
//                       >
//                         <h3 className="font-semibold" style={isFormStyling ? { color: formStyling.textColor } : undefined}>{item.title}</h3>
//                         <p className="text-sm" style={isFormStyling ? { color: formStyling.textColor } : undefined}>{item.description}</p>
//                       </div>
//                     )) || <p className="text-sm" style={textStyle}>No sidebar content available.</p>}
//                   </div>
//                 </div>
//               )
//             } else if (sectionType === 'section-items') {
//               const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
//               const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, 'section-items', section?._id)
//               const useDefaultButtonStyling = effectiveWebsite?.useDefaultStyling !== false
//               const isButtonStyling = useDefaultButtonStyling || isButtonStylingApplied(website?.sectionFormStyling, 'section-items', section?._id)
              
//               const formStyling = getFormStyling()
//               const buttonStyling = getButtonStyling()
//               // Ensure styling colors have fallbacks
//               const formBgColor = isFormStyling ? formStyling.backgroundColor : undefined
//               const formTextColor = isFormStyling ? formStyling.textColor : undefined
//               const formHoverBgColor = isFormStyling ? (formStyling.hoverBackgroundColor || formBgColor || '#F9FAFB') : undefined
//               const formHoverTextColor = isFormStyling ? (formStyling.hoverTextColor || formTextColor || '#374151') : undefined
              
//               const btnBgColor = isButtonStyling ? (buttonStyling.buttonBackgroundColor || buttonStyling.backgroundColor) : undefined
//               const btnTextColor = isButtonStyling ? (buttonStyling.buttonTextColor || buttonStyling.textColor) : undefined
//               const btnHoverBgColor = isButtonStyling ? (buttonStyling.hoverButtonBackgroundColor || buttonStyling.hoverBackgroundColor || btnBgColor || '#2563EB') : undefined
//               const btnHoverTextColor = isButtonStyling ? (buttonStyling.hoverButtonTextColor || buttonStyling.hoverTextColor || btnTextColor || '#FFFFFF') : undefined
               
//               const formStyle = isFormStyling ? {
//                 backgroundColor: formBgColor,
//                 color: formTextColor
//               } : undefined
//               const textStyle = isFormStyling ? { 
//                 color: formTextColor
//               } : undefined
//               const hoverFormStyle = isFormStyling ? {
//                 backgroundColor: formHoverBgColor,
//                 color: formHoverTextColor,
//                 borderColor: formHoverTextColor
//               } : undefined
               
//               const buttonStyle = isButtonStyling ? {
//                 backgroundColor: btnBgColor,
//                 color: btnTextColor,
//                 borderColor: btnBgColor
//               } : undefined
//               const buttonHoverStyle = isButtonStyling ? {
//                 backgroundColor: btnHoverBgColor,
//                 color: btnHoverTextColor,
//                 borderColor: btnHoverBgColor
//               } : undefined
               
//               const cardStyle = {
//                 backgroundColor: isFormStyling ? formBgColor : '#ffffff',
//                 color: isFormStyling ? formTextColor : '#111827',
//                 borderColor: isFormStyling ? (formTextColor || formBgColor) : '#e5e7eb'
//               }
//               const cardHoverStyle = isFormStyling ? {
//                 backgroundColor: formHoverBgColor,
//                 color: formHoverTextColor,
//                 borderColor: formHoverTextColor
//               } : undefined
               
//               const imageBoxStyle = (isHovered) => ({
//                 backgroundColor: isFormStyling ? (isHovered ? formHoverBgColor : formBgColor) : '#f3f4f6',
//                 color: isFormStyling ? (isHovered ? formHoverTextColor : formTextColor) : undefined
//               })
//               const currentPage = itemPagination[section._id] || 1
//               const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE)
//               const startIdx = (currentPage - 1) * ITEMS_PER_PAGE
//               const displayedItems = needsPagination 
//                 ? filteredItems.slice(startIdx, startIdx + ITEMS_PER_PAGE)
//                 : filteredItems
              
//               innerContent = (
//                 <div style={formStyle}>
//                   {!hasTopHeader && (
//                     <div className="mb-12 text-center">
//                       <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={textStyle}>{section.title}</h2>
//                       {section.description && (
//                         <p className="text-base sm:text-lg max-w-2xl mx-auto" style={textStyle}>{section.description}</p>
//                       )}
//                     </div>
//                   )}
                  
//                   {/* Search Bar */}
//                   <div className="mb-6">
//                     <input
//                       type="text"
//                       placeholder="Search items by title or description..."
//                       value={itemFilters[section._id] || ''}
//                       onChange={(e) => handleItemFilterChange(section._id, e.target.value)}
//                       className="input-field w-full border-2 rounded-lg px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
//                       style={isFormStyling ? {
//                         backgroundColor: formStyling.backgroundColor,
//                         color: formStyling.textColor,
//                         borderColor: formStyling.textColor
//                       } : { borderColor: '#d1d5db', color: '#111827', backgroundColor: '#ffffff' }}
//                     />
//                   </div>
                  
//                   {/* Pagination Info */}
//                   {needsPagination && (
//                     <div className="mb-4 flex items-center justify-between">
//                       <div className="text-sm" style={textStyle}>
//                         Showing {startIdx + 1} to {Math.min(startIdx + ITEMS_PER_PAGE, filteredItems.length)} of {filteredItems.length} items
//                       </div>
//                     </div>
//                   )}
                  
//                   {/* Items Grid */}
//                   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//                     {displayedItems.map((item, itemIdx) => {
//                       const cardKey = `${section._id}-${itemIdx}`
//                       return (
//                         <div
//                           key={cardKey}
//                           className={`border-2 rounded-lg overflow-hidden hover:shadow-xl transition-all duration-200 cursor-default ${!isFormStyling ? 'bg-white text-gray-900' : ''}`}
//                           style={hoveredSectionItemCards.has(cardKey) && cardHoverStyle ? cardHoverStyle : cardStyle}
//                           onMouseEnter={() => setHoveredSectionItemCards(prev => new Set([...prev, cardKey]))}
//                           onMouseLeave={() => setHoveredSectionItemCards(prev => { const newSet = new Set(prev); newSet.delete(cardKey); return newSet; })}
//                         >
//                         {/* Gallery/Image Section */}
//                         {(item.gallery && item.gallery.length > 0) ? (
//                           <div className="space-y-3 p-3" style={imageBoxStyle(hoveredSectionItemCards.has(cardKey))}>
//                             <div
//                               role="button"
//                               tabIndex={0}
//                               onClick={(e) => {
//                                 try {
//                                   e.preventDefault()
//                                   e.stopPropagation()
//                                   if (e.stopImmediatePropagation) e.stopImmediatePropagation()
//                                   handleViewItem(item, 0)
//                                 } catch (err) {
//                                   console.error('[PublicWebsiteViewer] Error opening gallery:', err)
//                                 }
//                               }}
//                               onKeyDown={(e) => {
//                                 if (e.key === 'Enter' || e.key === ' ') {
//                                   e.preventDefault()
//                                   e.stopPropagation()
//                                   handleViewItem(item, 0)
//                                 }
//                               }}
//                               className="relative overflow-hidden rounded h-40 cursor-pointer hover:opacity-90 transition-opacity"
//                               style={imageBoxStyle(hoveredSectionItemCards.has(cardKey))}
//                             >
//                               {item.gallery[0].image?.url || item.gallery[0].url ? (
//                                 <img
//                                   src={item.gallery[0].image?.url || item.gallery[0].url}
//                                   alt={item.gallery[0].title || item.title}
//                                   className="w-full h-full object-cover pointer-events-none"
//                                   draggable={false}
//                                   onMouseDown={(e) => e.preventDefault()}
//                                 />
//                               ) : (item.gallery[0].video?.url || item.gallery[0].videoUrl || item.gallery[0].video) ? (
//                                 <video
//                                   src={getVideoUrl(item.gallery[0].video || item.gallery[0].videoUrl || item.gallery[0].video)}
//                                   poster={getVideoPoster(item.gallery[0].video || item.gallery[0].videoUrl || item.gallery[0].video)}
//                                   className="w-full h-full object-cover pointer-events-none"
//                                   playsInline
//                                   autoPlay
//                                   muted
//                                   loop
//                                   preload="metadata"
//                                   onMouseDown={(e) => e.preventDefault()}
//                                 />
//                               ) : null}
//                               {/* Top video overlay removed per design: keep other video metadata unchanged */}
//                             </div>
//                             <div className="flex items-center justify-between">
//                               <div className="flex items-center gap-2">
//                                 {(item.gallery || []).slice(0, 4).map((g, gi) => {
//                                   const thumb = getVideoPoster(g.video || g.videoUrl) || g.image?.url || g.url
//                                   return thumb ? (
//                                     <button 
//                                       key={g.id || gi} 
//                                       type="button"
//                                       onClick={(e) => {
//                                         try {
//                                           e.preventDefault()
//                                           e.stopPropagation()
//                                           if (e.stopImmediatePropagation) e.stopImmediatePropagation()
//                                           handleViewItem(item, gi)
//                                         } catch (err) {
//                                           console.error('[PublicWebsiteViewer] Error opening gallery from thumbnail:', err)
//                                         }
//                                       }} 
//                                       className="w-8 h-8 rounded overflow-hidden border-2 border-gray-200 hover:border-primary-400 transition-colors cursor-pointer"
//                                     >
//                                       <img src={thumb} alt={`thumb-${gi}`} className="w-full h-full object-cover pointer-events-none" draggable={false} onMouseDown={(e) => e.preventDefault()} />
//                                     </button>
//                                   ) : (
//                                     <div key={g.id || gi} className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center text-xs text-gray-500">📹</div>
//                                   )
//                                 })}
//                                 {item.gallery.length > 4 && (
//                                   <span className="text-sm" style={isFormStyling ? {
//                                     color: hoveredSectionItemCards.has(cardKey) ? formHoverTextColor : formTextColor
//                                   } : { color: '#4B5563' }}>+{item.gallery.length - 4}</span>
//                                 )}
//                               </div>
//                               <button
//                                 type="button"
//                                 onClick={(e) => {
//                                   e.preventDefault()
//                                   e.stopPropagation()
//                                   if (e.stopImmediatePropagation) e.stopImmediatePropagation()
//                                   handleViewItem(item, 0)
//                                 }}
//                                 className={`px-3 py-1 rounded-lg transition-colors text-sm font-medium ${isButtonStyling ? '' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
//                                 style={hoveredSectionItemButtons.has(`view-${itemIdx}`) && buttonHoverStyle ? buttonHoverStyle : buttonStyle || { backgroundColor: '#0EA5E9', color: 'white' }}
//                                 onMouseEnter={() => setHoveredSectionItemButtons(prev => new Set([...prev, `view-${itemIdx}`]))}
//                                 onMouseLeave={() => setHoveredSectionItemButtons(prev => { const newSet = new Set(prev); newSet.delete(`view-${itemIdx}`); return newSet; })}
//                               >
//                                 View
//                               </button>
//                             </div>
//                           </div>
//                         ) : item.image ? (
//                           <div
//                             role="button"
//                             tabIndex={0}
//                             className="relative overflow-hidden rounded h-40 cursor-pointer hover:opacity-90 transition-opacity m-3"
//                             style={imageBoxStyle(hoveredSectionItemCards.has(cardKey))}
//                             onClick={(e) => {
//                               try {
//                                 e.preventDefault()
//                                 e.stopPropagation()
//                                 if (e.stopImmediatePropagation) e.stopImmediatePropagation()
//                                 handleViewItem(item, 0)
//                               } catch (err) {
//                                 console.error('[PublicWebsiteViewer] Error opening item from image click:', err)
//                               }
//                             }}
//                             onKeyDown={(e) => {
//                               if (e.key === 'Enter' || e.key === ' ') {
//                                 e.preventDefault()
//                                 e.stopPropagation()
//                                 handleViewItem(item, 0)
//                               }
//                             }}
//                           >
//                             <img
//                               src={item.image}
//                               alt={item.title}
//                               className="w-full h-full object-cover pointer-events-none"
//                               draggable={false}
//                               onMouseDown={(e) => e.preventDefault()}
//                             />
//                           </div>
//                         ) : (item.video || item.video?.url) ? (
//                           <div
//                             role="button"
//                             tabIndex={0}
//                             className="relative overflow-hidden rounded h-40 cursor-pointer hover:opacity-90 transition-opacity m-3"
//                             onClick={(e) => {
//                               try {
//                                 e.preventDefault()
//                                 e.stopPropagation()
//                                 if (e.stopImmediatePropagation) e.stopImmediatePropagation()
//                                 handleViewItem(item, 0)
//                               } catch (err) {
//                                 console.error('[PublicWebsiteViewer] Error opening video item:', err)
//                               }
//                             }}
//                             onKeyDown={(e) => {
//                               if (e.key === 'Enter' || e.key === ' ') {
//                                 e.preventDefault()
//                                 e.stopPropagation()
//                                 handleViewItem(item, 0)
//                               }
//                             }}
//                           >
//                             <video
//                               src={getVideoUrl(item.video)}
//                               poster={getVideoPoster(item.video)}
//                               className="w-full h-full object-cover pointer-events-none"
//                               playsInline
//                               autoPlay
//                               muted
//                               loop
//                               preload="metadata"
//                               draggable={false}
//                               onMouseDown={(e) => e.preventDefault()}
//                             />
//                             {/* Top video overlay removed per design: keep other video metadata unchanged */}
//                           </div>
//                         ) : null}
                        
//                         {/* Content Section */}
//                         <div className="p-4" style={isFormStyling ? {
//                           backgroundColor: hoveredSectionItemCards.has(cardKey) ? formHoverBgColor : formBgColor,
//                           color: hoveredSectionItemCards.has(cardKey) ? formHoverTextColor : formTextColor
//                         } : undefined}>
//                           {item.title && (
//                             <h4 className="text-lg font-semibold mb-2" style={isFormStyling ? {
//                               color: hoveredSectionItemCards.has(cardKey) ? formHoverTextColor : formTextColor
//                             } : undefined}>
//                               {item.title}
//                             </h4>
//                           )}
//                           {item.description && (
//                             <p className="text-sm mb-3" style={isFormStyling ? {
//                               color: hoveredSectionItemCards.has(cardKey) ? formHoverTextColor : formTextColor
//                             } : undefined}>
//                               {item.description}
//                             </p>
//                           )}
                          
//                           {/* Price Display */}
//                           {item.price !== undefined && item.price !== null && item.price !== '' && (
//                             <div className="mb-3">
//                               <span className="text-lg font-bold" style={isFormStyling ? {
//                                 color: hoveredSectionItemCards.has(cardKey) ? formHoverTextColor : formTextColor
//                               } : undefined}>
//                                 {formatPrice(item.price, item.currency)}
//                               </span>
//                             </div>
//                           )}
                          
//                           {/* Action Buttons */}
//                           <div className="flex flex-col gap-2">
//                             {item.link && (
//                               <a
//                                 href={item.link}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors"
//                                 style={hoveredSectionItemButtons.has(`link-${itemIdx}`) && buttonHoverStyle ? buttonHoverStyle : buttonStyle || { backgroundColor: '#0EA5E9', color: 'white' }}
//                                 onMouseEnter={() => setHoveredSectionItemButtons(prev => new Set([...prev, `link-${itemIdx}`]))}
//                                 onMouseLeave={() => setHoveredSectionItemButtons(prev => { const newSet = new Set(prev); newSet.delete(`link-${itemIdx}`); return newSet; })}
//                               >
//                                 {item.buttonText || 'View'}
//                               </a>
//                             )}
                            
//                             {(item.whatsappContact || item.whatsappNumber) && (
//                               <button
//                                 onClick={() => {
//                                   console.log('[PublicWebsiteViewer] ✅ Section item WhatsApp button clicked:', {
//                                     itemTitle: item.title,
//                                     itemId: item.id,
//                                     sectionType: 'section-items',
//                                     phoneNumber: item.whatsappContact || item.whatsappNumber,
//                                     timestamp: new Date().toISOString(),
//                                     buttonColor: '#10B981',
//                                     colorVerified: true,
//                                     styling: 'permanent green with !important flags',
//                                     deviceWidth: window.innerWidth,
//                                     deviceType: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop'
//                                   })
//                                   handleItemWhatsAppContact(item)
//                                 }}
//                                 type="button"
//                                 className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 hover:shadow-md permanent-whatsapp-button"
//                                 style={{
//                                   backgroundColor: hoveredSectionItemButtons.has(`whatsapp-${itemIdx}`) ? '#059669' : '#10B981',
//                                   color: '#FFFFFF',
//                                   border: 'none',
//                                   cursor: 'pointer',
//                                   fontWeight: 600,
//                                   boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
//                                   padding: '0.5rem 0.75rem',
//                                   fontSize: '0.875rem',
//                                   display: 'inline-flex',
//                                   alignItems: 'center',
//                                   gap: '0.5rem',
//                                   whiteSpace: 'nowrap',
//                                   transition: 'all 200ms'
//                                 }}
//                                 title="Contact via WhatsApp"
//                                 aria-label={`Contact ${item.title} via WhatsApp`}
//                                 onMouseEnter={() => setHoveredSectionItemButtons(prev => new Set([...prev, `whatsapp-${itemIdx}`]))}
//                                 onMouseLeave={() => setHoveredSectionItemButtons(prev => { const newSet = new Set(prev); newSet.delete(`whatsapp-${itemIdx}`); return newSet; })}
//                               >
//                                 <WhatsAppIcon className="w-4 h-4 whatsapp-icon" style={{ color: '#FFFFFF' }} />
//                                 Contact via WhatsApp
//                               </button>
//                             )}
//                           </div>
//                         </div>
//                       </div>
//                     )})}
//                   </div>
                  
//                   {/* Pagination Controls */}
//                   {needsPagination && (
//                     <PaginationControls
//                       currentPage={currentPage}
//                       totalPages={totalPages}
//                       onPageChange={(page) => handleItemPageChange(section._id, page)}
//                       sectionFormStyling={getFormStyling()}
//                       useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
//                       section={section}
//                       sectionType={sectionType}
//                     />
//                   )}
//                 </div>
//               )
//             } else if (sectionType === 'image-gallery-modal') {
//               const images = section.content?.images || []
//               const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
//               const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, 'image-gallery-modal', section?._id)
//               const formStyling = getFormStyling()
//               const formStyle = isFormStyling ? {
//                 backgroundColor: formStyling.backgroundColor,
//                 color: formStyling.textColor
//               } : undefined
//               const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
//               innerContent = (
//                 <div style={formStyle}>
//                   {!hasTopHeader && (
//                     <div className="mb-12 text-center">
//                       <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={textStyle}>{section.title}</h2>
//                       {section.description && (
//                         <p className="text-base sm:text-lg max-w-2xl mx-auto" style={textStyle}>{section.description}</p>
//                       )}
//                     </div>
//                   )}
//                   <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
//                     {images.map((image, index) => (
//                       <div
//                         key={index}
//                         className="relative group cursor-pointer"
//                         style={isFormStyling ? {
//                           backgroundColor: formStyling.backgroundColor,
//                           color: formStyling.textColor
//                         } : undefined}
//                         onClick={() => {
//                           setGalleryItems(images)
//                           setGalleryStartIndex(index)
//                           setShowGalleryModal(true)
//                         }}
//                       >
//                         <img
//                           src={image.url}
//                           alt={image.alt || `Gallery image ${index + 1}`}
//                           className="w-full h-48 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow"
//                         />
//                         <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
//                           <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity">View</span>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                   {images.length === 0 && <p className="text-center" style={textStyle}>No images to display.</p>}
//                 </div>
//               )
//             } else if (sectionType === 'crypto-product') {
//               innerContent = (
//                 <CryptoSection website={website} websiteId={website._id} section={section} sectionFormStyling={getFormStyling()} useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false} sectionType="crypto-product" customerProfile={customerAuthUser} customerAuthToken={customerAuthToken} />
//               )
//             } else if (sectionType === 'section-items' || section.type === 'section-items' || section.sectionType === 'section-items') {
//               // Fallback handler for section-items if not caught above (shouldn't happen but safety check)
//               console.warn('[PublicWebsiteViewer] Section Items caught in fallback handler - should have been caught by explicit handler at line 1618')
//               // Re-route to the proper section-items handler above by recursively calling (this is a safety net)
//               // For now, render nothing to prevent double rendering
//               innerContent = <div className="text-center text-gray-600 py-8">Section Items section not properly rendered</div>
//             } else {
//               // Default generic content
//               let itemsContent = null
//               if (section.content.items && section.content.items.length > 0) {
//                 const q = (itemFilters[section._id] || '').toString().trim().toLowerCase()
//                 const filteredItems = (section.content.items || []).filter(item => {
//                   const title = (item.title || '').toString().toLowerCase()
//                   const desc = (item.description || '').toString().toLowerCase()
//                   return !q || title.includes(q) || desc.includes(q)
//                 })

//                 // Pagination logic: show 10 items per page if more than 10 items
//                 const needsPagination = filteredItems.length > ITEMS_PER_PAGE
//                 const currentPage = itemPagination[section._id] || 1
//                 const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE)
//                 const startIdx = (currentPage - 1) * ITEMS_PER_PAGE
//                 const displayedItems = needsPagination 
//                   ? filteredItems.slice(startIdx, startIdx + ITEMS_PER_PAGE)
//                   : filteredItems

//                 // Apply section form styling
//                 const useDefaultFormStyling = effectiveWebsite?.useDefaultStyling !== false
//                 const isFormStyling = useDefaultFormStyling || isFormStylingApplied(website?.sectionFormStyling, sectionType, section?._id)
//                 const useDefaultButtonStyling = effectiveWebsite?.useDefaultStyling !== false
//                 const isButtonStyling = useDefaultButtonStyling || isButtonStylingApplied(website?.sectionFormStyling, sectionType, section?._id)
//                 const formStyling = getFormStyling()
//                 const buttonStyling = getButtonStyling()
//                 const formStyle = isFormStyling ? {
//                   backgroundColor: formStyling.backgroundColor,
//                   color: formStyling.textColor
//                 } : undefined
//                 const textStyle = isFormStyling ? { color: formStyling.textColor } : undefined
//                 const hoverFormStyle = isFormStyling ? {
//                   backgroundColor: formStyling.hoverBackgroundColor || formStyling.backgroundColor,
//                   color: formStyling.hoverTextColor || formStyling.textColor
//                 } : undefined
//                 const buttonStyle = isButtonStyling ? {
//                   backgroundColor: buttonStyling.buttonBackgroundColor || buttonStyling.backgroundColor,
//                   color: buttonStyling.buttonTextColor || buttonStyling.textColor,
//                   borderColor: buttonStyling.buttonBackgroundColor || buttonStyling.backgroundColor
//                 } : undefined
//                 const buttonHoverStyle = isButtonStyling ? {
//                   backgroundColor: buttonStyling.hoverButtonBackgroundColor || buttonStyling.hoverBackgroundColor || buttonStyling.buttonBackgroundColor || buttonStyling.backgroundColor,
//                   color: buttonStyling.hoverButtonTextColor || buttonStyling.hoverTextColor || buttonStyling.buttonTextColor || buttonStyling.textColor
//                 } : undefined
//                 const imageWrapperStyle = isFormStyling ? {
//                   backgroundColor: formStyling.backgroundColor,
//                   color: formStyling.textColor
//                 } : { backgroundColor: '#f3f4f6' }

//                 itemsContent = (
//                   <div className="mt-4">
//                     <div className="mb-4">
//                       <input
//                         type="text"
//                         placeholder="Search items by title or description..."
//                         value={itemFilters[section._id] || ''}
//                         onChange={(e) => handleItemFilterChange(section._id, e.target.value)}
//                         className="input-field w-full"
//                         style={isFormStyling ? {
//                           backgroundColor: formStyling.backgroundColor,
//                           color: formStyling.textColor,
//                           borderColor: formStyling.textColor
//                         } : undefined}
//                       />
//                     </div>

//                     {needsPagination && (
//                       <div className="mb-4 flex items-center justify-between">
//                         <div className="text-sm text-gray-600" style={textStyle}>
//                           Showing {startIdx + 1} to {Math.min(startIdx + ITEMS_PER_PAGE, filteredItems.length)} of {filteredItems.length} items
//                         </div>
//                       </div>
//                     )}

//                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//                       {displayedItems.map((item, itemIdx) => (
//                         <div key={itemIdx} className={`rounded-lg shadow p-6 hover:shadow-lg transition-shadow ${isFormStyling ? '' : 'bg-white'}`} style={isFormStyling ? {
//                           backgroundColor: formStyling.backgroundColor,
//                           color: formStyling.textColor
//                         } : undefined}
//                         onMouseEnter={(e) => {
//                           if (isFormStyling && hoverFormStyle) {
//                             e.currentTarget.style.backgroundColor = hoverFormStyle.backgroundColor
//                             e.currentTarget.style.color = hoverFormStyle.color
//                           }
//                         }}
//                         onMouseLeave={(e) => {
//                           if (isFormStyling) {
//                             e.currentTarget.style.backgroundColor = formStyling.backgroundColor
//                             e.currentTarget.style.color = formStyling.textColor
//                           }
//                         }}
//                         >
//                           {item.gallery && item.gallery.length > 0 ? (
//                             <div className="space-y-3 mb-4">
//                               <div
//                                 role="button"
//                                 tabIndex={0}
//                                 onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation() }}
//                                 onClick={(e) => {
//                                   try {
//                                     e.preventDefault()
//                                     e.stopPropagation()
//                                     if (e.stopImmediatePropagation) e.stopImmediatePropagation()
//                                     console.log('[PublicWebsiteViewer] Gallery main image clicked:', { title: item.title, itemId: item.id })
//                                     handleViewItem(item, 0)
//                                   } catch (err) {
//                                     console.error('[PublicWebsiteViewer] Error opening gallery:', err)
//                                   }
//                                 }}
//                                 onKeyDown={(e) => {
//                                   if (e.key === 'Enter' || e.key === ' ') {
//                                     e.preventDefault()
//                                     e.stopPropagation()
//                                     handleViewItem(item, 0)
//                                   }
//                                 }}
//                                 className="relative overflow-hidden rounded h-48 cursor-pointer hover:opacity-90 transition-opacity"
//                                 style={imageWrapperStyle}
//                               >
//                                 {item.gallery[0].image?.url || item.gallery[0].url ? (
//                                   <img
//                                     src={item.gallery[0].image?.url || item.gallery[0].url}
//                                     alt={item.gallery[0].title || item.title}
//                                     className="w-full h-full object-cover pointer-events-none"
//                                     draggable={false}
//                                     onMouseDown={(e) => e.preventDefault()}
//                                   />
//                                 ) : (item.gallery[0].video?.url || item.gallery[0].videoUrl || item.gallery[0].video) ? (
//                                   <video
//                                     src={getVideoUrl(item.gallery[0].video || item.gallery[0].videoUrl || item.gallery[0].video)}
//                                     poster={getVideoPoster(item.gallery[0].video || item.gallery[0].videoUrl || item.gallery[0].video)}
//                                     className="w-full h-full object-cover pointer-events-none"
//                                     playsInline
//                                     autoPlay
//                                     muted
//                                     loop
//                                     preload="metadata"
//                                     onMouseDown={(e) => e.preventDefault()}
//                                   />
//                                 ) : null}
//                                 {/* Top video overlay removed per design: keep other video metadata unchanged */}
//                               </div>
//                               <div className="flex items-center justify-between">
//                                 <div className="flex items-center gap-2">
//                                   {(item.gallery || []).slice(0, 4).map((g, gi) => {
//                                     const thumb = getVideoPoster(g.video || g.videoUrl) || g.image?.url || g.url
//                                     return thumb ? (
//                                       <button 
//                                         key={g.id || gi} 
//                                         type="button"
//                                         onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation() }}
//                                         onClick={(e) => {
//                                           try {
//                                             e.preventDefault()
//                                             e.stopPropagation()
//                                             if (e.stopImmediatePropagation) e.stopImmediatePropagation()
//                                             console.log('[PublicWebsiteViewer] Gallery thumbnail clicked:', { itemTitle: item.title, thumbIndex: gi, itemId: item.id })
//                                             handleViewItem(item, gi)
//                                           } catch (err) {
//                                             console.error('[PublicWebsiteViewer] Error opening gallery from thumbnail:', err)
//                                           }
//                                         }} 
//                                         className="w-10 h-10 rounded overflow-hidden border-2 border-gray-200 hover:border-primary-400 transition-colors cursor-pointer"
//                                       >
//                                         <img src={thumb} alt={`thumb-${gi}`} className="w-full h-full object-cover pointer-events-none" draggable={false} onMouseDown={(e) => e.preventDefault()} />
//                                       </button>
//                                     ) : (
//                                       <div key={g.id || gi} className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center text-xs text-gray-500">Video</div>
//                                     )
//                                   })}
//                                   {item.gallery.length > 4 && (
//                                     <span className="text-sm text-gray-600">+{item.gallery.length - 4}</span>
//                                   )}
//                                 </div>
//                                 <button
//                                   type="button"
//                                   onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation() }}
//                                   onClick={(e) => {
//                                     e.preventDefault()
//                                     e.stopPropagation()
//                                     if (e.stopImmediatePropagation) e.stopImmediatePropagation()
//                                     handleViewItem(item, 0)
//                                   }}
//                                   className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium ${isButtonStyling ? '' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
//                                   style={buttonStyle || { backgroundColor: '#3B82F6', color: 'white' }}
//                                   onMouseEnter={(e) => {
//                                     if (isButtonStyling && buttonHoverStyle) {
//                                       e.currentTarget.style.backgroundColor = buttonHoverStyle.backgroundColor
//                                       e.currentTarget.style.color = buttonHoverStyle.color
//                                     }
//                                   }}
//                                   onMouseLeave={(e) => {
//                                     if (isButtonStyling && buttonStyle) {
//                                       e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor
//                                       e.currentTarget.style.color = buttonStyle.color
//                                     }
//                                   }}
//                                 >
//                                   View
//                                 </button>
//                               </div>
//                             </div>
//                           ) : item.image ? (
//                             <div
//                               role="button"
//                               tabIndex={0}
//                               className="relative overflow-hidden rounded h-48 cursor-pointer hover:opacity-90 transition-opacity mb-4"
//                               style={imageWrapperStyle}
//                               onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation() }}
//                               onClick={(e) => {
//                                 try {
//                                   e.preventDefault()
//                                   e.stopPropagation()
//                                   if (e.stopImmediatePropagation) e.stopImmediatePropagation()
//                                   console.log('[PublicWebsiteViewer] Item single image clicked:', { title: item.title, itemId: item.id, image: item.image })
//                                   handleViewItem(item, 0)
//                                 } catch (err) {
//                                   console.error('[PublicWebsiteViewer] Error opening item from image click:', err)
//                                 }
//                               }}
//                               onKeyDown={(e) => {
//                                 if (e.key === 'Enter' || e.key === ' ') {
//                                   e.preventDefault()
//                                   e.stopPropagation()
//                                   handleViewItem(item, 0)
//                                 }
//                               }}
//                             >
//                               <img
//                                 src={item.image}
//                                 alt={item.title}
//                                 className="w-full h-full object-cover pointer-events-none"
//                                 draggable={false}
//                                 onMouseDown={(e) => e.preventDefault()}
//                               />
//                             </div>
//                           ) : (item.video || item.video?.url) ? (
//                             <div
//                               role="button"
//                               tabIndex={0}
//                               className="relative overflow-hidden rounded h-48 cursor-pointer hover:opacity-90 transition-opacity mb-4"
//                               onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation() }}
//                               onClick={(e) => {
//                                 try {
//                                   e.preventDefault()
//                                   e.stopPropagation()
//                                   if (e.stopImmediatePropagation) e.stopImmediatePropagation()
//                                   handleViewItem(item, 0)
//                                 } catch (err) {
//                                   console.error('[PublicWebsiteViewer] Error opening video item:', err)
//                                 }
//                               }}
//                               onKeyDown={(e) => {
//                                 if (e.key === 'Enter' || e.key === ' ') {
//                                   e.preventDefault()
//                                   e.stopPropagation()
//                                   handleViewItem(item, 0)
//                                 }
//                               }}
//                             >
//                               <video
//                                 src={getVideoUrl(item.video)}
//                                 poster={getVideoPoster(item.video)}
//                                 className="w-full h-full object-cover pointer-events-none"
//                                 playsInline
//                                 autoPlay
//                                 muted
//                                 loop
//                                 preload="metadata"
//                                 draggable={false}
//                                 onMouseDown={(e) => e.preventDefault()}
//                               />
//                               {/* Top video overlay removed per design: keep other video metadata unchanged */}
//                             </div>
//                           ) : null}
                          
//                           {item.title && (
//                             <h4 className="text-xl font-semibold text-gray-900 mb-2" style={textStyle}>
//                               {item.title}
//                             </h4>
//                           )}
//                           {item.description && (
//                             <p className="text-gray-600" style={textStyle}>
//                               {item.description}
//                             </p>
//                           )}
//                           {item.price !== undefined && item.price !== null && item.price !== '' && (
//                             <div className="mt-2">
//                               <span className="text-lg font-bold text-green-600" style={textStyle}>
//                                 {formatPrice(item.price, item.currency)}
//                               </span>
//                             </div>
//                           )}
//                           {(item.whatsappContact || item.whatsappNumber) && (
//                             <button
//                               onClick={() => {
//                                 console.log('[PublicWebsiteViewer] ✅ Section items WhatsApp button clicked:', {
//                                   itemTitle: item.name || item.title,
//                                   itemId: item._id,
//                                   sectionType: 'section-items',
//                                   phoneNumber: item.whatsappContact || item.whatsappNumber,
//                                   timestamp: new Date().toISOString(),
//                                   buttonColor: '#10B981',
//                                   colorVerified: true,
//                                   styling: 'permanent green with !important flags',
//                                   deviceWidth: window.innerWidth,
//                                   deviceType: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop'
//                                 })
//                                 handleItemWhatsAppContact(item)
//                               }}
//                               type="button"
//                               className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 hover:shadow-md permanent-whatsapp-button"
//                               style={{
//                                 backgroundColor: hoveredSectionItemButtons.has(`whatsapp-list-${itemIdx}`) ? '#059669' : '#10B981',
//                                 color: '#FFFFFF',
//                                 border: 'none',
//                                 cursor: 'pointer',
//                                 fontWeight: 600,
//                                 boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
//                                 padding: '0.5rem 0.75rem',
//                                 fontSize: '0.875rem',
//                                 display: 'inline-flex',
//                                 alignItems: 'center',
//                                 gap: '0.5rem',
//                                 whiteSpace: 'nowrap',
//                                 transition: 'all 200ms'
//                               }}
//                               title="Contact via WhatsApp"
//                               aria-label={`Contact ${item.name || item.title} via WhatsApp`}
//                               onMouseEnter={() => setHoveredSectionItemButtons(prev => new Set([...prev, `whatsapp-list-${itemIdx}`]))}
//                               onMouseLeave={() => setHoveredSectionItemButtons(prev => { const newSet = new Set(prev); newSet.delete(`whatsapp-list-${itemIdx}`); return newSet; })}
//                             >
//                               <WhatsAppIcon className="w-4 h-4 whatsapp-icon" style={{ color: '#FFFFFF' }} />
//                               Contact via WhatsApp
//                             </button>
//                           )}
//                         </div>
//                       ))}
//                     </div>

//                     {needsPagination && (
//                       <PaginationControls
//                         currentPage={currentPage}
//                         totalPages={totalPages}
//                         onPageChange={(page) => handleItemPageChange(section._id, page)}
//                         sectionFormStyling={getFormStyling()}
//                         useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
//                         section={section}
//                         sectionType={sectionType}
//                       />
//                     )}
//                   </div>
//                 )
//               }
//               innerContent = (
//                 <>
//                   {!hasTopHeader && (
//                     <div className="mb-8">
//                       <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{section.title}</h2>
//                       {section.description && (
//                         <p className="text-base sm:text-lg text-gray-600">{section.description}</p>
//                       )}
//                     </div>
//                   )}

//                   {section.content && (
//                     <div className="space-y-6">
//                       {section.content.heading && (
//                         <h3 className="text-2xl font-semibold text-gray-800">
//                           {section.content.heading}
//                         </h3>
//                       )}

//                       {section.content.subheading && (
//                         <p className="text-gray-600 text-lg leading-relaxed">
//                           {section.content.subheading}
//                         </p>
//                       )}

//                       {itemsContent}
//                     </div>
//                   )}
//                 </>
//               )
//             }

//             // Build wrapper layout with optional sidebars and special media
//             const getSpecialMediaStyles = (m) => {
//               const isMobile = window.innerWidth < 768 // Mobile breakpoint
//               const baseWidth = m.style?.width || '100%'
//               const baseHeight = m.style?.height || 'auto'

//               return {
//                 width: isMobile ? '100%' : baseWidth, // Force full width on mobile for better fit
//                 height: isMobile ? 'auto' : baseHeight, // Auto height on mobile
//                 maxWidth: '100%',
//                 maxHeight: isMobile ? '60vh' : '100%', // Limit height on mobile
//                 paddingLeft: m.style?.paddingLeft || '0px',
//                 paddingRight: m.style?.paddingRight || '0px',
//                 marginLeft: m.style?.marginLeft || '0px',
//                 marginRight: m.style?.marginRight || '0px',
//                 objectFit: 'contain'
//               }
//             }

//             const getFullMediaWrapperStyle = (m) => {
//               const isMobile = window.innerWidth < 768

//               return {
//                 position: 'relative', // Changed from absolute for better mobile support
//                 top: 0,
//                 left: 0,
//                 width: '100%',
//                 height: isMobile ? '40vh' : (m.style?.height || '60vh'), // Smaller height on mobile
//                 minHeight: isMobile ? '200px' : '300px', // Smaller min height on mobile
//                 maxHeight: isMobile ? '50vh' : '80vh', // Smaller max height on mobile
//                 overflow: 'hidden',
//                 display: 'flex',
//                 justifyContent: 'center',
//                 alignItems: 'center',
//                 pointerEvents: 'none',
//                 zIndex: 0
//               }
//             }

//             const getFullMediaStyles = (m) => {
//               const isMobile = window.innerWidth < 768

//               return {
//                 width: isMobile ? '100%' : (m.style?.width || '100%'), // Full width on mobile
//                 height: isMobile ? '100%' : (m.style?.height || '100%'), // Full height within container on mobile
//                 maxWidth: '100%',
//                 maxHeight: '100%',
//                 paddingLeft: m.style?.paddingLeft || '0px',
//                 paddingRight: m.style?.paddingRight || '0px',
//                 marginLeft: m.style?.marginLeft || '0px',
//                 marginRight: m.style?.marginRight || '0px',
//                 objectFit: 'contain'
//               }
//             }

//             const hasFullMedia = specialMedia.some(m => m.display === 'full')

//             return (
//               <section 
//                 key={section._id} 
//                 id={`section-${idx}`} 
//                 className={`relative overflow-hidden py-8 sm:py-12 md:py-16 border-b border-gray-200 last:border-b-0 scroll-mt-20 transition-colors${section.styling?.textColor ? ' public-section-text-override' : ''}`}
//                 onMouseEnter={() => setHoveredSectionId(section._id)}
//                 onMouseLeave={() => setHoveredSectionId(null)} 
//                 style={{ 
//                   minHeight: hasFullMedia ? 'auto' : 'auto',
//                   backgroundColor: hoveredSectionId === section._id ? (getBodyStyling()?.hoverBackgroundColor || getBodyStyling()?.backgroundColor || 'transparent') : (getBodyStyling()?.backgroundColor || 'transparent'),
//                   color: hoveredSectionId === section._id ? (getBodyStyling()?.hoverTextColor || getBodyStyling()?.textColor || 'inherit') : (getBodyStyling()?.textColor || 'inherit'),
//                   '--section-text-color': hoveredSectionId === section._id ? (getBodyStyling()?.hoverTextColor || getBodyStyling()?.textColor || 'inherit') : (getBodyStyling()?.textColor || 'inherit')
//                 }}
//               >
//                 {hasTopHeader && (
//                   <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4 sm:mb-6 md:mb-8">
//                     {section.title && (
//                       <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">{section.title}</h2>
//                     )}
//                     {section.description && (
//                       <p className="text-sm sm:text-base md:text-lg text-gray-600">{section.description}</p>
//                     )}
//                   </div>
//                 )}

//                 {/* Full-width media (display='full') */}
//                   {specialMedia.filter(m => m.display === 'full').map(m => (
//                     <div key={m.id} style={getFullMediaWrapperStyle(m)}>
//                       {m.type === 'image' ? (
//                         <img src={m.url} alt={m.alt || ''} style={getFullMediaStyles(m)} />
//                       ) : (
//                         <video style={getFullMediaStyles(m)} autoPlay muted loop playsInline preload="metadata">
//                           <source src={m.url} type={getVideoMimeType(m.url)} />
//                           Your browser does not support video playback.
//                         </video>
//                       )}
//                     </div>
//                 ))}

//                 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
//                   <div className="flex flex-col md:flex-row gap-6 items-start">
//                     {hasLeft && (
//                       <aside className="flex flex-shrink-0 w-full md:w-[250px]">
//                         <div className="space-y-4">
//                           {/* Render left sidebar media */}
//                           {(left.media || []).map(m => (
//                             <div key={m.id} className="relative w-full h-32 sm:h-40 md:h-48 rounded-lg overflow-hidden bg-gray-100">
//                               {m.type === 'image' ? (
//                                 <img src={m.url} alt={m.alt || ''} className="w-full h-full object-cover" />
//                               ) : (
//                                 <video className="w-full h-full object-cover" autoPlay muted loop playsInline preload="metadata">
//                                   <source src={m.url} type={getVideoMimeType(m.url)} />
//                                   Your browser does not support video playback.
//                                 </video>
//                               )}
//                             </div>
//                           ))}

//                           {left.contentText && <div className="text-sm text-gray-700">{left.contentText}</div>}
//                         </div>
//                       </aside>
//                     )}

//                     <div className="flex-1 relative w-full">
//                       {/* Content-width media (display='content') rendered as content */}
//                       {(specialMedia.filter(m => m.display === 'content') || []).map(m => (
//                         <div key={m.id} className="mb-4">
//                           {m.type === 'image' ? (
//                             <img
//                               src={m.url}
//                               alt={m.alt || ''}
//                               style={{ ...getSpecialMediaStyles(m), objectFit: 'contain' }}
//                               className="rounded"
//                             />
//                           ) : (
//                             <video
//                               style={{ ...getSpecialMediaStyles(m), objectFit: 'contain' }}
//                               controls
//                               className="rounded"
//                               playsInline
//                               preload="metadata"
//                             >
//                               <source src={m.url} type={getVideoMimeType(m.url)} />
//                               Your browser does not support video playback.
//                             </video>
//                           )}
//                         </div>
//                       ))}

//                       <div className="mb-8 relative z-10">
//                         {innerContent}
//                       </div>

//                       {renderCTA(section)}
//                     </div>

//                     {hasRight && (
//                       <aside className="flex flex-shrink-0 w-full md:w-[250px]">
//                         <div className="space-y-4">
//                           {/* Render right sidebar media */}
//                           {(right.media || []).map(m => (
//                             <div key={m.id} className="relative w-full h-32 sm:h-40 md:h-48 rounded-lg overflow-hidden bg-gray-100">
//                               {m.type === 'image' ? (
//                                 <img src={m.url} alt={m.alt || ''} className="w-full h-full object-cover" />
//                               ) : (
//                                 <video className="w-full h-full object-cover" autoPlay muted loop playsInline preload="metadata">
//                                   <source src={m.url} type={getVideoMimeType(m.url)} />
//                                   Your browser does not support video playback.
//                                 </video>
//                               )}
//                             </div>
//                           ))}

//                           {right.contentText && <div className="text-sm text-gray-700">{right.contentText}</div>}
//                         </div>
//                       </aside>
//                     )}
//                   </div>
//                 </div>
//               </section>
//             )
//               })
//             }
//             return (
//               <div className="py-16 text-center">
//                 <p className="text-gray-500 text-lg">No sections configured for this website yet.</p>
//               </div>
//             )
//           })()
//         )}
//       </main>

//       {itemDetailsOpen && selectedItem && (
//         <div 
//           className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" 
//           onClick={() => {
//             console.log('[PublicWebsiteViewer] Closing item details modal')
//             setItemDetailsOpen(false)
//           }}
//         >
//           <div 
//             className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" 
//             onClick={(e) => e.stopPropagation()}
//           >
//             <div className="flex items-start justify-between gap-4 mb-4">
//               <div>
//                 <h3 className="text-2xl font-semibold text-gray-900">
//                   {selectedItem.title || selectedItem.name || 'Item Details'}
//                 </h3>
//                 {selectedItem.description && (
//                   <p className="text-gray-600 mt-1">{selectedItem.description}</p>
//                 )}
//               </div>
//               <button 
//                 onClick={() => {
//                   console.log('[PublicWebsiteViewer] Close button clicked')
//                   setItemDetailsOpen(false)
//                 }} 
//                 className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
//               >
//                 ×
//               </button>
//             </div>

//             {selectedItemPreviewMedia && (
//               <div className="mb-6 w-full h-[60vh] sm:h-[70vh] md:h-[80vh] rounded-lg overflow-hidden bg-black flex items-center justify-center p-2 sm:p-3">
//                 {selectedItemPreviewMedia.type === 'image' ? (
//                   <img
//                     src={selectedItemPreviewMedia.url}
//                     alt={selectedItem.title || 'Item image'}
//                     className="max-w-full max-h-full object-contain"
//                     onError={(e) => console.error('[PublicWebsiteViewer] Failed to load selected item preview image:', e)}
//                     style={{ cursor: 'default' }}
//                     draggable={false}
//                   />
//                 ) : selectedItemPreviewMedia.type === 'video' ? (
//                   <video
//                     ref={selectedPreviewVideoRef}
//                     key={selectedItemPreviewMedia.url}
//                     controls
//                     poster={selectedItemPreviewMedia.poster || ''}
//                     className="w-full h-full max-w-full max-h-full object-contain"
//                     playsInline
//                     autoPlay
//                     muted
//                     loop
//                     preload="metadata"
//                     onClick={(e) => e.stopPropagation()}
//                     onLoadStart={() => {
//                       console.log('[PublicWebsiteViewer] Video loading started:', selectedItemPreviewMedia.url)
//                     }}
//                     onCanPlay={() => {
//                       console.log('[PublicWebsiteViewer] Video can play:', selectedItemPreviewMedia.url)
//                     }}
//                     onError={(e) => {
//                       console.error('[PublicWebsiteViewer] Video playback error:', {
//                         url: selectedItemPreviewMedia.url,
//                         mimeType: getVideoMimeType(selectedItemPreviewMedia.url),
//                         error: e.target.error
//                       })
//                     }}
//                   >
//                     <source src={selectedItemPreviewMedia.url} type={getVideoMimeType(selectedItemPreviewMedia.url)} />
//                     Your browser does not support video playback.
//                   </video>
//                 ) : null}
//               </div>
//             )}

//             {selectedItem.gallery && selectedItem.gallery.length > 0 && (
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-6 mb-6">
//                 {selectedItem.gallery.map((g, i) => {
//                   const previewUrl = g.image?.url || g.url || g.video?.url || g.videoUrl
//                   if (!previewUrl) return null
//                   return (
//                     <div
//                       key={`${previewUrl}-${i}`}
//                       className={`cursor-pointer rounded overflow-hidden border ${i === selectedItemGalleryIndex ? 'border-primary-600 ring-2 ring-primary-200' : 'border-gray-200'} bg-gray-100`}
//                       onClick={() => {
//                         console.log('[PublicWebsiteViewer] Gallery thumbnail clicked:', {
//                           galleryIndex: i,
//                           mediaUrl: previewUrl,
//                           mediaType: (g.image?.url || g.url) ? 'image' : 'video',
//                           itemId: selectedItem.id,
//                           itemTitle: selectedItem.title
//                         })
//                         setSelectedItemGalleryIndex(i)
//                       }}
//                     >
//                       {g.image?.url || g.url ? (
//                         <img src={previewUrl} alt={`Gallery ${i + 1}`} className="w-full h-24 object-cover" />
//                         ) : (
//                         <img
//                           src={getVideoPoster(g.video || g.videoUrl) || previewUrl}
//                           alt={`Gallery video ${i + 1}`}
//                           className="w-full h-24 object-cover"
//                           draggable={false}
//                         />
//                       )}
//                     </div>
//                   )
//                 })}
//               </div>
//             )}
            
//             {selectedItem.video && (
//               <video
//                 key={getVideoUrl(selectedItem.video)}
//                 controls
//                 poster={getVideoPoster(selectedItem.video)}
//                 className="w-full h-auto rounded mb-4"
//                 playsInline
//                 autoPlay
//                 muted
//                 loop
//                 preload="metadata"
//               >
//                 <source
//                   src={typeof selectedItem.video === 'string' ? selectedItem.video : selectedItem.video?.url}
//                   type={getVideoMimeType(typeof selectedItem.video === 'string' ? selectedItem.video : selectedItem.video?.url)}
//                 />
//                 Your browser does not support video playback.
//               </video>
//             )}

//             <div className="flex flex-wrap gap-3 mt-6">
//               {(selectedItem.whatsappContact || selectedItem.whatsappNumber) && (
//                 <button
//                   type="button"
//                   className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors duration-200 permanent-whatsapp-button"
//                   onClick={() => {
//                     console.log('[PublicWebsiteViewer] ✅ Selected item WhatsApp button clicked:', {
//                       itemTitle: selectedItem.name || selectedItem.title,
//                       itemId: selectedItem._id,
//                       sectionType: 'section-items',
//                       phoneNumber: selectedItem.whatsappContact || selectedItem.whatsappNumber,
//                       timestamp: new Date().toISOString(),
//                       buttonColor: '#10B981',
//                       colorVerified: true,
//                       styling: 'permanent green with !important flags',
//                       deviceWidth: window.innerWidth,
//                       deviceType: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop'
//                     })
//                     handleItemWhatsAppContact(selectedItem)
//                   }}
//                   style={{
//                     backgroundColor: isHoveredWhatsAppModal ? '#059669' : '#10B981',
//                     color: '#FFFFFF',
//                     border: 'none',
//                     cursor: 'pointer',
//                     fontWeight: 600,
//                     boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
//                     padding: '0.5rem 1rem',
//                     fontSize: '0.875rem',
//                     display: 'inline-flex',
//                     alignItems: 'center',
//                     gap: '0.5rem',
//                     whiteSpace: 'nowrap',
//                     transition: 'all 200ms'
//                   }}
//                   onMouseEnter={() => setIsHoveredWhatsAppModal(true)}
//                   onMouseLeave={() => setIsHoveredWhatsAppModal(false)}
//                 >
//                   <WhatsAppIcon className="w-4 h-4 whatsapp-icon" style={{ color: '#FFFFFF' }} />
//                   Contact via WhatsApp
//                 </button>
//               )}

//               {selectedItem.price !== undefined && selectedItem.price !== null && selectedItem.price !== '' && (
//                 <span className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-lg font-semibold text-green-600">
//                   {formatPrice(selectedItem.price, selectedItem.currency)}
//                 </span>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       <footer
//         className="py-8 mt-12 transition-colors"
//         style={{
//           backgroundColor: isFooterHovered ? (getFooterStyling()?.hoverBackgroundColor || '#1F2937') : (getFooterStyling()?.backgroundColor || '#111827'),
//           color: isFooterHovered ? (getFooterStyling()?.hoverTextColor || '#F9FAFB') : (getFooterStyling()?.textColor || '#FFFFFF')
//         }}
//         onMouseEnter={() => setIsFooterHovered(true)}
//         onMouseLeave={() => setIsFooterHovered(false)}
//       >
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
//           <div className="flex flex-wrap justify-center gap-3">
//             {socialMediaLinks.map((link) => {
//               const Icon = link.icon
//               return (
//                 <a
//                   key={link.name}
//                   href={link.href}
//                   target="_blank"
//                   rel="noreferrer"
//                   className="inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors"
//                   style={{
//                     backgroundColor: `${isFooterHovered ? (getFooterStyling()?.hoverTextColor || '#F9FAFB') : (getFooterStyling()?.textColor || '#FFFFFF')}20`,
//                     color: isFooterHovered ? (getFooterStyling()?.hoverTextColor || '#F9FAFB') : (getFooterStyling()?.textColor || '#FFFFFF')
//                   }}
//                   aria-label={link.name}
//                 >
//                   <Icon className="w-5 h-5" />
//                 </a>
//               )
//             })}
//           </div>
//           <p>&copy; 2026 {website?.name || ''}. All rights reserved.</p>
//           {/* <p className="text-gray-400 text-sm mt-2">Powered by MarketBook Solution</p> */}
//         </div>
//       </footer>

//       <ReportContentModal
//         isOpen={reportModalOpen}
//         onClose={() => setReportModalOpen(false)}
//         website={website}
//         sectionFormStyling={getFormStyling()}
//         useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
//         sectionType="report"
//       />

//       <ImageGalleryModal
//         isOpen={showGalleryModal}
//         images={galleryItems}
//         onClose={closeGalleryModal}
//         startIndex={galleryStartIndex}
//         sectionFormStyling={getFormStyling()}
//         useDefaultStyling={effectiveWebsite?.useDefaultStyling !== false}
//         sectionType="image-gallery-modal"
//       />
      

//       {/* Buyer Info Modal */}
//       {showBuyerForm && (
//         <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
//           <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
//             <h3 className="text-xl font-bold mb-4">Enter Your Details</h3>
//             <form onSubmit={(e) => { e.preventDefault(); buyTicket(); }}>
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Name *</label>
//                 <input
//                   type="text"
//                   value={buyerInfo.name}
//                   onChange={(e) => setBuyerInfo({ ...buyerInfo, name: e.target.value })}
//                   className="w-full px-3 py-2 border rounded-lg"
//                   required
//                 />
//               </div>
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Email *</label>
//                 <input
//                   type="email"
//                   value={buyerInfo.email}
//                   onChange={(e) => setBuyerInfo({ ...buyerInfo, email: e.target.value })}
//                   className="w-full px-3 py-2 border rounded-lg"
//                   required
//                 />
//               </div>
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Phone</label>
//                 <input
//                   type="tel"
//                   value={buyerInfo.phone}
//                   onChange={(e) => setBuyerInfo({ ...buyerInfo, phone: e.target.value })}
//                   className="w-full px-3 py-2 border rounded-lg"
//                 />
//               </div>
//               <div className="flex gap-3">
//                 <button
//                   type="submit"
//                   disabled={buyingTicket}
//                   className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
//                 >
//                   {buyingTicket ? 'Processing...' : 'Proceed to Payment'}
//                 </button>
//                 <button
//                   type="button"
//                   onClick={() => setShowBuyerForm(false)}
//                   className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
//                 >
//                   Cancel
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//         </>
//       )}
//     </div>
//   )
// }

// export default function PublicWebsiteViewer({ prefetchedWebsite, prefetchedWebsiteError }) {
//   return (
//     <CartProvider>
//       <PublicWebsiteViewerContent prefetchedWebsite={prefetchedWebsite} prefetchedWebsiteError={prefetchedWebsiteError} />
//     </CartProvider>
//   )
// }



